import React, { useState } from 'react';
import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  formatReportPrintedDate,
  reportModalBorderClass,
  reportModalPanelClass,
} from '../reportTheme';
import ReportLogo from './ReportLogo';
import ReportBrandMark from './ReportBrandMark';
import { isoToMDY } from '../lib/reportDates';
import { lotRowKey } from '../lib/lotRowKey';
import { CURATED_INDEX_NAMES } from '../lib/benchmarkIndexGroups';
import { groupSigil } from '../lib/configLabel';

// Inert callback for embedded group members: their inline row-toggle onClick is a
// no-op because expansion is driven by the controlled `expandedRows` slice supplied
// per member (via expandedByMember) + the viewer's delegated single/double handler
// (which owns the toggle so a double-click-to-explain never flickers the lot rows).
const NOOP = () => {};
// Stable empty slice so an embedded member with no expanded rows still renders in
// CONTROLLED mode (expandedRowsProp != null), not uncontrolled-with-a-dead-NOOP.
const EMPTY_EXPANDED = {};

// Assets Under Management list: the money columns, in APX's printed order.
// Total is keyed `value` - the existing AUM key name, so pickSettle and the
// backend's group-collapse helper work on it unchanged. Acc. Interest is a memo
// (already inside Fixed Income) and prints only when the popup's "Include
// accrued interest" box was ticked.
const AUM_LIST_COLUMNS = [
  ['fixed_income', 'Fixed Income'],
  ['equity', 'Equity'],
  ['cash_and_equivalents', 'Cash and Equivalents'],
  ['other', 'Other'],
  ['accrued_interest', 'Acc. Interest'],
  ['value', 'Total'],
];
const aumListColumns = (withAccrued) =>
  AUM_LIST_COLUMNS.filter(([key]) => withAccrued || key !== 'accrued_interest');
const SORT_LABELS = {
  total: 'Total AUM',
  name: 'Portfolio name',
  port_code: 'Port code',
  open_date: 'Inception date',
};

// PM directive 2026-09-17: EVERY bond prints "{coupon}% Due {maturity}" on a line
// of its own under the description, so the two figures sit in a fixed, scannable
// place on every row. A block, not an inline span - it must always break, even
// when the description's last line has room. Rendered next to
// stripCouponDue(description), which is what keeps it from reading twice.
const DueLine = ({ coupon, due, darkMode }) => (
  <div className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{Number(coupon || 0).toFixed(3)}% Due {due || ''}</div>
);

// APX's two-row description shape, now used by BOTH the rendered report and the
// xlsx export: row 1 carries the issuer name alone, row 2 carries "4.125% Due
// 12/01/2039". Pershing fullnames embed that coupon+due text inline for nearly
// every held bond ("... SOLID WASTE DISP REV 2019 A 4.125% 12/01/39 B/E DTD 12/06/19
// PUT"), so printing the name verbatim above the new line would say it twice -
// strip the tail. It takes the trailing "B/E DTD ... PUT" with it; that is the
// intended shape, and Call/Put have their own columns. The full untouched
// fullname stays in the cell's `title`. The " FACTOR: <8dp>" suffix the backend
// appends AFTER the tail is preserved (factored bonds need it), and "VARIABL"
// covers floaters that print no numeric coupon. Stripping to nothing falls back
// to the whole name, so a sparse "Bond <sid>" never blanks the cell.
const stripCouponDue = (d) => {
  const s = String(d || '');
  const factor = s.match(/\s+FACTOR:\s*[\d.]+\s*$/);
  const head = factor ? s.slice(0, factor.index) : s;
  const stripped = head
    .replace(/\s+(?:\d+(?:\.\d+)?\s*%|VARIABL)\s+\d{1,2}\/\d{1,2}\/\d{2,4}.*$/, '')
    .trim();
  return (stripped || head.trim()) + (factor ? factor[0] : '');
};

// APX's row 2 uses dashes and a 2-digit year ("Due 08-01-31"), unlike isoToMDY.
const mdyDash = (iso) => {
  const m = typeof iso === 'string' && iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}-${m[3]}-${m[1].slice(2)}` : (iso || '');
};

// A sortable YYYYMMDD integer for a payload date. String-only (no Date object,
// so no timezone skew), like isoToMDY. FI Holdings publishes its lot dates
// ALREADY FORMATTED as "MM-DD-YY" (backend strftime "%m-%d-%y", "" when
// unknown), so comparing the raw strings would order by MONTH; SCR-era payloads
// use ISO, so both forms are accepted. The 2-digit year takes the same 69/70
// pivot strftime round-trips through, and anything missing or unparseable
// returns Infinity - i.e. sorts AFTER every dated value.
const dateSortKey = (v) => {
  const s = String(v ?? '');
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return Number(iso[1]) * 10000 + Number(iso[2]) * 100 + Number(iso[3]);
  const mdy = s.match(/^(\d{2})-(\d{2})-(\d{2})$/);
  if (!mdy) return Infinity;
  const yy = Number(mdy[3]);
  return (yy >= 69 ? 1900 + yy : 2000 + yy) * 10000
    + Number(mdy[1]) * 100 + Number(mdy[2]);
};

// Tax lots in DISPLAY order, each carrying the 1-based number printed beside it,
// so the label can never disagree with the row's position on the page.
//
// SETTLE DATE ascending is the order (PM request 2026-08-15). Two lots of one
// bond routinely share a settle date, so the comparator is a TOTAL order -
// settle, then trade date, then par, then the payload's own index, which is
// unique by construction. That last term is what makes the numbering
// byte-reproducible: Array.prototype.sort's stability guarantee is about equal
// elements within ONE input, not about two payloads that order the same lots
// differently, and this report's output is diffed run to run.
//
// Undated lots (settle "" / null - the backend blanks a date it cannot parse)
// sort last but are still numbered, so every lot on the page carries a number.
//
// The size tiebreak reads `par_value ?? quantity`: a bond lot is sized in par
// (FI Holdings) and a share lot in quantity (Unrealized G&L), and reading only
// one of those names would silently drop the tiebreak on the other report,
// leaving same-settle-date lots ordered by payload position alone.
const lotSize = (lot) => Number(lot.par_value ?? lot.quantity) || 0;
const numberedLots = (lots) =>
  (lots || [])
    .map((lot, index) => ({ lot, index }))
    .sort((a, b) => (
      dateSortKey(a.lot.settle_date) - dateSortKey(b.lot.settle_date)
      || dateSortKey(a.lot.trade_date) - dateSortKey(b.lot.trade_date)
      || lotSize(a.lot) - lotSize(b.lot)
      || a.index - b.index
    ))
    .map(({ lot }, i) => ({ lot, number: i + 1 }));

const MONEY_DIGITS = { cents: 2, whole: 0 };

// Bare-number money (no currency symbol) at the active precision. Folds a value
// that rounds to a negative zero back to a clean "0" (N11 cosmetics) - a
// residual smaller than the last displayed digit must not read as a debit.
const formatAmount = (value, digits) => {
  const n = Number(value);
  const v = n < 0 && n > -(0.5 * 10 ** -digits) ? 0 : n;
  return v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

// Stable default for the hidden-benchmark set so an omitted prop doesn't create
// a new Set() each render (would defeat memoization and churn effects).
const EMPTY_HIDDEN = new Set();

// Why a time-weighted headline dashed, in the reader's own words
// (`money_weighted.window_twr_unavailable_reason`). One blanket sentence blamed
// the window anchor for a tape defect and for a dead read alike. The default is
// the anchor case ('no_base'), which an older payload also arrives without.
const TWR_UNAVAILABLE_COPY = {
  base_guard:
    'time-weighted return unavailable for this window - a day in the daily series moved by more than its base can carry',
  read_failed:
    'time-weighted return unavailable - the daily series could not be read',
  imported_aggregate:
    'time-weighted return unavailable for this window - imported performance history is not combined across accounts',
};
const DEFAULT_TWR_UNAVAILABLE_COPY =
  'time-weighted return unavailable for this window - the daily series could not be linked from the window start';
// Own-property lookup only: a bare index would answer 'constructor' or
// 'toString' with something off Object.prototype and print a function body
// where a sentence belongs.
const twrUnavailableCopy = (reason) => (
  Object.prototype.hasOwnProperty.call(TWR_UNAVAILABLE_COPY, reason)
    ? TWR_UNAVAILABLE_COPY[reason]
    : DEFAULT_TWR_UNAVAILABLE_COPY);

const ReportDisplayModal = ({
  isOpen,
  onClose,
  reportData,
  reportType,
  reportName,
  embedded = false,
  chromeless = false,
  activeMember: activeMemberProp,
  onActiveMemberChange,
  expandedByMember,
  expandedRows,
  showLogo = false,
  brand = null,
  moneyPrecision,
  yocBasis: yocBasisProp,
  onYocBasisChange,
  showMarketYield: showMarketYieldProp,
  onShowMarketYieldChange,
  includeCashYield: includeCashYieldProp,
  onIncludeCashYieldChange,
  feeBasis: feeBasisProp,
  onFeeBasisChange,
  securityFilter: securityFilterProp,
  onSecurityFilterChange,
  accruedBasis: accruedBasisProp,
  onAccruedBasisChange,
  holdingsBasis: holdingsBasisProp,
  onHoldingsBasisChange,
  returnBasis: returnBasisProp,
  onReturnBasisChange,
  hiddenIndexes = EMPTY_HIDDEN,
  onToggleIndex,
  selectedIndexNames = null,
}) => {
  const { darkMode } = useTheme();
  // Benchmark hide (ephemeral, per report view). `indexKey` is the identity used
  // both for the hidden-set membership test and the toggle callback; it matches
  // the backend benchmark name (falling back to label). The hide control renders
  // only on the interactive screen mount (onToggleIndex present) and is stripped
  // from the print/export DOM by data-*-skip as belt-and-suspenders.
  const indexKey = (idx) => idx.name || idx.label || 'Index';
  // Persistent benchmark selection from the generation dialog (see
  // lib/benchmarkIndexGroups). null => no restriction. A curated index shows
  // only if selected; a non-curated name always passes (fail-open).
  const isIndexAllowed = (idx) => {
    if (selectedIndexNames == null) return true;
    const k = indexKey(idx);
    if (!CURATED_INDEX_NAMES.has(k)) return true;
    return selectedIndexNames.has(k);
  };
  const visibleIndexes = (list) =>
    (Array.isArray(list) ? list : []).filter(
      (idx) => !hiddenIndexes.has(indexKey(idx)) && isIndexAllowed(idx),
    );
  const indexHideBtn = (idx) =>
    (onToggleIndex ? (
      <button
        type="button"
        data-export-skip
        data-print-skip
        title={`Hide ${indexKey(idx)}`}
        aria-label={`Hide ${indexKey(idx)}`}
        onClick={() => onToggleIndex(indexKey(idx))}
        className={`ml-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${darkMode ? 'text-gray-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}
      >
        <X className="inline h-3 w-3" />
      </button>
    ) : null);
  // Active member tab for group "@" sequential reports (a string of per-account
  // reports). See the report-groups design spec (Component B). Controlled when
  // the parent (a floating ReportWindow) supplies activeMember/onActiveMemberChange
  // so the on-screen body and the print copy stay in sync.
  const [activeMemberState, setActiveMemberState] = useState(0);
  const activeMember = activeMemberProp != null ? activeMemberProp : activeMemberState;
  const setActiveMember = onActiveMemberChange || setActiveMemberState;
  // Portfolio Summary (PM Q1): toggle the TOTAL row between the bond-only
  // yield (FI Distribution convention, default) and the cash-blended yield.
  // Controlled when the parent supplies the prop pair (same shape as
  // activeMember above) so print copies mirror the on-screen choice.
  const [includeCashYieldState, setIncludeCashYieldState] = useState(false);
  const includeCashYield = includeCashYieldProp != null ? includeCashYieldProp : includeCashYieldState;
  const setIncludeCashYield = onIncludeCashYieldChange || setIncludeCashYieldState;
  // Unified yield-ON-COST basis selector, shared by every report that surfaces a
  // yield-on-cost figure (FI Holdings, FI Distribution, Portfolio Summary). Both
  // ends are ON COST: 'ytm' = yield-to-(effective)-maturity-on-cost (the APX
  // default), 'ytw' = yield-to-worst-on-cost. 'both' shows the two side by side
  // for management review. There is no on-MARKET to-worst here by design.
  const [yocBasisState, setYocBasisState] = useState('ytm'); // 'ytm' | 'ytw' | 'both'
  const yocBasis = yocBasisProp != null ? yocBasisProp : yocBasisState;
  const setYocBasis = onYocBasisChange || setYocBasisState;
  // In-report security lookup for the transaction listings: narrows the
  // already-generated rows by symbol / CUSIP / security name without
  // re-requesting (the payload is a superset). Controlled when the parent
  // supplies the pair (same shape as yocBasis above) so the print copy
  // mirrors the on-screen filter; the input itself is stripped from the
  // print/export DOM via data-*-skip.
  const [securityFilterState, setSecurityFilterState] = useState('');
  const securityFilter = securityFilterProp != null ? securityFilterProp : securityFilterState;
  const setSecurityFilter = onSecurityFilterChange || setSecurityFilterState;

  // A `.report-sticky-toolbar` bar freezes with the column headers, painted by
  // index.css in the white report sheet's colour. A bar that sits inside one of
  // the tinted summary cards has to freeze in the CARD's colour instead, or the
  // pinned strip reads as a white band laid across the card. Written inline off
  // `darkMode` - the same source of truth the card's own bg-gray-50 /
  // bg-[#141414] classes use - because ReportPopup forces the report body to
  // light mode through React context while the surrounding window keeps the
  // user's theme, so a `.dark`-keyed CSS rule would follow the wrong one.
  const stickyToolbarOnCard = { '--report-toolbar-bg': darkMode ? '#141414' : '#f9fafb' };

  const yocBasisSelect = (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Yield on cost</span>
      <select
        value={yocBasis}
        onChange={(e) => setYocBasis(e.target.value)}
        className={`rounded border px-1 py-0.5 text-xs ${darkMode ? 'bg-[#1c1c1c] border-white/10 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
      >
        <option value="ytm">To Maturity</option>
        <option value="ytw">To Worst</option>
        <option value="both">Both</option>
      </select>
    </label>
  );

  // Companion to the basis selector above: show the MARKET yield beside each
  // on-cost column (FI Holdings only, for now - the other market-value reports
  // do not ship a market yield in their payload). Basis and horizon are
  // independent, so this stays a separate checkbox rather than folding into a
  // six-entry dropdown.
  //
  // DISPLAY ONLY, like yocBasis: the payload already carries yield_to_maturity /
  // yield_to_worst on every row, subtotal and the grand total, so a flip never
  // re-fetches, never enters `params` and never round-trips into a saved config.
  // Same prop-or-local-state shape so a parent ReportWindow can lift it and keep
  // the print/export clones in sync with what is on screen.
  const [showMarketYieldState, setShowMarketYieldState] = useState(false);
  const showMarketYield = showMarketYieldProp != null ? showMarketYieldProp : showMarketYieldState;
  const setShowMarketYield = onShowMarketYieldChange || setShowMarketYieldState;

  const marketYieldCheckbox = (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <input
        type="checkbox"
        checked={showMarketYield}
        onChange={(e) => setShowMarketYield(e.target.checked)}
      />
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Show market yield</span>
    </label>
  );

  // Holdings basis (settlement date vs trade date, PM spec 2026-08-28). BanyanAI
  // is a TRADE-date system: the default view is the economic position as of the
  // trade date, unsettled purchases included. The settlement view is what the
  // custodian holds on the date - unsettled purchases out, sold-but-unsettled
  // positions back in - plus the settled cash balance, which is the basis the
  // Pershing statement prints.
  //
  // DISPLAY ONLY, like yocBasis: ONE payload carries both bases (rows flagged
  // `pending_settlement` / `settled_only`, every affected aggregate mirrored
  // `x` / `x_settle`), so a flip never re-fetches and never enters `params`.
  // Same prop-or-local-state shape so a parent ReportWindow can lift it and keep
  // the print/export clones in sync. Default is ALWAYS trade - today's numbers.
  const [holdingsBasisState, setHoldingsBasisState] = useState('trade'); // 'trade' | 'settle'
  const holdingsBasis = holdingsBasisProp != null ? holdingsBasisProp : holdingsBasisState;
  const setHoldingsBasis = onHoldingsBasisChange || setHoldingsBasisState;
  // Presence gate, exactly like feeBasisAvailable below: the backend ships the
  // settlement mirrors only where it can compute them (mapper-era dates served
  // by the live read path), and a toggle that changes nothing would be noise.
  const settlementBasisAvailable = !!reportData?.settlement_basis_available;
  const settleView = settlementBasisAvailable && holdingsBasis === 'settle';
  // Read an aggregate on the active basis. Falls back to the unsuffixed (trade)
  // key whenever the settlement mirror is absent, so a flip can never blank a
  // cell or silently zero a total.
  const pickSettle = React.useCallback((obj, key) => {
    if (!obj) return undefined;
    if (!settleView) return obj[key];
    const settle = obj[`${key}_settle`];
    return settle === undefined || settle === null ? obj[key] : settle;
  }, [settleView]);
  // Row visibility: an unsettled purchase is a position on the TRADE basis only;
  // a sold-but-unsettled position is still in custody, so it is a position on
  // the SETTLEMENT basis only. A consolidated (@@) security one account sold
  // and another still holds arrives as two rows - the `trade_only` blend of the
  // holders and the `settled_only` blend of every account - so each view shows
  // exactly one. Rows carrying no flag show on both.
  const basisRows = React.useCallback(
    (rows) => rows.filter((r) => (settleView
      ? !(r.pending_settlement || r.trade_only)
      : !r.settled_only)),
    [settleView],
  );
  // Disclosure for the row-hiding surfaces: a settlement view that silently
  // drops rows reads as a data gap, so say how many moved and which way.
  // Counted on the PRE-filter set - the only place both populations exist.
  const basisDisclosure = (rows) => {
    if (!settleView) return null;
    const excluded = rows.filter((r) => r.pending_settlement).length;
    // A row flagged both ways (bought AND sold before either settled) is hidden
    // on every basis - it is never "included" by the settlement view; nor is a
    // consolidated settlement blend split off only because it leaves out a
    // purchase (`unsettled_sale: false` - it holds no sale awaiting delivery).
    const included = rows.filter(
      (r) => r.settled_only && !r.pending_settlement && r.unsettled_sale !== false,
    ).length;
    if (!excluded && !included) return null;
    return (
      <div
        className={`text-[11px] ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}
        data-export-skip
      >
        {`Settlement basis: ${excluded} unsettled ${excluded === 1 ? 'purchase' : 'purchases'} excluded`}
        {included > 0 && `, ${included} unsettled ${included === 1 ? 'sale' : 'sales'} included`}
      </div>
    );
  };

  const holdingsBasisSelect = settlementBasisAvailable ? (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Holdings basis</span>
      <select
        value={holdingsBasis}
        onChange={(e) => setHoldingsBasis(e.target.value)}
        className={`rounded border px-1 py-0.5 text-xs ${darkMode ? 'bg-[#1c1c1c] border-white/10 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
      >
        <option value="trade">Trade date</option>
        <option value="settle">Settlement date</option>
      </select>
    </label>
  ) : null;

  // Accrued-interest basis (BTAM worksheet item 1, 2026-08-07; PM respec
  // 2026-08-21). Every report prints accrued on a SETTLEMENT basis - accrued to
  // the report date itself, which is the figure that ties the custodian and
  // Bloomberg. 'trade' is that same date on a TRADE-DATE basis: a trade executed
  // on the report date settles the next day, so it carries one day of coupon
  // more - except where the coupon pays the next day, where the curve resets
  // between the two legs and the trade figure IS the settlement figure.
  //
  // DISPLAY ONLY, like yocBasis: the payload carries `accrued_interest` and
  // `accrued_interest_trade` on every row, subtotal and grand total, so a flip
  // never re-fetches, never enters `params`, and one payload-cache entry serves
  // both. Same prop-or-local-state shape so a parent ReportWindow can lift it
  // and keep the print/export clones in sync with what is on screen. Default is
  // ALWAYS settle - that is the figure that ties APX today.
  const [accruedBasisState, setAccruedBasisState] = useState('settle'); // 'settle' | 'trade'
  const accruedBasis = accruedBasisProp != null ? accruedBasisProp : accruedBasisState;
  const setAccruedBasis = onAccruedBasisChange || setAccruedBasisState;

  // Read accrued on the active basis. Falls back to the settlement figure
  // whenever the trade value is absent (bond_accrued could not step that row
  // forward a day), so a flip can never blank a cell or silently zero a total.
  //
  // The two toggles COMPOSE: pick the accrual convention first, then read that
  // field on the active holdings basis, so a settlement-basis total of the
  // trade-date accrual is `accrued_interest_trade_settle`. Rows carry no
  // mirrors, so pickSettle is the identity there.
  const pickAccrued = React.useCallback((obj) => {
    if (!obj) return undefined;
    if (accruedBasis !== 'trade') return pickSettle(obj, 'accrued_interest');
    const trade = pickSettle(obj, 'accrued_interest_trade');
    return trade == null ? pickSettle(obj, 'accrued_interest') : trade;
  }, [accruedBasis, pickSettle]);

  const accruedBasisSelect = (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Accrued basis</span>
      <select
        value={accruedBasis}
        onChange={(e) => setAccruedBasis(e.target.value)}
        className={`rounded border px-1 py-0.5 text-xs ${darkMode ? 'bg-[#1c1c1c] border-white/10 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
      >
        <option value="settle">Settlement date</option>
        <option value="trade">Trade date</option>
      </select>
    </label>
  );

  // Net-of-fees basis picker (net-of-fees plan step 6). Same prop-or-local-state
  // shape as yocBasis above so a parent ReportWindow can lift it and keep the
  // print/PDF clone in sync with what is on screen.
  //
  // DISPLAY ONLY. The payload always carries BOTH bases (`x` and `x_net`), so
  // this never re-fetches, never enters `params`, and one payload-cache entry
  // serves both - a flip is instant. Default is ALWAYS gross (PM ruling).
  const [feeBasisState, setFeeBasisState] = useState('gross'); // 'gross' | 'net'
  const feeBasis = feeBasisProp != null ? feeBasisProp : feeBasisState;
  const setFeeBasis = onFeeBasisChange || setFeeBasisState;
  // Presence gate: the control only exists when the payload actually has a net
  // basis. An UNBILLED account renders net == gross, which is correct rather
  // than a gap, so offering a toggle that changes nothing would be noise.
  const feeBasisAvailable = !!reportData?.fee_basis_available;
  const netBasis = feeBasisAvailable && feeBasis === 'net';
  // Pick a metric on the active basis. Never fills a net cell with the gross
  // figure: the backend writes net == gross itself wherever that is the answer
  // (an unbilled account, an account that pays no fee, a class row, a
  // benchmark), so a net figure that is absent or null does not exist, is
  // withheld (Ruling 159) or is not meaningful (F44), and prints '-'.
  const pickBasis = React.useCallback((row, key) => {
    if (!row) return undefined;
    return netBasis ? row[`${key}_net`] : row[key];
  }, [netBasis]);

  const feeBasisSelect = feeBasisAvailable ? (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Returns</span>
      <select
        value={feeBasis}
        onChange={(e) => setFeeBasis(e.target.value)}
        className={`rounded border px-1 py-0.5 text-xs ${darkMode ? 'bg-[#1c1c1c] border-white/10 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
      >
        <option value="gross">Gross of fees</option>
        <option value="net">Net of fees</option>
      </select>
    </label>
  ) : null;

  // Return basis. TIME-WEIGHTED is the default - the strategy's return,
  // flow-neutral, the industry standard and what the fund administrator
  // reports. IRR (gain over average capital, the Advent headline) is the
  // toggle. 'dcf' is deliberately absent - that is a generation param
  // (IrrMethodField, the IRR flavour), not a display basis.
  //
  // DISPLAY ONLY, like feeBasis: one payload carries both bases, so a flip
  // never re-fetches and never enters `params`. Same prop-or-local-state
  // shape so a parent ReportWindow can lift it and keep the print/export
  // clones in sync.
  const [returnBasisState, setReturnBasisState] = useState('twr'); // 'twr' | 'irr'
  const returnBasis = returnBasisProp != null ? returnBasisProp : returnBasisState;
  const setReturnBasis = onReturnBasisChange || setReturnBasisState;
  // Presence gate, exactly like feeBasisAvailable above: the backend stamps
  // the reports whose payload carries both bases. An old payload has no flag,
  // no control, and renders the IRR view it always rendered.
  const returnBasisAvailable = !!reportData?.return_basis_available;
  const twrView = returnBasisAvailable && returnBasis === 'twr';

  const returnBasisSelect = returnBasisAvailable ? (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-export-skip>
      <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Return basis</span>
      <select
        value={returnBasis}
        onChange={(e) => setReturnBasis(e.target.value)}
        className={`rounded border px-1 py-0.5 text-xs ${darkMode ? 'bg-[#1c1c1c] border-white/10 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
      >
        <option value="twr">Time-weighted</option>
        <option value="irr">IRR</option>
      </select>
    </label>
  ) : null;

  // FI distribution rating tables: the per-row yield column(s) honor the same
  // unified yield-on-cost selector as the headline panel. BOTH ends are ON COST —
  // 'ytm' = to (effective) maturity, 'ytw' = to worst (APX ytwoncost). The basis
  // is disclosed in each header so the on-cost figure — which runs high for
  // deep-discount or short-dated bonds (the pull-to-par annualizes large) — is
  // read as a yield-on-cost, not a market yield.
  const distColSpan = 6 + (yocBasis === 'both' ? 2 : 1);
  // YTW precedes YTM (matching FI Holdings and the Weighted Averages card):
  // the to-worst figure is by definition the smaller of the pair, so reading
  // left-to-right goes worst -> maturity. Headers and cells must swap in
  // lockstep - they pair by position.
  const renderDistYieldHeaders = () => (
    <>
      {yocBasis !== 'ytm' && (
        <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Avg YTW (on cost)</th>
      )}
      {yocBasis !== 'ytw' && (
        <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Avg YTM (on cost)</th>
      )}
    </>
  );
  const renderDistYieldCells = (item) => (
    <>
      {yocBasis !== 'ytm' && (
        <td className="p-3 text-right">{(pickSettle(item, 'average_ytw_on_cost') ?? pickSettle(item, 'average_ytm') ?? 0).toFixed(2)}</td>
      )}
      {yocBasis !== 'ytw' && (
        <td className="p-3 text-right">{(pickSettle(item, 'average_ytm') || 0).toFixed(2)}</td>
      )}
    </>
  );

  if (!isOpen || !reportData) return null;

  // The window's cents/whole-dollar choice, routed into every money helper below
  // from one place instead of ~220 call sites. Undefined (nobody has chosen) =
  // each column keeps its own native precision: some reports print whole dollars
  // on purpose because their APX parity is compared as printed strings, so a
  // single default would regress one convention or the other.
  const moneyDigits = MONEY_DIGITS[moneyPrecision];
  const curDigits = moneyDigits ?? 2;

  const formatCurrency = (value) => {
    // Fold a value that rounds to a negative zero ("-$0.00") back to "$0.00"
    // (N11 cosmetics) - a tiny negative residual must not read as a debit.
    const v = Object.is(value, -0) || (value < 0 && value > -(0.5 * 10 ** -curDigits)) ? 0 : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: reportData.currency || 'USD',
      minimumFractionDigits: curDigits,
      maximumFractionDigits: curDigits,
    }).format(v);
  };

  // Normalize a formatted number string so a value that rounds to zero from the
  // negative side ("-0.00") prints as a clean "0.00" (N11 cosmetics).
  const stripNegZero = (s) => (/^-0(?:\.0+)?$/.test(s) ? s.slice(1) : s);
  const fmtNum = (v, digits = 2) => {
    if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
    return stripNegZero(Number(v).toLocaleString('en-US', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }));
  };
  const fmtShares = (v) => {
    if (v === null || v === undefined) return '-';
    return Number(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  // Report-header entity-suffix casing (L8): a client name stored/title-cased
  // as "HAGGARD Zygose Rc" mangles the entity suffix. Re-uppercase ONLY the
  // known suffix tokens (LP/LLP/LLC/LC) so the header reads "... LP" like the
  // APX print; already-all-caps tokens ("HAGGARD") and every other word are
  // left exactly as the source supplied them. Scoped to the report header.
  const _ENTITY_SUFFIX = /^(LP|LLP|LLC|LC)$/i;
  const formatAccountLabel = (name) =>
    String(name || '')
      .split(' ')
      .map((w) => (_ENTITY_SUFFIX.test(w) ? w.toUpperCase() : w))
      .join(' ');

  // Render the printed report-header strip that appears at the top of every
  // Banyan-style PDF template (firm name, report title, client/account code
  // and the as-of date / date range). The whole strip is suppressed if no
  // header info was supplied so the legacy renderers stay backwards
  // compatible.
  const renderReportHeader = (header, fallbackDate) => {
    if (!header) return null;
    const firm = header.firm_name;
    const title = header.report_title;
    const acctName = formatAccountLabel(header.account_name);
    const acctCode = header.account_code;
    const dateLine = header.date_range_label || header.as_of_date || fallbackDate;
    const dateNotice =
      header.date_adjusted_notice || reportData?.dateAdjustedNotice || null;
    // Data-quality caveats surfaced by the API layer (holdings unknown /
    // unpriced holdings / cash-history limit). Each renders as its own amber
    // banner using the same styling as the date-clamp notice; skip any whose
    // text duplicates the date notice already shown above.
    const dataCaveats = (Array.isArray(header.dataCaveats) ? header.dataCaveats : [])
      .filter((c) => c && c.message && c.message !== dateNotice);
    // No STATIC fee-basis subtitle. The old permanent "GROSS OF FEES" line is
    // gone for good: header.basis_label is ignored even on an older cached
    // payload, and the DEFAULT (gross) view still carries no banner at all.
    //
    // A label renders only as the CONSEQUENCE OF A USER ACTION - flipping the
    // basis picker to net. Gross is the default, so an unlabelled report is
    // gross exactly as it was before the picker existed; net is the deviation,
    // and a deviation has to say so. Flipping back returns to the unlabelled
    // default. The backend ships BOTH labels (header.basis_labels.g/.n) so a
    // future ruling to always label is a one-line change here.
    //
    // This strip is shared by the viewer, the print/PDF clone and the xlsx
    // export, so the label follows the choice onto all three at once.
    const basisLabel = (header.basis_labels && feeBasisAvailable && netBasis)
      ? header.basis_labels.n
      : null;
    if (!firm && !title && !acctName && !acctCode && !dateLine && !dateNotice
        && !basisLabel && dataCaveats.length === 0) return null;
    return (
      <div className="text-center mb-2">
        {firm && (
          <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{firm}</div>
        )}
        {title && (
          <div className={`text-lg font-bold tracking-wider mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{title}</div>
        )}
        {title && basisLabel && (
          <div className={`text-sm font-semibold tracking-wider ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{basisLabel}</div>
        )}
        {(acctName || acctCode) && (
          <div className={`mt-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {acctName}{acctName && acctCode ? ' \u2022 ' : ''}{acctCode}
          </div>
        )}
        {dateLine && (
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {header.date_range_label ? dateLine : formatReportPrintedDate(dateLine)}
          </div>
        )}
        {dateNotice && (
          <div
            className={`text-sm mt-2 px-3 py-2 rounded ${
              darkMode
                ? 'bg-amber-950/50 text-amber-200 border border-amber-800/60'
                : 'bg-amber-50 text-amber-900 border border-amber-200'
            }`}
            role="status"
          >
            {dateNotice}
          </div>
        )}
        {dataCaveats.map((c, i) => (
          <div
            key={c.code || i}
            className={`text-sm mt-2 px-3 py-2 rounded ${
              darkMode
                ? 'bg-amber-950/50 text-amber-200 border border-amber-800/60'
                : 'bg-amber-50 text-amber-900 border border-amber-200'
            }`}
            role="status"
          >
            {c.message}
          </div>
        ))}
      </div>
    );
  };

  const renderSecurityCrossReference = () => {
    // Extract data matching PDF structure
    const {
      securities: allSecurities = [],
      security_info = {},
      no_holdings_reason = '',
    } = reportData;

    // Holdings basis: a position bought but not yet settled is not in custody on
    // the settlement basis, and one sold but not yet delivered still is.
    const securities = basisRows(allSecurities);
    const total_quantity = pickSettle(reportData, 'total_quantity') || 0;
    const total_market_value = pickSettle(reportData, 'total_market_value') || 0;
    const total_unrealized_gain_loss = pickSettle(reportData, 'total_unrealized_gain_loss') || 0;

    const formatNumber = (value) => {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US').format(value);
    };

    const formatPercent = (value) => {
      if (value === null || value === undefined) return '-';
      return `${value.toFixed(2)}`;
    };

    // APX prints unit costs/price to exactly 2 decimals (e.g. 100.06, not
    // the unrounded source value) regardless of how many decimals it carries.
    const formatUnitCost = (value) => {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const interestRate = security_info.coupon_rate ?? security_info.interest_rate;

    // Yield-on-cost basis: mirrors the FI Holdings column pattern ('both' shows
    // YTM-on-cost and YTW-on-cost side by side; the to-worst cell falls back to
    // the to-maturity figure when the row has no stored ytw-on-cost).
    const yocCols = yocBasis === 'both' ? ['ytm', 'ytw'] : [yocBasis];
    const yocColLabel = (b) =>
      (b === 'ytw' ? 'YOC (YTW)' : yocCols.length === 1 ? 'Yield On Cost' : 'YOC (YTM)');
    const yocColValue = (obj, b) =>
      (b === 'ytw'
        ? (pickSettle(obj, 'yield_to_worst_on_cost') ?? pickSettle(obj, 'yield_on_cost'))
        : pickSettle(obj, 'yield_on_cost'));

    // Per-portfolio expandable individual tax lots: rows with >1 lot get a
    // chevron + data-toggle-key (the viewer's delegated click handler drives
    // expandedRows); toggle-all expands/collapses every multi-lot row.
    const multiLotKeys = securities
      .filter((h) => (h.lots || []).length > 1)
      .map(lotRowKey);
    const allExpanded =
      multiLotKeys.length > 0 && multiLotKeys.every((k) => expandedRows?.[k]);

    return (
      <div className="space-y-4">
        {/* Security Header Info - matches PDF layout */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <div className="space-y-2">
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Security: {security_info.security_description || security_info.name || 'N/A'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Symbol: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{security_info.symbol || security_info.cusip || 'N/A'}</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Price: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{security_info.price ? formatUnitCost(security_info.price) : 'N/A'}</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Interest Rate: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{interestRate !== null && interestRate !== undefined && interestRate !== '' && !isNaN(Number(interestRate)) ? Number(interestRate).toFixed(2) : (interestRate || 'N/A')}</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Maturity Date: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{isoToMDY(security_info.maturity_date) || 'N/A'}</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Call Date: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{isoToMDY(security_info.call_date) || '-'}</span>
              </div>
              <div>
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'}>Put Date: </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{isoToMDY(security_info.put_date) || '-'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Holdings Table - matches PDF columns exactly */}
        {securities.length > 0 ? (
          <div className="space-y-2">
            {/* `report-sticky-toolbar`: the viewer freezes this bar with the
                column headers (index.css, .report-scroll scope), so the yield
                basis stays reachable from anywhere in a long report. It must
                stay a SIBLING of the table wrapper - the measured height is
                written on their shared parent and inherited by the <thead>. */}
            <div className="report-sticky-toolbar flex items-center justify-end gap-4" data-export-skip data-print-skip>
              {multiLotKeys.length > 0 && (
                <button
                  type="button"
                  data-toggle-all={allExpanded ? 'collapse' : 'expand'}
                  data-toggle-all-keys={multiLotKeys.join(',')}
                  className={`text-xs underline mr-auto ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {allExpanded ? 'Minimize All' : 'Expand All'}
                </button>
              )}
              {holdingsBasisSelect}
              {yocBasisSelect}
            </div>
            {basisDisclosure(allSecurities)}
            <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Portfolio Code</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Account Number</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Name</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Quantity</th>
                  <th className={`p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Unit Cost</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Adj Unit Cost</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Market Value</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Unrealized G/L</th>
                  {yocCols.map((b) => (
                    <th key={b} className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>{yocColLabel(b)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {securities.map((holding, index) => {
                  const hasLots = (holding.lots || []).length > 1;
                  const isExpanded = hasLots && !!expandedRows?.[lotRowKey(holding)];
                  const code = holding.portfolio_code || holding.codename || '-';
                  return (
                    <React.Fragment key={index}>
                      <tr
                        {...(hasLots ? { 'data-toggle-key': lotRowKey(holding) } : {})}
                        className={`border-b ${hasLots ? 'cursor-pointer ' : ''}${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <td className="p-2" data-export-text>
                          {hasLots ? (
                            <span className="inline-flex items-center gap-1">
                              {/* Screen-only affordance: stripped from the print DOM.
                                  data-no-insight keeps the viewer's field-insight
                                  click off it - the arrow only toggles the lots,
                                  while the portfolio code beside it stays a normal
                                  inspectable cell. */}
                              {isExpanded
                                ? <ChevronDown className="w-3.5 h-3.5" data-print-skip data-no-insight />
                                : <ChevronRight className="w-3.5 h-3.5" data-print-skip data-no-insight />}
                              {code}
                            </span>
                          ) : code}
                        </td>
                        <td className="p-2" data-export-text>{holding.account_number || '-'}</td>
                        <td className="p-2" data-export-text>{holding.account_name || holding.name || '-'}</td>
                        {/* A holder row is single-basis: a settlement-only holder
                            arrives built on its settlement figures, so the bare
                            fields are what its view's total carries. */}
                        <td className="p-2 text-right">{formatNumber(holding.quantity)}</td>
                        <td className="p-2 text-center">{isoToMDY(holding.purchase_date || holding.date) || '-'}</td>
                        <td className="p-2 text-right">{holding.unit_cost ? formatUnitCost(holding.unit_cost) : '-'}</td>
                        <td className="p-2 text-right">{holding.adjusted_unit_cost ? formatUnitCost(holding.adjusted_unit_cost) : '-'}</td>
                        <td className="p-2 text-right">{formatCurrency(holding.market_value || 0)}</td>
                        <td className={`p-2 text-right ${(holding.unrealized_gain_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(holding.unrealized_gain_loss || 0)}
                        </td>
                        {yocCols.map((b) => (
                          <td key={b} className="p-2 text-right">{formatPercent(yocColValue(holding, b))}</td>
                        ))}
                      </tr>
                      {isExpanded && (holding.lots || []).map((lot, li) => (
                        <tr
                          key={`lot-${li}`}
                          className={`border-b ${darkMode ? 'border-white/5 bg-[#101010] text-gray-400' : 'border-gray-100 bg-gray-50 text-gray-600'}`}
                        >
                          <td className="p-2" />
                          <td className="p-2" />
                          <td className="p-2" />
                          <td className="p-2 text-right">{formatNumber(lot.quantity)}</td>
                          <td className="p-2 text-center">{isoToMDY(lot.date) || '-'}</td>
                          <td className="p-2 text-right">{lot.unit_cost ? formatUnitCost(lot.unit_cost) : '-'}</td>
                          <td className="p-2 text-right">{lot.adjusted_unit_cost ? formatUnitCost(lot.adjusted_unit_cost) : '-'}</td>
                          <td className="p-2 text-right">{formatCurrency(lot.market_value || 0)}</td>
                          <td className={`p-2 text-right ${(lot.unrealized_gain_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(lot.unrealized_gain_loss || 0)}
                          </td>
                          {yocCols.map((b) => (
                            <td key={b} className="p-2 text-right">{formatPercent(yocColValue(lot, b))}</td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {/* Total Row */}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-400 bg-gray-100'}`}>
                  <td className="p-2">TOTAL</td>
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">{formatNumber(total_quantity || securities.reduce((sum, h) => sum + (h.quantity || 0), 0))}</td>
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                  <td className="p-2"></td>
                  <td className="p-2 text-right">{formatCurrency(total_market_value || securities.reduce((sum, h) => sum + (h.market_value || 0), 0))}</td>
                  <td className={`p-2 text-right ${(total_unrealized_gain_loss || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(total_unrealized_gain_loss || securities.reduce((sum, h) => sum + (h.unrealized_gain_loss || 0), 0))}
                  </td>
                  {yocCols.map((b) => (
                    <td key={b} className="p-2"></td>
                  ))}
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        ) : (
          // The backend distinguishes "that symbol matches no security" from
          // "a real security the selection does not hold on this date"; print
          // whichever it decided, so an empty cross reference is readable
          // instead of just blank.
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {no_holdings_reason || 'No holdings found for this security.'}
          </div>
        )}
      </div>
    );
  };

  const renderPortfolioSummary = () => {
    const {
      startDate,
      security_type_summary: securityTypeSummary,
      performance_history: performanceHistory,
    } = reportData;
    // End-date holdings/cash read on the active basis (the START column stays
    // settlement basis by design - see the toggle contract).
    const totalValue = pickSettle(reportData, 'totalValue');
    const holdingsValue = pickSettle(reportData, 'holdingsValue');
    const cashValue = pickSettle(reportData, 'cashValue');

    const formatReportAmount = (value) => {
      if (value === null || value === undefined) return '';
      return formatAmount(value, moneyDigits ?? 0);
    };

    // Firm convention: report yields display to the hundredth. Pct. Assets is
    // not a yield and keeps the APX tenths format, so it has its own formatter.
    const formatReportYield = (value) => {
      if (value === null || value === undefined) return '';
      return `${Number(value).toFixed(2)}`;
    };

    const formatReportPct = (value) => {
      if (value === null || value === undefined) return '';
      return `${Number(value).toFixed(1)}`;
    };

    const formatPercent = (value) => {
      if (value === null || value === undefined || Number.isNaN(value)) return '-';
      return `${Number(value).toFixed(2)}%`;
    };

    // Performance History rows - show ALL Pershing-supported asset classes, display N/A if not owned
    const performanceRows = [];
    // Always show all asset classes, even if null (will display as N/A)
    const assetClassOrder = [
      { key: 'fixed_income', label: 'Fixed Income' },
      { key: 'equity', label: 'Equity' },
      { key: 'options', label: 'Options' },
      { key: 'mutual_funds', label: 'Mutual Funds' }
    ];
    
    assetClassOrder.forEach(({ key, label }) => {
      const data = performanceHistory?.[key];
      if (data !== null && data !== undefined) {
        performanceRows.push({ name: label, ...data });
      } else {
        // Show N/A for asset classes not owned
        performanceRows.push({ 
          name: label, 
          month_to_date: null, 
          quarter_to_date: null, 
          year_to_date: null, 
          inception_to_date: null,
          isNA: true 
        });
      }
    });
    
    // Account total always shown
    if (performanceHistory?.account) {
      performanceRows.push({ name: 'Account', ...performanceHistory.account, isTotal: true });
    }

    // Index section placeholder - will be populated when index data is available
    const indexRows = [];
    if (Array.isArray(performanceHistory?.indexes)) {
      performanceHistory.indexes.forEach((index) => {
        indexRows.push({
          name: index.name || 'Index',
          ...index
        });
      });
    } else if (performanceHistory?.index) {
      indexRows.push({ name: performanceHistory.index.name || 'Index', ...performanceHistory.index });
    }

    // Use start date for the last column header (e.g., "12-31-24 To Date") instead of "Inception To Date"
    const startDateLabel = startDate
      ? `${startDate} To Date`
      : (performanceHistory?.inception_date
        ? `${performanceHistory.inception_date} To Date`
        : 'Inception To Date');

    // Holdings basis: a section only one basis holds anything of arrives
    // flagged for that basis alone (pending purchases only / sales awaiting
    // delivery only), exactly like a holding row.
    const securitySections = basisRows(securityTypeSummary?.sections || []);
    const securityTotal = securityTypeSummary?.total;
    // An account that held nothing at the date (emptied, or not yet funded) is
    // empty by fact: its totals print at zero, under the header's notice, where
    // missing data prints the not-loaded message instead.
    const showTotals = securitySections.length > 0 || Boolean(reportData.noHoldingsNotice);
    // Always render the ground-truth security-type layout for consistency across
    // dates. Previously this flipped to an asset-class/activity/performance layout
    // whenever holdings were absent, so the same report looked different by date.
    const groundTruthLayout = true;
    // PM Q1: the backend exposes both total-yield conventions; the cash checkbox
    // switches the to-maturity total between bond-only (FI Distribution match) and
    // cash-blended. Both the to-maturity and to-worst totals are ON COST.
    const hasYieldToggle = securityTotal && securityTotal.yield_bonds_only != null;
    const displayedTotalYield = hasYieldToggle
      ? pickSettle(securityTotal, includeCashYield ? 'yield_including_cash' : 'yield_bonds_only')
      : pickSettle(securityTotal, 'yield');
    // To-worst-on-cost total (the unified toggle companion); falls back to the
    // on-cost to-maturity total when the column is absent (pre-remap payloads).
    const displayedTotalYieldWorst =
      pickSettle(securityTotal, 'yield_to_worst_on_cost') ?? pickSettle(securityTotal, 'yield');

    // One "Yield" column per selected on-cost basis ('both' -> Mat + Worst side
    // by side, mirrored on the section rows and the total row so they align).
    const yocSectionCols = yocBasis === 'both' ? ['ytm', 'ytw'] : [yocBasis];
    const yocSectionLabel = (b) => {
      if (yocBasis === 'ytm') return 'Yield';
      if (yocBasis === 'ytw') return 'Yield (Worst)';
      return b === 'ytw' ? 'Yield (Worst)' : 'Yield (Mat)';
    };
    const yocSectionValue = (row, b) =>
      (b === 'ytw'
        ? (pickSettle(row, 'yield_to_worst_on_cost') ?? pickSettle(row, 'yield'))
        : pickSettle(row, 'yield'));
    const yocTotalValue = (b) => (b === 'ytw' ? displayedTotalYieldWorst : displayedTotalYield);

    return (
      <div className="space-y-6" data-export-sheet="Portfolio Summary">
        {securitySections.length > 0 && (
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            {/* `report-sticky-toolbar`: the viewer freezes this bar with the
                column headers (index.css, .report-scroll scope) so the yield
                basis and the cash-in-total-yield toggle stay reachable from
                anywhere in the table. It must stay a SIBLING of the table
                wrapper - the measured height is written on their shared parent
                (this card) and inherited by the <thead>. The bar sits ON that
                card, so it carries the card's colour as --report-toolbar-bg;
                the shared rule's white-sheet default would freeze a white strip
                across the tint. */}
            <div
              className={`report-sticky-toolbar flex items-center justify-end gap-4 mb-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}
              style={stickyToolbarOnCard}
              data-export-skip
            >
              {holdingsBasisSelect}
              {yocBasisSelect}
              {hasYieldToggle && (
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={includeCashYield}
                    onChange={(e) => setIncludeCashYield(e.target.checked)}
                  />
                  Include cash in total yield
                  {includeCashYield && securityTotal.cash_yield_source === 'assumed_bond_portfolio_average' && (
                    <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>
                      (no sweep rate — cash assumed at the bond average)
                    </span>
                  )}
                </label>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className="p-2 text-left">Security Type</th>
                    <th className="p-2 text-right">Adjusted Cost</th>
                    <th className="p-2 text-right">Market Value</th>
                    <th className="p-2 text-right">Pct. Assets</th>
                    {yocSectionCols.map((b) => (
                      <th key={b} className="p-2 text-right">{yocSectionLabel(b)}</th>
                    ))}
                    <th className="p-2 text-right">Est. Annual Income</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // APX layout: sections print under their asset-class block
                    // (Fixed Income closed by Accrued Interest, then Other, then
                    // Cash and Equivalents), each block ending in an underlined
                    // subtotal - so the table shows the ex-cash total AND the
                    // with-cash TOTAL PORTFOLIO. Payloads without asset_class
                    // (older cached reports) keep the flat layout.
                    const sectionRow = (row) => (
                      <tr
                        key={row.name}
                        className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} ${row.is_accrued_interest ? 'italic' : ''}`}
                      >
                        <td className="p-2">{row.name}</td>
                        <td className="p-2 text-right">{formatReportAmount(pickSettle(row, 'adjusted_cost'))}</td>
                        <td className="p-2 text-right">{formatReportAmount(pickSettle(row, 'market_value'))}</td>
                        <td className="p-2 text-right">{formatReportPct(pickSettle(row, 'pct'))}</td>
                        {yocSectionCols.map((b) => (
                          <td key={b} className="p-2 text-right">{formatReportYield(yocSectionValue(row, b))}</td>
                        ))}
                        <td className="p-2 text-right">{formatReportAmount(pickSettle(row, 'est_annual_income'))}</td>
                      </tr>
                    );
                    if (!securitySections.some((r) => r.asset_class)) {
                      return securitySections.map(sectionRow);
                    }
                    // Settlement basis ships a parallel map, not per-field
                    // mirrors inside each subtotal object.
                    const classSubtotals = pickSettle(securityTypeSummary, 'class_subtotals') || {};
                    const colCount = 5 + yocSectionCols.length;
                    // APX block order: Fixed Income, Equity, Other, Cash. A class
                    // missing here would silently DROP its sections from the table,
                    // so keep this in step with apx_reports._class_rank.
                    const GROUPS = [
                      { cls: 'fixed_income', label: 'Fixed Income' },
                      { cls: 'equity', label: 'Equity' },
                      { cls: 'other', label: 'Other' },
                      { cls: 'cash', label: 'Cash and Equivalents' },
                    ];
                    return GROUPS.flatMap(({ cls, label }) => {
                      const rows = securitySections.filter((r) => r.asset_class === cls);
                      if (!rows.length) return [];
                      const sub = classSubtotals[cls];
                      const out = [
                        // <th>: group headers are labels, not data - the
                        // field-insight delegation matches td only.
                        <tr key={`hdr-${cls}`}>
                          <th
                            colSpan={colCount}
                            className={`p-2 pt-3 text-left font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}
                          >
                            {label}
                          </th>
                        </tr>,
                        ...rows.map(sectionRow),
                      ];
                      if (sub) {
                        out.push(
                          <tr
                            key={`sub-${cls}`}
                            // The name cell is blank (APX style); this feeds the
                            // field-insight row context for the numeric cells.
                            data-row-description={`${label} subtotal`}
                            className={`border-b font-semibold ${darkMode ? 'border-white/10' : 'border-gray-200'}`}
                          >
                            <td className="p-2" />
                            <td className={`p-2 text-right border-t ${darkMode ? 'border-white/40' : 'border-gray-500'}`}>{formatReportAmount(pickSettle(sub, 'adjusted_cost'))}</td>
                            <td className={`p-2 text-right border-t ${darkMode ? 'border-white/40' : 'border-gray-500'}`}>{formatReportAmount(pickSettle(sub, 'market_value'))}</td>
                            <td className={`p-2 text-right border-t ${darkMode ? 'border-white/40' : 'border-gray-500'}`}>{formatReportPct(pickSettle(sub, 'pct'))}</td>
                            {yocSectionCols.map((b) => (
                              <td key={b} className={`p-2 text-right border-t ${darkMode ? 'border-white/40' : 'border-gray-500'}`}>{formatReportYield(yocSectionValue(sub, b))}</td>
                            ))}
                            <td className={`p-2 text-right border-t ${darkMode ? 'border-white/40' : 'border-gray-500'}`}>{formatReportAmount(pickSettle(sub, 'est_annual_income'))}</td>
                          </tr>,
                        );
                      }
                      return out;
                    });
                  })()}
                  {securityTotal && (
                    <tr className={`font-semibold ${darkMode ? 'bg-white/5' : 'bg-gray-100'}`}>
                      <td className="p-2">TOTAL PORTFOLIO</td>
                      <td className="p-2 text-right">{formatReportAmount(pickSettle(securityTotal, 'adjusted_cost'))}</td>
                      <td className="p-2 text-right">{formatReportAmount(pickSettle(securityTotal, 'market_value'))}</td>
                      <td className="p-2 text-right">{formatReportPct(pickSettle(securityTotal, 'pct'))}</td>
                      {yocSectionCols.map((b) => (
                        <td key={b} className="p-2 text-right">{formatReportYield(yocTotalValue(b))}</td>
                      ))}
                      <td className="p-2 text-right">{formatReportAmount(pickSettle(securityTotal, 'est_annual_income'))}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showTotals ? (
          <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Value</p>
                <p data-insight-field="Total Value" className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(totalValue || 0)}
                </p>
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Securities Value</p>
                <p data-insight-field="Market Value" className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(holdingsValue || 0)}
                </p>
              </div>
              <div>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cash Value</p>
                <p data-insight-field="Cash Value" className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(cashValue || 0)}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className={`p-6 rounded-lg text-center border ${darkMode ? 'bg-[#141414] border-white/10 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
            <p className="text-base font-medium">No holdings data is available for this date.</p>
            <p className="text-sm mt-1">
              Holdings (Portfolio Summary) data is available from June 1, 2026 onward; earlier dates are not yet loaded into the reporting lake.
            </p>
          </div>
        )}

        {/* Export/print parity: the Total/Market/Cash Value figures are shown as
            summary cards (<p>, not a table), so the xlsx walker would drop them.
            Mirror them in a hidden table (display:none -> excluded from the visual
            and print copy, but still walked via textContent) so the exported sheet
            carries the same totals as the report. */}
        {showTotals && (
          <table style={{ display: 'none' }} aria-hidden="true">
            <tbody>
              <tr><td>Total Value</td><td>{formatCurrency(totalValue || 0)}</td></tr>
              <tr><td>Securities Value</td><td>{formatCurrency(holdingsValue || 0)}</td></tr>
              <tr><td>Cash Value</td><td>{formatCurrency(cashValue || 0)}</td></tr>
            </tbody>
          </table>
        )}

        {!groundTruthLayout && (
        <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
          <h4 className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Performance History
          </h4>
          {performanceRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className="p-2 text-left">Asset Class</th>
                    <th className="p-2 text-right">Month To Date</th>
                    <th className="p-2 text-right">Quarter To Date</th>
                    <th className="p-2 text-right">Year To Date</th>
                    <th className="p-2 text-right">{startDateLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceRows.map((row) => {
                    const isNA = row.isNA || (row.month_to_date === null && row.quarter_to_date === null && row.year_to_date === null && row.inception_to_date === null);
                    return (
                      <tr
                        key={row.name}
                        className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} ${row.isTotal ? 'font-bold' : ''}`}
                      >
                        <td className="p-2">{row.name}</td>
                        <td className="p-2 text-right">{isNA ? 'N/A' : formatPercent(row.month_to_date)}</td>
                        <td className="p-2 text-right">{isNA ? 'N/A' : formatPercent(row.quarter_to_date)}</td>
                        <td className="p-2 text-right">{isNA ? 'N/A' : formatPercent(row.year_to_date)}</td>
                        <td className="p-2 text-right">{isNA ? 'N/A' : formatPercent(row.inception_to_date)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Index Section - Placeholder for future implementation */}
              {visibleIndexes(indexRows).length > 0 && (
                <>
                  <h5 className={`text-md font-semibold mt-4 mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Index
                  </h5>
                  <table className={`w-full text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    <tbody>
                      {visibleIndexes(indexRows).map((row) => (
                        <tr key={row.name} className={`group border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                          <td className="p-2"><span className="inline-flex items-center">{row.name}{indexHideBtn(row)}</span></td>
                          <td className="p-2 text-right">{formatPercent(row.month_to_date)}</td>
                          <td className="p-2 text-right">{formatPercent(row.quarter_to_date)}</td>
                          <td className="p-2 text-right">{formatPercent(row.year_to_date)}</td>
                          <td className="p-2 text-right">{formatPercent(row.inception_to_date)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              {/* TODO: Add Index benchmark comparisons when index data is available */}
            </div>
          ) : (
            <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>
              No performance history data available.
            </p>
          )}
        </div>
        )}
      </div>
    );
  };

  const renderAssetAllocation = () => {
    // The whole bucket map is the mirrored field here: a settlement view
    // reweights every bucket at once, so the payload carries `allocations_settle`
    // rather than a per-bucket suffix.
    const allocations = pickSettle(reportData, 'allocations');

    return (
      <div className="space-y-4">
        {holdingsBasisSelect && (
          <div className="flex items-center justify-end" data-export-skip data-print-skip>
            {holdingsBasisSelect}
          </div>
        )}
        {/* This report is nothing BUT this block, and it draws its rows as divs,
            so its export used to be the walker's last-resort text dump. Each
            row's cells are the class span and the bar/percentage wrapper, whose
            only text is the percentage (the bar itself is an empty div). */}
        <div
          className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}
          data-export-rows="Asset Allocation"
        >
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Asset Allocation
          </h3>
          <div className="space-y-3">
            {allocations && Object.entries(allocations).map(([assetClass, percentage]) => (
              <div key={assetClass} data-export-row className="flex items-center justify-between">
                <span className={`font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  {assetClass}
                </span>
                <div className="flex items-center gap-3">
                  <div className={`w-48 h-4 rounded-full ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-200'}`}>
                    <div
                      className={`h-full rounded-full ${darkMode ? 'bg-white' : 'bg-gray-900'}`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className={`font-semibold w-16 text-right ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {percentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Money-weighted (Average Capital Base) section shared by the portfolio
  // performance summary and the compound summary. It carries BOTH return
  // bases - time-weighted by default, IRR on the toggle. Honesty rules: a
  // suspect end value (pending trade settlements, F1a) nulls the IRR and
  // Total Gain - render a dash + note, never a sign-flipped number - while
  // the time-weighted figure, linked from the daily PERFORMANCE series rather
  // than the end appraisal, survives and keeps printing; the
  // realized/unrealized split is Advent's OnMval attribution when
  // realized_basis === 'onmval' (no caveat), and dashes with a why-note when
  // the beginning-of-window market value is unsourceable.
  const renderMoneyWeightedSection = (mw, toolbar = null) => {
    if (!mw) return null;
    const money = (v) => (v == null ? '-' : formatCurrency(v));
    const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(2)}%`);
    const suspect = mw.reliability === 'end_mv_suspect_pending_settlements';
    const mixedAccrued = mw.change_in_accrued_basis && mw.change_in_accrued_basis !== 'snapshot';
    // realized_basis === 'onmval' means the split IS Advent's convention
    // (proceeds - beginning-of-window market value): no caveat needed. Any
    // other basis means the begin MV could not be sourced and the split lines
    // are nulled - keep the flag and say why.
    const onmval = mw.realized_basis === 'onmval';
    const rows = [
      { label: 'Realized Gains', value: money(mw.realized_gains), flag: !onmval },
      { label: 'Unrealized Gains', value: money(mw.unrealized_gains), flag: !onmval },
      { label: 'Interest', value: money(mw.interest) },
      { label: 'Dividends', value: money(mw.dividends) },
      { label: 'Change in Accrued Interest', value: money(mw.change_in_accrued_interest), flag: mixedAccrued },
      { label: 'Average Capital', value: money(mw.average_capital_base), strong: true },
      { label: 'Total Gain before Fees', value: money(mw.total_gain_before_fees), strong: true },
      // Headline return row - the ONLY row the basis control moves. Average
      // Capital and Total Gain are the same dollars on either basis, so they
      // stay put and the two headlines stay comparable across a flip.
      twrView
        ? { label: mw.window_twr_label || 'Time-Weighted Return', value: pct(mw.window_twr_pct), strong: true }
        : { label: mw.irr_period_label || 'IRR', value: pct(mw.account_irr_pct), strong: true },
    ];
    // The linked tape can stop BEFORE the requested window and still return a
    // number for the part it covers, so a short series has to say so - a TWR
    // silently measuring a shorter window is the failure mode worth shouting.
    const coverage = reportData.twr_coverage;
    const shortCoverage = twrView && coverage && coverage.last
      && coverage.last < (reportData.endDate || reportData.activity_summary?.end_date);
    return (
      <div>
        <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          {twrView ? 'AVERAGE CAPITAL & TIME-WEIGHTED RETURN' : 'AVERAGE CAPITAL & IRR'}
        </h3>
        {/* Same h3 -> toolbar -> content shape as the PERFORMANCE HISTORY card's
            gross/net bar: the basis control sits beside the block it moves. */}
        {toolbar && (
          <div className="-mt-2 mb-3 flex justify-end" data-export-skip data-print-skip>
            {toolbar}
          </div>
        )}
        {/* The card is a div list, not a table, so the spreadsheet walker used
            to skip the whole block - the export lost Average Capital, Total
            Gain and the headline return. `data-export-rows` names its sheet
            (on the basis on screen, like the h3 above) and `data-export-row`
            marks what becomes a row: label span + value span for a figure,
            bare text for a caveat sentence. Display-only, no look change. */}
        <div
          className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}
          data-export-rows={twrView ? 'Average Capital & TWR' : 'Average Capital & IRR'}
        >
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} data-export-row className={`flex justify-between ${r.strong ? 'border-t pt-2 mt-2 font-semibold' : ''} ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>
                  {r.label}
                  {r.flag ? ' *' : ''}
                </span>
                <span className={darkMode ? 'text-white' : 'text-gray-900'}>{r.value}</span>
              </div>
            ))}
          </div>
          {/* Net rides under the gross headline rather than replacing it: the
              net series is a separate tape that can fall short of the window,
              and a headline that vanishes reads as a data gap. No
              data-export-skip - these caveats belong on the printed page. */}
          {twrView && (
            <>
              {mw.window_twr_pct == null && (
                <p className="mt-1 text-right text-xs italic text-gray-500" data-export-row>
                  {twrUnavailableCopy(mw.window_twr_unavailable_reason)}
                </p>
              )}
              {/* Label and value in their OWN spans, not one template literal:
                  the export walker reads a row's element children as its cells,
                  so a single string would land the net return in the sheet as
                  the prose "Net: 4.61%" while the gross headline above it lands
                  as a real number. Two inline spans with the space inside the
                  label render the same "Net: 4.61%" as before. */}
              <div className={`mt-1 text-right text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-row>
                <span>{'Net: '}</span>
                <span>{pct(mw.window_twr_pct_net)}</span>
              </div>
              {mw.window_twr_pct_net == null && (
                <p className="mt-1 text-right text-xs italic text-gray-500" data-export-row>
                  net return unavailable for this window
                </p>
              )}
              {shortCoverage && (
                <p className={`mt-2 text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`} data-export-row>
                  {`TWR series covers ${coverage.first} to ${coverage.last}`}
                </p>
              )}
            </>
          )}
          {suspect && (
            <p className={`mt-3 text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`} data-export-row>
              {twrView
                ? 'End value provisional - pending trade settlements; Total Gain is withheld until the lot data settles.'
                : 'End value provisional - pending trade settlements; IRR and Total Gain are withheld until the lot data settles.'}
            </p>
          )}
          {(!onmval || mixedAccrued) && (
            <p className={`mt-2 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-export-row>
              {'* '}
              {!onmval
                ? 'Realized/Unrealized split unavailable - beginning-of-window market value could not be sourced for this window'
                : ''}
              {!onmval && mixedAccrued ? '; ' : ''}
              {mixedAccrued ? 'accrued change uses a mixed snapshot/replay basis for this window' : ''}.
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderPortfolioPerformanceSummary = () => {
    const {
      asset_classes = [],
      activity_summary = {},
      performance = [],
      indexes = [],
    } = reportData;

    const fmtPct = (v) => {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
      return `${Number(v).toFixed(2)}%`;
    };

    const changeStart = activity_summary.start_date;
    const changeEnd = activity_summary.end_date;
    const changeLabel = changeStart && changeEnd
      ? `Change from ${changeStart} to ${changeEnd}`
      : 'Change';

    const inceptionColLabel = reportData?.header?.inception_date
      ? `${reportData.header.inception_date} To Date`
      : 'Inception To Date';

    // Activity rows grouped exactly like the PDF: opening / flows / closing
    // then a subtotal, then income breakdown then another subtotal.
    const activityGroupA = [
      { label: `Portfolio Value on ${changeStart || '—'}`, value: activity_summary.portfolio_value_start },
      { label: 'Net Additions & Withdrawals', value: activity_summary.net_additions_withdrawals },
      { label: 'Net Intra Account Transfers', value: activity_summary.net_intra_account_transfers },
      { label: 'Adjusted Portfolio Value', value: activity_summary.adjusted_portfolio_value, subtotal: true },
      { label: `Portfolio Value on ${changeEnd || '—'}`, value: activity_summary.portfolio_value_end },
      { label: changeLabel, value: activity_summary.change_subtotal, subtotal: true },
    ];
    const activityGroupB = [
      { label: 'Income Received', value: activity_summary.income_received },
      { label: 'Change in Market Value', value: activity_summary.change_in_market_value },
      { label: 'Change in Accrued Interest', value: activity_summary.change_in_accrued_interest },
      { label: changeLabel, value: activity_summary.change_subtotal_2, subtotal: true },
    ];

    return (
      <div className="space-y-6">
        {/* Portfolio Summary (asset class) */}
        <div>
          <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>PORTFOLIO SUMMARY</h3>
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-100'}`}>
                  <th className="p-3 text-left font-semibold">Asset Class</th>
                  <th className="p-3 text-right font-semibold">Market Value</th>
                  <th className="p-3 text-right font-semibold">Pct.</th>
                </tr>
              </thead>
              <tbody>
                {asset_classes.map((row, i) => (
                  <tr
                    key={`${row.asset_class}-${i}`}
                    className={`border-b ${
                      row.is_total
                        ? (darkMode ? 'border-gray-500 bg-gray-700 font-bold' : 'border-gray-400 bg-gray-50 font-bold')
                        : (darkMode ? 'border-white/10' : 'border-gray-200')
                    }`}
                  >
                    <td className="p-3">{row.asset_class}</td>
                    <td className="p-3 text-right">{formatCurrency(row.market_value || 0)}</td>
                    <td className="p-3 text-right">{(row.pct || 0).toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Summary */}
        <div>
          <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>ACTIVITY SUMMARY</h3>
        </div>
        {/* Two cards of label/value divs, not a table (the compound summary's
            activity block IS a table and needs nothing), so the spreadsheet
            walker never saw them. One sheet for both halves: they are one
            ACTIVITY SUMMARY on the page and the second half re-states the same
            change from the income side. Attributes only, no look change. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-export-rows="Activity Summary">
          {[activityGroupA, activityGroupB].map((group, gi) => (
            <div key={gi} className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
              <div className="space-y-2">
                {group.map((item, i) => (
                  <div key={i} data-export-row className={`flex justify-between ${item.subtotal ? 'border-t pt-2 mt-2 font-semibold' : ''} ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{item.label}</span>
                    <span className={`${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {item.value === null || item.value === undefined ? '-' : formatCurrency(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {renderMoneyWeightedSection(reportData.money_weighted, returnBasisSelect)}

        {/* Performance History */}
        <div>
          <h3 className={`text-xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>PERFORMANCE HISTORY</h3>
          {/* `report-sticky-toolbar`: frozen with the column headers so the
              gross/net basis stays reachable while reading the table. Sibling
              of the table wrapper, so the measured height lands on the block
              that holds both. This bar sits on the white report sheet, so it
              needs no --report-toolbar-bg override. */}
          {feeBasisSelect && (
            <div className="report-sticky-toolbar -mt-2 mb-3 flex justify-end" data-export-skip data-print-skip>
              {feeBasisSelect}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-100'}`}>
                  <th className="p-3 text-left font-semibold">Asset Class</th>
                  <th className="p-3 text-right font-semibold">Month To Date</th>
                  <th className="p-3 text-right font-semibold">Quarter To Date</th>
                  <th className="p-3 text-right font-semibold">Year To Date</th>
                  <th className="p-3 text-right font-semibold">{inceptionColLabel}</th>
                </tr>
              </thead>
              <tbody>
                {/* pickBasis: gross by default; net when the user flips the
                    basis picker, and a dash where no net figure exists. Benchmark
                    rows mirror gross onto both bases by design (the fee is
                    ours, not the index's), so they never move. */}
                {performance.map((row, i) => (
                  <tr key={`${row.label}-${i}`} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-3">{row.label}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(row, 'month_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(row, 'quarter_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(row, 'year_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(row, 'inception_to_date'))}</td>
                  </tr>
                ))}
                {visibleIndexes(indexes).map((idx, i) => (
                  <tr key={`idx-${i}`} className={`group border-b italic ${darkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
                    <td className="p-3"><span className="inline-flex items-center">{idx.label}{indexHideBtn(idx)}</span></td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(idx, 'month_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(idx, 'quarter_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(idx, 'year_to_date'))}</td>
                    <td className="p-3 text-right">{fmtPct(pickBasis(idx, 'inception_to_date'))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {indexes.length === 0 && (
              <p className={`mt-2 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Benchmark index values are not available for this report date.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFixedIncomeDistribution = () => {
    const {
      summary = {},
      moody_distribution: moodyAll = [],
      sp_distribution: spAll = [],
      maturity_distribution: maturityAll = [],
      show_summary_information = true,
      show_distribution_by_moody_rating = true,
      show_distribution_by_sp_rating = true,
      show_distribution_by_maturity = false,
    } = reportData;
    // A bucket whose only holding sits on one side of the settle/trade divide
    // arrives flagged with zeroed off-basis columns; hide it on the other basis.
    const moody_distribution = basisRows(moodyAll);
    const sp_distribution = basisRows(spAll);
    const maturity_distribution = basisRows(maturityAll);

    const totals = summary.totals || {};
    const weightedAverages = summary.weighted_averages || {};

    return (
      <div className="space-y-6">
        {/* The distribution tables are separate blocks, so the basis picker heads
            the report rather than freezing over one of them (the report-sticky
            -toolbar contract wants a bar that is a SIBLING of a table wrapper). */}
        {holdingsBasisSelect && (
          <div className="flex items-center justify-end" data-export-skip data-print-skip>
            {holdingsBasisSelect}
          </div>
        )}
        {show_summary_information && (
          <div>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Summary Information
            </h3>
            {/* `report-kv-summary` lets the paged/print stylesheet shrink these
                card DIVs to the 9px table body size. Without it the summary is
                the only non-<table> block in the report and keeps its 16-20px
                on-screen size on paper, towering over every 9px distribution
                table (see pagedReportCss.js). On screen the class is inert. */}
            <div className="grid grid-cols-2 gap-6 report-kv-summary">
              <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`text-lg font-semibold mb-3 border-b pb-2 ${darkMode ? 'text-white border-gray-700' : 'text-gray-900 border-gray-300'}`}>
                  Totals
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Par Value</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'par_value') || 0)}
                    </span>
                  </div>
                  {/* Both figures are qualified because neither is the naive
                      reading of a bare label: market_value is DIRTY (clean MV +
                      accrued, the APX bonddist convention) and total_cost is
                      APPRAISAL.costbasis, which the lake stores ADJUSTED
                      (amortized toward par), not original purchase cost. */}
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Market Value (incl. accrued interest)</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'market_value') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Total Cost (adjusted)</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'total_cost') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Net Gain/Loss</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'net_gain_loss') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Annual Income</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'annual_income') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Accrued Interest</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {formatCurrency(pickSettle(totals, 'accrued_interest') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Number of Issues</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {pickSettle(totals, 'number_of_issues') || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                <div className={`flex items-center justify-between mb-3 border-b pb-2 ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
                  <h4 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    Weighted Averages
                  </h4>
                  {yocBasisSelect}
                </div>
                <div className="space-y-2">
                  {/* YTW above YTM, matching FI Holdings' column order: the
                      to-worst figure is by definition the smaller of the pair,
                      so reading top-to-bottom goes worst -> maturity. */}
                  {yocBasis !== 'ytm' && (
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average YTW (to worst, on cost)</span>
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {(pickSettle(weightedAverages, 'average_ytw_on_cost') ?? pickSettle(weightedAverages, 'average_ytm') ?? 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {yocBasis !== 'ytw' && (
                    <div className="flex justify-between">
                      <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average YTM (to maturity, on cost)</span>
                      <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {(pickSettle(weightedAverages, 'average_ytm') || 0).toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average Maturity (yrs)</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {(pickSettle(weightedAverages, 'average_maturity_yrs') || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average Coupon (%)</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {/* APX bonddist prints the summary coupon at 2dp (bucket rows stay 3dp) */}
                      {(pickSettle(weightedAverages, 'average_coupon_pct') || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average Duration</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {(pickSettle(weightedAverages, 'average_duration') || 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average Moody Rating</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {pickSettle(weightedAverages, 'average_moody_rating') || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Average S&P Rating</span>
                    <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      {pickSettle(weightedAverages, 'average_sp_rating') || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {show_distribution_by_maturity && (
          <div>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Distribution by Maturity
            </h3>
            <div className="overflow-x-auto">
              <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-100'}`}>
                    <th className={`p-3 text-left font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Maturity Range</th>
                    <th className={`p-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Number</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Mkt Value</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>% Bond Holdings</th>
                    {renderDistYieldHeaders()}
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Coupon</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {maturity_distribution.length > 0 ? (
                    maturity_distribution.map((item, index) => (
                      <tr
                        key={index}
                        className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <td className={`p-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.label}
                        </td>
                        <td className="p-3 text-center">{pickSettle(item, 'number') || 0}</td>
                        <td className="p-3 text-right">{formatCurrency(pickSettle(item, 'market_value') || 0)}</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'pct_bond_holdings') || 0).toFixed(1)}</td>
                        {renderDistYieldCells(item)}
                        <td className="p-3 text-right">{(pickSettle(item, 'average_coupon') || 0).toFixed(3)}%</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'average_duration') || 0).toFixed(1)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={distColSpan} className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        No maturity distribution data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {show_distribution_by_moody_rating && (
          <div>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Distribution by Moody Rating
            </h3>
            <div className="overflow-x-auto">
              <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-100'}`}>
                    <th className={`p-3 text-left font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rating</th>
                    <th className={`p-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Number</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Mkt Value</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>% Bond Holdings</th>
                    {renderDistYieldHeaders()}
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Coupon</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {moody_distribution.length > 0 ? (
                    moody_distribution.map((item, index) => (
                      <tr
                        key={index}
                        className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <td className={`p-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.rating || 'Not Rated'}
                        </td>
                        <td className="p-3 text-center">{pickSettle(item, 'number') || 0}</td>
                        <td className="p-3 text-right">{formatCurrency(pickSettle(item, 'market_value') || 0)}</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'pct_bond_holdings') || 0).toFixed(1)}</td>
                        {renderDistYieldCells(item)}
                        <td className="p-3 text-right">{(pickSettle(item, 'average_coupon') || 0).toFixed(3)}%</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'average_duration') || 0).toFixed(1)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={distColSpan} className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        No Moody rating distribution data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {show_distribution_by_sp_rating && (
          <div>
            <h3 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Distribution by S&P Rating
            </h3>
            <div className="overflow-x-auto">
              <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-100'}`}>
                    <th className={`p-3 text-left font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Rating</th>
                    <th className={`p-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Number</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Mkt Value</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>% Bond Holdings</th>
                    {renderDistYieldHeaders()}
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Coupon</th>
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {sp_distribution.length > 0 ? (
                    sp_distribution.map((item, index) => (
                      <tr
                        key={index}
                        className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                      >
                        <td className={`p-3 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.rating || 'Not Rated'}
                        </td>
                        <td className="p-3 text-center">{pickSettle(item, 'number') || 0}</td>
                        <td className="p-3 text-right">{formatCurrency(pickSettle(item, 'market_value') || 0)}</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'pct_bond_holdings') || 0).toFixed(1)}</td>
                        {renderDistYieldCells(item)}
                        <td className="p-3 text-right">{(pickSettle(item, 'average_coupon') || 0).toFixed(3)}%</td>
                        <td className="p-3 text-right">{(pickSettle(item, 'average_duration') || 0).toFixed(1)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={distColSpan} className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        No S&P rating distribution data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFixedIncomeHoldingsByMaturity = () => {
    const {
      groups = [],
      grand_total = {},
      // Legacy/fallback fields if backend returned the old shape
      holdings = [],
      holdings_by_maturity = {},
      totals = {},
    } = reportData;

    const fmtPercent = (v, digits = 3) => {
      if (v === null || v === undefined || Number.isNaN(Number(v))) return '-';
      return `${Number(v).toFixed(digits)}%`;
    };

    // Yield-on-cost basis: one trailing column per selected basis ('both' shows
    // YTM-on-cost and YTW-on-cost side by side). The to-worst cell falls back to
    // the to-maturity figure when the row has no stored ytw-on-cost.
    // YTW precedes YTM: the to-worst is by definition the SMALLER of the pair, so
    // reading left-to-right goes worst -> maturity (APX itself prints a single
    // "Yield On Cost", which is the to-maturity figure).
    const yocCols = yocBasis === 'both' ? ['ytw', 'ytm'] : [yocBasis];
    const yocColLabel = (b) => (b === 'ytw' ? 'YOC (YTW)' : 'YOC (YTM)');
    const yocColValue = (obj, b) =>
      (b === 'ytw'
        ? (pickSettle(obj, 'yield_to_worst_on_cost') ?? pickSettle(obj, 'yield_on_cost'))
        : pickSettle(obj, 'yield_on_cost'));

    // "Show market yield" APPENDS a market column per on-cost column shown, in
    // the same horizon order, so the existing layout never shifts when the box
    // is off and each pair reads worst -> maturity like the on-cost block. The
    // "(Mkt)" suffix is not optional: FI Distribution already calls an ON-COST
    // number "ytm", so an unsuffixed market column here would be ambiguous.
    // No fallback between horizons - an absent market yield prints '-' rather
    // than borrowing the other horizon's number.
    const mktCols = showMarketYield ? yocCols : [];
    const mktColLabel = (b) => (b === 'ytw' ? 'YTW (Mkt)' : 'YTM (Mkt)');
    const mktColValue = (obj, b) =>
      pickSettle(obj, b === 'ytw' ? 'yield_to_worst' : 'yield_to_maturity');

    // Column layout follows APX's own export (cols A..S): the per-100 price block
    // (Unit Cost, Adj Unit Cost, Mkt Price) sits immediately right of Description,
    // ahead of the extended money columns. Body rows, every section subtotal, and
    // the GRAND TOTAL all live in ONE <table> so the columns line up (separate
    // tables auto-size independently) and paged.js repeats the single <thead>
    // across split pages. The 11 leading columns (Par Value..Mkt Price) carry the
    // section/total labels - APX likewise leaves its J/K/L price cells blank on a
    // "<SECTION> (USD) Total" row; then the four money columns (Total Cost, Adj
    // Cost, Market Value, Accrued Int.), the two rating columns, and the
    // yield-on-cost column(s) mirror the body cells exactly.
    const LEAD_COLS = 11; // Par Value .. Mkt Price
    // + 4 money + 2 rating + on-cost yields + the optional market-yield pair
    const totalCols = LEAD_COLS + 6 + yocCols.length + mktCols.length;

    const headTh = (extra) =>
      `p-2 font-semibold ${extra} ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const renderHead = () => (
      <thead>
        <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
          <th className={headTh('text-right')}>Par Value</th>
          <th className={headTh('text-center')}>Purchase Date</th>
          <th className={headTh('text-center')}>Settle Date</th>
          <th className={headTh('text-center')} title="Bond status: r=prerefunded/escrow, c=called, p=putable, blank=runs to maturity">Stat</th>
          <th className={headTh('text-center')}>Call</th>
          <th className={headTh('text-center')}>Put</th>
          <th className={headTh('text-left')}>Symbol / CUSIP</th>
          <th className={headTh('text-left')}>Description</th>
          <th className={headTh('text-right')}>Unit Cost</th>
          <th className={headTh('text-right')}>Adj Unit Cost</th>
          <th className={headTh('text-right')}>Mkt Price</th>
          <th className={headTh('text-right')}>Total Cost</th>
          <th className={headTh('text-right')}>Adj Cost</th>
          <th className={headTh('text-right')}>Market Value</th>
          <th className={headTh('text-right')}>Accrued Int.</th>
          <th className={headTh('text-center')}>Moody</th>
          <th className={headTh('text-center')}>S&amp;P</th>
          {yocCols.map((b) => (
            <th key={b} className={headTh('text-right')}>{yocColLabel(b)}</th>
          ))}
          {mktCols.map((b) => (
            <th key={`mkt-${b}`} className={headTh('text-right')}>{mktColLabel(b)}</th>
          ))}
        </tr>
      </thead>
    );

    // Individual tax lots. A holding built from more than one purchase carries
    // its lots in the payload; expanding prints them as muted sub-rows under the
    // lumped holding. DISPLAY ONLY - every subtotal and the grand total are the
    // backend's, computed from the LUMPED rows, so expanding can never
    // double-count. The popup's "Show Individual Tax Lots" checkbox seeds every
    // one of these expanded (windows/ReportPopup.jsx).
    const rowHasLots = (r) => (r.lots || []).length > 1;
    // Only the columns DEFINED per lot carry a value; adjusted cost, the market
    // price block and the ratings are position- or security-level and stay
    // blank rather than repeat or invent a figure. The italic label is what
    // identifies the row in PRINT, where the chevron and the muted background
    // are both flattened away - and it is NUMBERED, because a position with
    // three purchases otherwise printed "Tax lot" three times with nothing to
    // tell the rows apart. `numberedLots` fixes the order and the numbering
    // together, so #1 is always the topmost row (see its contract above).
    const renderLotRows = (r) =>
      numberedLots(r.lots).map(({ lot, number }, li) => (
        <tr
          key={`lot-${li}`}
          className={`border-b ${darkMode ? 'border-white/5 bg-[#101010] text-gray-400' : 'border-gray-100 bg-gray-50 text-gray-600'}`}
        >
          <td className="p-2 text-right">{fmtNum(lot.par_value, 0)}</td>
          <td className="p-2 text-center whitespace-nowrap">{lot.trade_date || '-'}</td>
          <td className="p-2 text-center whitespace-nowrap">{lot.settle_date || '-'}</td>
          <td className="p-2" />
          <td className="p-2" />
          <td className="p-2" />
          <td className="p-2" />
          <td className="p-2 pl-6 italic">{`Tax lot #${number}`}</td>
          <td className="p-2 text-right">{fmtNum(lot.unit_cost, 2)}</td>
          <td className="p-2" />
          <td className="p-2" />
          <td className="p-2 text-right">{fmtNum(lot.total_cost, moneyDigits ?? 0)}</td>
          <td className="p-2" />
          <td className="p-2 text-right">{fmtNum(lot.market_value, moneyDigits ?? 0)}</td>
          <td className="p-2 text-right">{fmtNum(pickAccrued(lot), moneyDigits ?? 0)}</td>
          <td className="p-2" />
          <td className="p-2" />
          {yocCols.map((b) => (
            <td key={b} className="p-2 text-right">{fmtPercent(yocColValue(lot, b), 2)}</td>
          ))}
          {mktCols.map((b) => (
            <td key={`mkt-${b}`} className="p-2" />
          ))}
        </tr>
      ));

    const renderBodyRows = (rows) =>
      rows.map((r, i) => {
        const hasLots = rowHasLots(r);
        const isExpanded = hasLots && !!expandedRows?.[lotRowKey(r)];
        const identifier = r.cusip || r.symbol || '-';
        return (
          <React.Fragment key={`${r.cusip || r.symbol}-${i}`}>
            <tr
              {...(hasLots ? { 'data-toggle-key': lotRowKey(r) } : {})}
              className={`border-b ${hasLots ? 'cursor-pointer ' : ''}${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
            >
              {/* Par = ORIGINAL face (PM ruling 2026-08-05: "display the original face
                  and show a (current face*) below that"). A factored pool bought after
                  original issue has two par numbers, and the money columns are on the
                  CURRENT one - which is what ties APX - so printing only original face
                  made the row look internally inconsistent. current_face is null on
                  every non-factored and cash row, so no other row grows a second line. */}
              {/* report-lot-gutter reserves the 14px the absolutely-positioned
                  chevron below occupies but contributes no width to (index.css,
                  .report-scroll scope). A Tailwind padding utility CANNOT do
                  this: the viewer's compaction rule `.report-scroll
                  .report-member td { padding: 3px 4px }` outranks `.pl-5` on
                  specificity, so the class would silently no-op and the arrow
                  would keep sitting on the leading digit. Only the rows that
                  carry a chevron take the gutter - a report with no multi-lot
                  holding keeps its Par column at its natural width. */}
              <td className={`p-2 text-right relative${hasLots ? ' report-lot-gutter' : ''}`}>
                {/* Screen-only expand affordance at the row's LEFT edge (owner
                    spec 2026-08-11: the chevron sits left of the leftmost
                    column). Par is right-aligned so the cell's left gutter is
                    free; print strips the whole span.

                    Centered on the FULL cell height (top-1/2 + a half-height
                    pull-back), NOT pinned to the first text line: a factored
                    pool prints original face with "(current face*)" beneath it,
                    and a line-anchored chevron sat right beside the par figure
                    and read as an overlap. */}
                {hasLots && (
                  // data-no-insight: the arrow is a CONTROL inside a data cell,
                  // so the viewer's delegated field-insight click must skip it
                  // (clicking it toggles the lots only, never opens the chat).
                  <span className="absolute left-0 top-1/2 -translate-y-1/2" data-print-skip data-no-insight>
                    {isExpanded
                      ? <ChevronDown className="w-3.5 h-3.5" />
                      : <ChevronRight className="w-3.5 h-3.5" />}
                  </span>
                )}
                {fmtNum(r.par_value, 0)}
                {r.current_face != null && (
                  <span className={`block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    ({fmtNum(r.current_face, 0)}*)
                  </span>
                )}
              </td>
              <td className="p-2 text-center whitespace-nowrap">{r.trade_date || '-'}</td>
              <td className="p-2 text-center whitespace-nowrap">{r.settle_date || '-'}</td>
              <td className="p-2 text-center uppercase">{r.bond_stat_code || '-'}</td>
              <td className="p-2 text-center whitespace-nowrap">
                {r.call_date ? (
                  <>
                    {r.call_date}
                    {r.call_price != null && (
                      <span className={`block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>@ {fmtNum(r.call_price, 3)}</span>
                    )}
                  </>
                ) : '-'}
              </td>
              <td className="p-2 text-center whitespace-nowrap">
                {r.put_date ? (
                  <>
                    {r.put_date}
                    {r.put_price != null && (
                      <span className={`block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>@ {fmtNum(r.put_price, 3)}</span>
                    )}
                  </>
                ) : '-'}
              </td>
              <td className="p-2 whitespace-nowrap uppercase" data-export-text>{identifier}</td>
              {/* data-value / data-export-extra-row are EXPORT-ONLY (reportExport.js):
                  the spreadsheet gets APX's two-row shape - issuer on row 1, coupon +
                  maturity on its own row 2 - while the rendered cell is unchanged. */}
              {/* w-[210px] is a PREFERRED width, not a min: auto table layout
                  lets the column yield below it when the other 20 columns need
                  the room (a min-width instead pushes the table past the 960px
                  printed page and clips the right edge). 210px holds a median
                  94-char Pershing fullname to 2-3 lines; leading-[1.3] tightens
                  the wrapped lines against the inherited 16px line-height. */}
              <td
                className="p-2 align-top w-[210px] leading-[1.3]"
                title={r.description}
                data-value={stripCouponDue(r.description)}
                data-export-extra-row={
                  (r.coupon_rate > 0 || r.maturity_date)
                    ? `${(r.coupon_rate || 0).toFixed(3)}% Due ${mdyDash(r.maturity_date)}`
                    : undefined
                }
              >
                <div className="break-words">{stripCouponDue(r.description) || '-'}</div>
                {(r.coupon_rate > 0 || r.maturity_date) && (
                  <DueLine coupon={r.coupon_rate} due={isoToMDY(r.maturity_date)} darkMode={darkMode} />
                )}
              </td>
              {/* APX column order: the per-100 price block sits right of Description
                  (cols J/K/L) and is NOT money, so it keeps 2dp regardless of the
                  money-precision toggle; the money block below honours moneyDigits. */}
              <td className="p-2 text-right">{fmtNum(r.unit_cost, 2)}</td>
              <td className="p-2 text-right">{fmtNum(r.adjusted_unit_cost, 2)}</td>
              <td className="p-2 text-right">{fmtNum(r.market_price, 2)}</td>
              {/* pickSettle, not the bare field: the cash section's plain 'ca'
                  row - the one GMON could not decompose - carries its own
                  `_settle` override, because that single line IS the whole
                  trade-date net and only the straddle adjustment settles it.
                  Every other row has no mirror and falls back to itself. */}
              <td className="p-2 text-right">{fmtNum(pickSettle(r, 'total_cost'), moneyDigits ?? 0)}</td>
              <td className="p-2 text-right">{fmtNum(pickSettle(r, 'total_adjusted_cost'), moneyDigits ?? 0)}</td>
              <td className="p-2 text-right">{fmtNum(pickSettle(r, 'market_value'), moneyDigits ?? 0)}</td>
              <td className="p-2 text-right">{fmtNum(pickAccrued(r), moneyDigits ?? 0)}</td>
              <td className="p-2 text-center">{r.moody_rating || '-'}</td>
              <td className="p-2 text-center">{r.sp_rating || '-'}</td>
              {yocCols.map((b) => (
                <td key={b} className="p-2 text-right">{fmtPercent(yocColValue(r, b), 2)}</td>
              ))}
              {mktCols.map((b) => (
                <td key={`mkt-${b}`} className="p-2 text-right">{fmtPercent(mktColValue(r, b), 2)}</td>
              ))}
            </tr>
            {isExpanded && renderLotRows(r)}
          </React.Fragment>
        );
      });

    // A totals row (section subtotal or grand total): a colSpan label over the 11
    // leading columns, the four money columns, two blank rating columns, then the
    // yield-on-cost column(s) and the optional market-yield column(s) - accounting
    // for every body column so it stays pinned under the headers. The subtotal /
    // grand-total objects carry the market yields under the same keys as a row.
    const renderTotalsRow = (label, obj, { pad = 'p-2', rowClass }) => (
      <tr className={rowClass}>
        <td className={`${pad} text-left`} colSpan={LEAD_COLS}>{label}</td>
        <td className={`${pad} text-right`}>{fmtNum(pickSettle(obj, 'total_cost'), moneyDigits ?? 0)}</td>
        <td className={`${pad} text-right`}>{fmtNum(pickSettle(obj, 'total_adjusted_cost'), moneyDigits ?? 0)}</td>
        <td className={`${pad} text-right`}>{fmtNum(pickSettle(obj, 'market_value'), moneyDigits ?? 0)}</td>
        <td className={`${pad} text-right`}>{fmtNum(pickAccrued(obj), moneyDigits ?? 0)}</td>
        <td className={`${pad} text-center`} />
        <td className={`${pad} text-center`} />
        {yocCols.map((b) => (
          <td key={b} className={`${pad} text-right`}>{fmtPercent(yocColValue(obj, b), 2)}</td>
        ))}
        {mktCols.map((b) => (
          <td key={`mkt-${b}`} className={`${pad} text-right`}>{fmtPercent(mktColValue(obj, b), 2)}</td>
        ))}
      </tr>
    );

    // Adapter: if the backend returned the old "holdings_by_maturity" shape,
    // synthesise group entries from it so the renderer keeps working.
    const rawGroups = groups.length > 0
      ? groups
      : Object.entries(holdings_by_maturity).map(([name, rows]) => ({
          name,
          rows,
          subtotals: {
            total_cost: rows.reduce((s, r) => s + (r.total_cost || 0), 0),
            total_adjusted_cost: rows.reduce((s, r) => s + (r.total_adjusted_cost || r.total_cost || 0), 0),
            market_value: rows.reduce((s, r) => s + (r.market_value || 0), 0),
            accrued_interest: rows.reduce((s, r) => s + (r.accrued_interest || 0), 0),
            yield_on_cost: 0,
          },
        }));
    // Hide the rows the active holdings basis does not hold, ONCE, before
    // anything downstream: the section bands, the per-row `row-keep` tbodies,
    // the expand-all key list and each row's lot rows all read this set. The
    // subtotals below come from the backend's own settlement mirrors, never from
    // re-summing the visible rows.
    const sectionGroups = rawGroups.map((g) => ({ ...g, rows: basisRows(g.rows || []) }));
    const totalsRow = groups.length > 0 ? grand_total : totals;
    // How many holdings fell back to their settlement figure on the trade-date
    // basis. Counted by the backend (it is the only side that knows why a row
    // could not be stepped forward a day); surfaced so a partly-settlement total
    // never reads as a clean trade-date one.
    const accruedTradeFallbacks = totalsRow?.accrued_trade_rows_unavailable || 0;

    // Every multi-lot holding on the page, for the expand/collapse-all control.
    // The viewer's delegated handler reads the keys off the button; the same
    // keys drive each row's own chevron.
    const multiLotKeys = sectionGroups
      .flatMap((g) => g.rows || [])
      .filter(rowHasLots)
      .map(lotRowKey);
    const allLotsExpanded =
      multiLotKeys.length > 0 && multiLotKeys.every((k) => expandedRows?.[k]);

    return (
      <div className="space-y-6">
        {/* `report-sticky-toolbar`: the viewer freezes this control bar with the
            column headers (index.css, .report-scroll scope) so Expand All and
            the yield / accrued / market-yield pickers stay clickable without
            scrolling back to the top. It must stay a SIBLING of the table
            wrapper below - the measured bar height is written on their shared
            parent and inherited by the <thead> that pins under it. */}
        {sectionGroups.length > 0 && (
          <div className={`report-sticky-toolbar flex items-center justify-end gap-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-export-skip>
            {multiLotKeys.length > 0 && (
              <button
                type="button"
                data-print-skip
                data-toggle-all={allLotsExpanded ? 'collapse' : 'expand'}
                data-toggle-all-keys={multiLotKeys.join(',')}
                className={`text-xs underline mr-auto ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {allLotsExpanded ? 'Minimize All' : 'Expand All'}
              </button>
            )}
            {holdingsBasisSelect}
            {yocBasisSelect}
            {accruedBasisSelect}
            {marketYieldCheckbox}
          </div>
        )}
        {/* Securities only: the cash section's in-transit line also carries the
            pending flag, but it is money, not an excluded purchase. */}
        {basisDisclosure(rawGroups.filter((g) => !/CASH/i.test(g.name || '')).flatMap((g) => g.rows || []))}
        {accruedBasis === 'trade' && accruedTradeFallbacks > 0 && (
          <div
            className={`text-[11px] ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}
            data-export-skip
          >
            {`Accrued shown on a trade-date basis. ${accruedTradeFallbacks} `}
            {accruedTradeFallbacks === 1 ? 'holding has' : 'holdings have'}
            {' no derivable trade-date value and show the settlement figure instead.'}
          </div>
        )}
        {sectionGroups.length === 0 && (holdings.length === 0) && (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No fixed income holdings found.
          </div>
        )}

        {sectionGroups.length > 0 && (
          // Name the exported sheet: without this the walker finds no heading in
          // the subtree and falls back to "Table 1".
          <div className="overflow-x-auto" data-export-sheet="Fixed Income Holdings">
            {/* fi-holdings-table scopes the tighter cell padding (index.css +
                pagedReportCss.js) to this - the widest (up to 21-column) -
                report table, so the Description column keeps enough width. */}
            <table className={`fi-holdings-table w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              {renderHead()}
              {/* A section the active basis holds nothing of (every row a sale
                  awaiting delivery, or a purchase not yet settled) prints no
                  band and no zero subtotal. */}
              {sectionGroups.filter((group) => group.rows.length > 0).map((group, gi) => (
                <React.Fragment key={`${group.name}-${gi}`}>
                  {/* The section band + its FIRST data row form their own
                      `section-keep` tbody: paged.js honors break-inside:avoid
                      on a rendered tbody (rule in pagedReportCss.js), so a
                      section title can never be orphaned as the last line of
                      a printed page - it moves to the next page WITH its first
                      row. (break-after:avoid does NOT work here: paged.js
                      resolves break-before/after selectors against the
                      pre-render source tree, and even when set it will not
                      relocate a lone <tr> in 0.4.3.) */}
                  <tbody className="section-keep">
                    {/* Section band spans the full table so every group aligns to
                        the shared header. */}
                    <tr>
                      <td
                        colSpan={totalCols}
                        className={`text-xs font-bold uppercase tracking-wide p-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}
                      >
                        {group.name}
                      </td>
                    </tr>
                    {renderBodyRows((group.rows || []).slice(0, 1))}
                  </tbody>
                  {/* Every remaining bond row gets its OWN break-inside:avoid tbody
                      (.row-keep) so paged.js keeps the whole row together via its
                      tbody-container relocation path. The td/tr break-inside rules
                      alone are honored by Chrome/WebKit but can be missed by
                      Firefox/Gecko's paged.js layout, which then slices a row mid
                      cell; keying the break off the row's parent tbody fixes that
                      across engines. (Multiple <tbody> in one table is valid HTML
                      and shares the table's column widths, so layout is unchanged.) */}
                  {(group.rows || []).slice(1).map((r, ri) => (
                    <tbody className="row-keep" key={`rk-${group.name}-${ri}`}>
                      {renderBodyRows([r])}
                    </tbody>
                  ))}
                  <tbody>
                    {renderTotalsRow(`${group.name} Total`, group.subtotals, {
                      rowClass: `font-semibold border-t-2 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`,
                    })}
                  </tbody>
                </React.Fragment>
              ))}
              <tbody>
                {renderTotalsRow('GRAND TOTAL', totalsRow, {
                  pad: 'p-3',
                  rowClass: `font-bold border-t-4 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-700 bg-gray-100'}`,
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderFixedIncomeAnalysis = () => {
    const { bonds: allBonds = [], asOfDate } = reportData;
    // Holdings basis: the rows and their count are the active basis's, like the
    // Total Value beside them.
    const bonds = basisRows(allBonds);

    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Bonds</p>
              <p data-insight-field="Total Bonds" className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {bonds.length}
              </p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Value</p>
              <p data-insight-field="Total Value" className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {formatCurrency(pickSettle(reportData, 'totalValue') || 0)}
              </p>
            </div>
          </div>
          {asOfDate && (
            <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              As of: {asOfDate}
            </p>
          )}
        </div>

        {holdingsBasisSelect && (
          <div className="report-sticky-toolbar flex items-center justify-end" data-export-skip data-print-skip>
            {holdingsBasisSelect}
          </div>
        )}

        {bonds.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Fund Mnemonic
                  </th>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    CUSIP
                  </th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {bonds.map((bond, index) => (
                  <tr
                    key={index}
                    className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <td className="p-3" data-export-text>{bond.fund_mnemonic || '-'}</td>
                    <td className="p-3" data-export-text>{bond.cusip || '-'}</td>
                    <td className="p-3 text-right">{formatCurrency(pickSettle(bond, 'value') || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No bonds found for this report.
          </div>
        )}
      </div>
    );
  };

  const renderTransactionHistory = () => {
    const {
      transactions = [], transactionCount, startDate, endDate,
      sections = [], accounts = [],
    } = reportData;

    const thL = `p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const thR = `p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const gl = (v) => (v >= 0 ? 'text-green-600' : 'text-red-600');

    // Security lookup over the generated listing. Case-insensitive substring
    // match on symbol, CUSIP, or security name, so a ticker, a full 9-char
    // CUSIP, or an 8-char stem all narrow as you type. Empty = full report.
    const filterQuery = String(securityFilter || '').trim().toUpperCase();
    const txnMatches = (t) =>
      !filterQuery ||
      [t.symbol, t.cusip, t.security].some(
        (v) => String(v || '').toUpperCase().includes(filterQuery),
      );
    const securityFilterBox = (
      <div className="flex items-center gap-2" data-export-skip data-print-skip>
        <input
          type="text"
          data-security-filter
          value={securityFilter}
          onChange={(e) => setSecurityFilter(e.target.value)}
          placeholder="Filter by CUSIP / ticker / name"
          className={`px-2 py-1 text-sm rounded border w-64 ${darkMode
            ? 'bg-[#141414] border-white/10 text-gray-200 placeholder-gray-500'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
        />
        {filterQuery && (
          <button
            type="button"
            onClick={() => setSecurityFilter('')}
            className={`text-xs ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
          >
            Clear
          </button>
        )}
      </div>
    );

    // transaction_summary: grouped-by-type sections, each with a subtotal row.
    if (reportType === 'transaction_summary' && sections.length > 0) {
      const visibleSections = sections
        .map((s) => ({ ...s, transactions: (s.transactions || []).filter(txnMatches) }))
        .filter((s) => s.transactions.length > 0);
      const shownCount = visibleSections.reduce((n, s) => n + s.transactions.length, 0);
      const totalCount = transactionCount
        || sections.reduce((n, s) => n + (s.transactions || []).length, 0);
      return (
        <div className="space-y-4">
          {securityFilterBox}
          {startDate && endDate && (
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Period: {startDate} to {endDate} &middot; Total Transactions: {filterQuery ? `${shownCount} of ${totalCount}` : totalCount}
            </p>
          )}
          {filterQuery && visibleSections.length === 0 && (
            <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No transactions match "{securityFilter.trim()}".
            </div>
          )}
          {visibleSections.map((section, si) => (
            <div key={si} className="overflow-x-auto">
              <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {section.type} ({section.transactions.length})
              </h4>
              <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className={thL}>Date</th>
                    <th className={thL}>Settle Date</th>
                    <th className={thL}>Symbol / CUSIP</th>
                    <th className={thL}>Description</th>
                    <th className={thR}>Quantity</th>
                    <th className={thR}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(section.transactions || []).map((t, ti) => (
                    <tr key={ti} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-3">{t.date || '-'}</td>
                      <td className="p-3">{t.settle_date || '-'}</td>
                      <td className="p-3" data-export-text>{t.cusip || t.symbol || '-'}</td>
                      <td className="p-3">{t.description || '-'}</td>
                      <td className="p-3 text-right">{t.quantity ? fmtShares(t.quantity) : '-'}</td>
                      <td className="p-3 text-right">{formatCurrency(t.amount || 0)}</td>
                    </tr>
                  ))}
                  <tr className={`font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    <td className="p-3" colSpan="5">Subtotal</td>
                    <td className="p-3 text-right">{formatCurrency(filterQuery
                      ? section.transactions.reduce((a, t) => a + (t.amount || 0), 0)
                      : (section.subtotal_amount || 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      );
    }

    // master_transaction_summary: per-account sections with net cash flow.
    if (reportType === 'master_transaction_summary' && accounts.length > 0) {
      return (
        <div className="space-y-4">
          {accounts.map((acct, ai) => (
            <div key={ai} className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
              <h4 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Account {acct.account_number} &middot; {acct.transaction_count} transactions
              </h4>
              {acct.summary && acct.summary.net_cash_flow != null && (
                <p className={`text-sm ${gl(acct.summary.net_cash_flow)}`}>
                  Net Cash Flow: {formatCurrency(acct.summary.net_cash_flow)}
                </p>
              )}
            </div>
          ))}
        </div>
      );
    }

    const visibleTransactions = transactions.filter(txnMatches);
    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Transaction History
          </h3>
          {startDate && endDate && (
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Period: {startDate} to {endDate}
            </p>
          )}
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Total Transactions: {filterQuery
              ? `${visibleTransactions.length} of ${transactionCount || transactions.length}`
              : (transactionCount || transactions.length)}
          </p>
          <div className="mt-2">{securityFilterBox}</div>
        </div>

        {visibleTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Date
                  </th>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Description
                  </th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Amount
                  </th>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((transaction, index) => (
                  <tr
                    key={index}
                    className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <td className="p-3">{transaction.date || '-'}</td>
                    <td className="p-3">{transaction.description || '-'}</td>
                    <td className="p-3 text-right">{formatCurrency(transaction.amount || 0)}</td>
                    <td className="p-3">{transaction.type || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {filterQuery
              ? `No transactions match "${securityFilter.trim()}".`
              : 'No transactions found for this period.'}
          </div>
        )}
      </div>
    );
  };

  // Portfolio Register: the one-row-per-portfolio metadata roster
  // ({rows: [{portfolio_code, name, date_opened, close_date, recon_date,
  // index1, index2}]}). Under @@ the backend collapses it to one group row.
  const renderPortfolioRegister = () => {
    const rows = reportData.rows || [];
    const th = `p-2 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    if (rows.length === 0) {
      return (
        <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          No portfolios in the selection.
        </div>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
          <thead>
            <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <th className={`${th} text-left`}>Portfolio Code</th>
              <th className={`${th} text-left`}>Portfolio</th>
              <th className={`${th} text-left`}>Date Opened</th>
              <th className={`${th} text-left`}>Close Date</th>
              <th className={`${th} text-left`}>Recon Date</th>
              <th className={`${th} text-left`}>Index1</th>
              <th className={`${th} text-left`}>Index 2</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                <td className="p-2" data-export-text>{r.portfolio_code || '-'}</td>
                <td className="p-2" data-export-text>{r.name || '-'}</td>
                <td className="p-2">{r.date_opened || '-'}</td>
                <td className="p-2">{r.close_date || '-'}</td>
                <td className="p-2">{r.recon_date || '-'}</td>
                <td className="p-2">{r.index1 || '-'}</td>
                <td className="p-2">{r.index2 || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderGenericReport = () => {
    return (
      <div className="space-y-4">
        <pre className={`p-4 rounded-lg overflow-auto text-sm ${darkMode ? 'bg-[#141414] text-gray-200' : 'bg-gray-50 text-gray-900'}`}>
          {JSON.stringify(reportData, null, 2)}
        </pre>
      </div>
    );
  };

  const renderPerformanceSummaryCompound = () => {
    // Match PDF structure exactly
    const {
      portfolio_summary = {},
      activity_summary = {},
      performance_history = {},
      startDate,
      endDate
    } = reportData;
    // Benchmark rows live under performance_history.indexes (each keyed `label`);
    // there is no top-level `benchmarks` key in the backend contract.
    const benchmarks = performance_history.indexes || [];
    // A missing period (index series not yet ingested for the report month,
    // or no PERF_HISTORY row carrying that period) comes through as `null`,
    // not 0 - `|| 0` would print "0.00%", which reads as "flat" and masks
    // the data gap. Absent periods render as a dash; a real 0 still prints.
    const fmtPct = (v) => (v === null || v === undefined ? '-' : `${Number(v).toFixed(2)}%`);

    // Inception ("... To Date") column header. When the backend annualizes the
    // inception cell (N1: inception_annualized=true, per-annum basis over the
    // account's inception window), the column is labelled "Annualized ..." per
    // the GT; inception_cumulative carries the un-annualized value per block.
    const itdBaseLabel = performance_history.itd_label
      || (performance_history.inception_date
        ? `${performance_history.inception_date} To Date`
        : 'Inception To Date');
    const itdHeaderLabel = performance_history.inception_annualized
      ? `Annualized ${itdBaseLabel}`
      : itdBaseLabel;
    // Unheld asset class (§1d A3): the backend fallback routes through
    // _annualize_block and flags `unheld`. GT shape is 0.00% for the
    // sub-annual cells and NA% ONLY in the annualized-inception cell — never
    // NA across all four (the pre-A3 all-NA treatment misread the GT).
    const classIsEmpty = (block) =>
      !!performance_history.inception_annualized
      && !!block
      && !('inception_cumulative' in block);
    const fmtClassPct = (block, key) => {
      if (block && block.unheld) {
        return key === 'inception_to_date' ? 'NA%' : fmtPct(block[key] == null ? 0 : block[key]);
      }
      // Legacy bare zeros placeholder (pre-A3 backend payloads): keep NA.
      if (classIsEmpty(block)) return 'NA';
      return fmtPct(block?.[key]);
    };

    // Convert portfolio_summary object to array for table rendering.
    // Backend emits `null` for any ABSENT (zero) asset-class bucket and carries
    // the grand total under `total_portfolio` (rendered separately as the TOTAL
    // row below). A material NEGATIVE bucket (margin/debit cash) is a real row
    // and comes through non-null. Skip nulls and the total so we neither crash
    // nor double-count.
    const portfolioSummaryRows = Object.entries(portfolio_summary)
      .filter(([key, data]) => data && key !== 'total_portfolio')
      .map(([key, data]) => ({
        asset_class: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        market_value: data.market_value || 0,
        pct: data.pct || 0
      }));

    // TOTAL row = the backend's canonical netted total, NEVER a recomputed
    // visible-row sum: any bucket inside the total but not rendered as a row
    // made the visible sum print FI-only [amount] as "100%" (K3/N12, §1d A2).
    // Visible-row sum survives only as the legacy-payload fallback.
    const backendTotal = portfolio_summary.total_portfolio;
    const totalMarketValue = backendTotal && backendTotal.market_value != null
      ? backendTotal.market_value
      : portfolioSummaryRows.reduce((sum, row) => sum + row.market_value, 0);
    const totalPct = backendTotal && backendTotal.pct != null ? backendTotal.pct : 100;
    
    return (
      <div className="space-y-6">
        {/* PORTFOLIO SUMMARY - Table format matching PDF */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            PORTFOLIO SUMMARY
          </h3>
          <table className={`w-full border-collapse text-base ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Asset Class</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Market Value</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Pct.</th>
              </tr>
            </thead>
            <tbody>
              {portfolioSummaryRows.map((row, index) => (
                <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-2">{row.asset_class}</td>
                  <td className="p-2 text-right">{formatCurrency(row.market_value)}</td>
                  <td className="p-2 text-right">{row.pct.toFixed(0)}%</td>
                </tr>
              ))}
              <tr className={`font-bold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>
                <td className="p-2">TOTAL PORTFOLIO</td>
                <td className="p-2 text-right">{formatCurrency(totalMarketValue)}</td>
                <td className="p-2 text-right">{totalPct.toFixed(0)}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ACTIVITY SUMMARY - Matching PDF layout */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            ACTIVITY SUMMARY
          </h3>
          <table className={`w-full text-base ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <tbody>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2">Portfolio Value on {startDate}</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.portfolio_value_start || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2">Net Additions & Withdrawals</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.net_additions_withdrawals || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2">Net Intra Account Transfers</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.net_intra_account_transfers || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2">Adjusted Portfolio Value</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.adjusted_portfolio_value || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} font-semibold`}>
                <td className="p-2">Portfolio Value on {endDate}</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.portfolio_value_end || 0)}</td>
              </tr>
              <tr className={`border-b-2 ${darkMode ? 'border-white/10' : 'border-gray-400'} font-bold`}>
                <td className="p-2">Change from {startDate} to {endDate}</td>
                <td className={`p-2 text-right ${(activity_summary.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(activity_summary.change || 0)}
                </td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2 pl-6">Income Received</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.income_received || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2 pl-6">Change in Market Value</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.change_in_market_value || 0)}</td>
              </tr>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                <td className="p-2 pl-6">Change in Accrued Interest</td>
                <td className="p-2 text-right">{formatCurrency(activity_summary.change_in_accrued_interest || 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {renderMoneyWeightedSection(reportData.money_weighted, returnBasisSelect)}

        {/* PERFORMANCE HISTORY - Table matching PDF */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            PERFORMANCE HISTORY
          </h3>
          {/* `report-sticky-toolbar`: frozen with the column headers, sibling
              of the table so the measured height lands on this card (their
              shared parent). The card is tinted, so the bar freezes in the
              card's colour rather than the shared rule's white-sheet default. */}
          {feeBasisSelect && (
            <div
              className="report-sticky-toolbar -mt-2 mb-3 flex justify-end"
              style={stickyToolbarOnCard}
              data-export-skip
              data-print-skip
            >
              {feeBasisSelect}
            </div>
          )}
          <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Asset Class</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Month To Date</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Quarter To Date</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>Year To Date</th>
                <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#1a1a1a]' : 'bg-gray-100'}`}>{itdHeaderLabel}</th>
              </tr>
            </thead>
            <tbody>
              {performance_history.fixed_income && (
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-2">Fixed Income</td>
                  <td className="p-2 text-right">{fmtPct(performance_history.fixed_income.month_to_date)}</td>
                  <td className="p-2 text-right">{fmtPct(performance_history.fixed_income.quarter_to_date)}</td>
                  <td className="p-2 text-right">{fmtPct(performance_history.fixed_income.year_to_date)}</td>
                  <td className="p-2 text-right">{fmtPct(performance_history.fixed_income.inception_to_date)}</td>
                </tr>
              )}
              {performance_history.equity && (
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-2">Equity</td>
                  <td className="p-2 text-right">{fmtClassPct(performance_history.equity, 'month_to_date')}</td>
                  <td className="p-2 text-right">{fmtClassPct(performance_history.equity, 'quarter_to_date')}</td>
                  <td className="p-2 text-right">{fmtClassPct(performance_history.equity, 'year_to_date')}</td>
                  <td className="p-2 text-right">{fmtClassPct(performance_history.equity, 'inception_to_date')}</td>
                </tr>
              )}
              {/* The ACCOUNT row is the only one the basis picker moves: the
                  fee has no asset-class attribution, so class rows and
                  benchmarks print the same number on both bases (plan Q4,
                  implemented per the plan's default, awaiting PM confirmation). */}
              {performance_history.account && (
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} font-semibold`}>
                  <td className="p-2">Account</td>
                  <td className="p-2 text-right">{fmtPct(pickBasis(performance_history.account, 'month_to_date'))}</td>
                  <td className="p-2 text-right">{fmtPct(pickBasis(performance_history.account, 'quarter_to_date'))}</td>
                  <td className="p-2 text-right">{fmtPct(pickBasis(performance_history.account, 'year_to_date'))}</td>
                  <td className="p-2 text-right">{fmtPct(pickBasis(performance_history.account, 'inception_to_date'))}</td>
                </tr>
              )}
              {/* Benchmark indices */}
              {visibleIndexes(benchmarks).length > 0 && (
                <>
                  <tr className={`border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                    <td className="p-2 font-semibold" colSpan="5">Index</td>
                  </tr>
                  {visibleIndexes(benchmarks).map((benchmark, index) => (
                    <tr key={index} className={`group border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-2 text-sm">
                        <span className="inline-flex items-center">{benchmark.name || benchmark.label}{indexHideBtn(benchmark)}</span>
                      </td>
                      <td className="p-2 text-right">{fmtPct(benchmark.month_to_date)}</td>
                      <td className="p-2 text-right">{fmtPct(benchmark.quarter_to_date)}</td>
                      <td className="p-2 text-right">{fmtPct(benchmark.year_to_date)}</td>
                      <td className="p-2 text-right">{fmtPct(benchmark.inception_to_date)}</td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderBondTransactions = () => {
    // Match PDF columns exactly: Trade Date, Settle Date, Buy/Sell, Quantity, Bond Status, Put/Call Dates, Security Symbol, Security, Price, Market Value, YTM
    const { transactions = [], transactionCount, totals = {}, sections = [] } = reportData;

    const formatQuantity = (value) => {
      if (value === null || value === undefined) return '-';
      return new Intl.NumberFormat('en-US').format(value);
    };

    // PM Q28: Purchase & Sale rows flag how a position left the book
    // (redeemed/called/matured/sold). Only render the column when present.
    const hasDisposition = transactions.some((t) => t.disposition);

    // L5: Purchase & Sale ships PURCHASES/SALES sections whose subtotals fold in
    // the accrued-interest (pa/sa) legs riding each trade row. Render them as
    // labeled blocks with a per-section accrued-inclusive subtotal; the "Amount"
    // column is the trade principal and prices print at 2dp (APX parity).
    // bond_transactions / trade_blotter carry no sections and fall through to the
    // flat table below unchanged.
    const psSections = (Array.isArray(sections) ? sections : []).filter(
      (s) => Array.isArray(s.rows) && s.rows.length > 0,
    );
    if (psSections.length > 0) {
      const thL = `p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
      const thC = `p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
      const thR = `p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
      return (
        <div className="space-y-6">
          {psSections.map((section, si) => (
            <div key={si} className="overflow-x-auto">
              <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {section.section} ({section.count != null ? section.count : (section.rows || []).length})
              </h4>
              <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className={thL}>Trade Date</th>
                    <th className={thL}>Settle Date</th>
                    <th className={thC}>Buy/Sell</th>
                    <th className={thR}>Quantity</th>
                    <th className={thL}>Symbol / CUSIP</th>
                    <th className={thL}>Security</th>
                    <th className={thR}>Price</th>
                    <th className={thR}>Amount</th>
                    <th className={thR}>Accrued</th>
                    <th className={thR}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(section.rows || []).map((t, index) => (
                    <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <td className="p-2">{t.trade_date || '-'}</td>
                      <td className="p-2">{t.settle_date || '-'}</td>
                      <td className={`p-2 text-center font-medium ${t.buy_sell === 'Buy' ? 'text-green-600' : 'text-red-600'}`}>
                        {t.buy_sell || '-'}
                      </td>
                      <td className="p-2 text-right">{formatQuantity(t.quantity)}</td>
                      <td className="p-2" data-export-text>{t.cusip || t.symbol || '-'}</td>
                      <td className="p-2 break-words align-top" title={t.security_description || t.security}>
                        {t.security_description || t.security || '-'}
                      </td>
                      <td className="p-2 text-right">{t.price != null ? Number(t.price).toFixed(2) : '-'}</td>
                      <td className="p-2 text-right">{formatCurrency(t.principal_amount || 0)}</td>
                      <td className="p-2 text-right">{t.accrued_interest ? formatCurrency(t.accrued_interest) : '-'}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(t.total_amount != null ? t.total_amount : (t.principal_amount || 0))}</td>
                    </tr>
                  ))}
                  <tr className={`font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                    <td className="p-2" colSpan="7">{section.section} Subtotal</td>
                    <td className="p-2 text-right">{formatCurrency(section.subtotal_principal || 0)}</td>
                    <td className="p-2 text-right">{formatCurrency(section.subtotal_accrued || 0)}</td>
                    <td className="p-2 text-right">{formatCurrency(section.subtotal_amount || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <p>Total Transactions: {transactionCount != null ? transactionCount : psSections.reduce((n, s) => n + (s.rows || []).length, 0)}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Trade Date</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Settle Date</th>
                  <th className={`p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Buy/Sell</th>
                  {hasDisposition && (
                    <th className={`p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Disposition</th>
                  )}
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Quantity</th>
                  <th className={`p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Bond Status</th>
                  <th className={`p-2 text-center font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Put/Call Dates</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Security Symbol</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Security</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Price</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Market Value</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>YTM</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <td className="p-2">{t.trade_date || '-'}</td>
                    <td className="p-2">{t.settle_date || '-'}</td>
                    <td className={`p-2 text-center font-medium ${t.buy_sell === 'Buy' || t.transaction_type === 'BY' ? 'text-green-600' : 'text-red-600'}`}>
                      {t.buy_sell || (t.transaction_type === 'BY' ? 'Buy' : t.transaction_type === 'SL' ? 'Sell' : t.transaction_type) || '-'}
                    </td>
                    {hasDisposition && (
                      <td className={`p-2 text-center ${t.disposition && t.disposition !== 'sold' ? 'font-medium text-amber-600' : ''}`}>
                        {t.disposition || '-'}
                      </td>
                    )}
                    <td className="p-2 text-right">{formatQuantity(t.quantity)}</td>
                    <td className="p-2 text-center">{t.bond_status || '-'}</td>
                    <td className="p-2 text-center">{t.put_call_dates || '-'}</td>
                    <td className="p-2" data-export-text>{t.security_symbol || t.cusip || '-'}</td>
                    <td className="p-2 break-words align-top" title={t.security_description || t.security}>
                      {t.security_description || t.security || '-'}
                    </td>
                    <td className="p-2 text-right">{t.price ? t.price.toFixed(3) : '-'}</td>
                    <td className="p-2 text-right">{formatCurrency(t.market_value || t.principal_amount || 0)}</td>
                    <td className="p-2 text-right">
                      {t.ytm ? (t.ytm === 'mat' ? 'mat' : `${parseFloat(t.ytm).toFixed(2)}`) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <p>Total Transactions: {transactionCount || transactions.length}</p>
              {(() => {
                const bought = totals.total_bought ?? totals.purchase_principal ?? totals.buy_principal;
                const sold = totals.total_sold ?? totals.sale_principal ?? totals.sell_principal;
                const net = totals.net_invested;
                return (
                  <>
                    {bought != null && <p>Total Bought: {formatCurrency(bought)}</p>}
                    {sold != null && <p>Total Sold: {formatCurrency(sold)}</p>}
                    {net != null && <p>Net Invested: {formatCurrency(net)}</p>}
                  </>
                );
              })()}
            </div>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No bond transactions found for this period.
          </div>
        )}
      </div>
    );
  };

  const renderRealizedGainsLosses = () => {
    const {
      realized_transactions_by_close_date,
      realized_transactions = [],
      securities = [],
      gain_loss_totals,
      total_gain_loss,
      total_original_cost,
      total_amort_accretion,
      total_proceeds,
      total_short_term,
      total_long_term,
      basis_convention,
      lot_relief_method,
      apx_export,
      excluded_tbill_redemptions,
      startDate,
      endDate,
    } = reportData;
    const thCls = `p-3 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const gainCls = (v) => (v >= 0 ? 'text-green-600' : 'text-red-600');
    // The Cost Basis column shows the ORIGINAL (unamortized) cost, paired with
    // the Amort. or Accretion column (original + amort = adjusted; GT layout).
    // `original_cost`/`acquisition_cost` carry the original cost under the SAME
    // meaning in both the flat-row and grouped securities[].lots[] payload
    // shapes, so this helper works whichever list is rendered. Only the final
    // `cost_basis` fallback is shape-dependent (original cost on grouped lots,
    // ADJUSTED cost on legacy flat-row payloads) - a last resort for payloads
    // missing both of the unambiguous keys.
    const costBasisOf = (o) =>
      (o.original_cost != null ? o.original_cost
        : o.acquisition_cost != null ? o.acquisition_cost
        : o.cost_basis);

    // L3: the GT Realized schedule is ONE flat per-lot table sorted by
    // disposition (close) date, not a per-security caret tree - "carets don't
    // expand on paper". Prefer the backend's close-date-sorted projection; else
    // sort the flat list; else flatten the grouped lots (older payloads).
    const flattenGroups = () =>
      securities.flatMap((s) => (s.lots || []).map((lot) => ({
        security_id: s.security_id,
        cusip: s.cusip,
        symbol: s.symbol,
        security_name: s.security_name,
        ...lot,
      })));
    const rows = (
      realized_transactions_by_close_date
      || (realized_transactions.length ? realized_transactions : flattenGroups())
    )
      .slice()
      .sort((a, b) => String(a.close_date || '9999-12-31')
        .localeCompare(String(b.close_date || '9999-12-31')));

    // GT grand-total line (original cost / amort-or-accretion / proceeds, plus
    // the NET short/long term split - not the gains-only/losses-only split the
    // TOTAL GAINS/TOTAL LOSSES rows below show). Prefer the backend ledger
    // totals; fall back to summing the visible rows for older payloads that
    // omit them. Amort stays null (em-dash) rather than a fabricated $0.00 when
    // no row carries it at all (L1: omit where not derivable, never fabricate).
    const sumRows = (fn) => rows.reduce((acc, r) => acc + (Number(fn(r)) || 0), 0);
    const totOriginalCost = total_original_cost != null ? total_original_cost : sumRows(costBasisOf);
    const totAmortAccretion = total_amort_accretion != null
      ? total_amort_accretion
      : (rows.some((r) => r.amort_accretion != null) ? sumRows((r) => r.amort_accretion) : null);
    const totProceeds = total_proceeds != null ? total_proceeds : sumRows((r) => r.proceeds);
    const totShortTerm = total_short_term != null
      ? total_short_term : sumRows((r) => (r.term === 'ST' ? r.gain_loss : 0));
    const totLongTerm = total_long_term != null
      ? total_long_term : sumRows((r) => (r.term === 'LT' ? r.gain_loss : 0));

    // (b) split Gain/Loss into Short Term / Long Term columns keyed by the lot's
    // `term`: the value lands in its own term's column and the other shows '-';
    // an OTHER (unknown-term) lot renders a single combined value in the ST
    // column (plain, no tooltip). Never a fabricated 0 in the empty column.
    const stLtCells = (r) => {
      const g = r.gain_loss || 0;
      if (r.term === 'LT') {
        return (
          <>
            <td className="p-3 text-right whitespace-nowrap">-</td>
            <td className={`p-3 text-right font-medium whitespace-nowrap ${gainCls(g)}`}>{formatCurrency(g)}</td>
          </>
        );
      }
      // ST and OTHER both land the value in the Short Term column.
      return (
        <>
          <td className={`p-3 text-right font-medium whitespace-nowrap ${gainCls(g)}`}>{formatCurrency(g)}</td>
          <td className="p-3 text-right whitespace-nowrap">-</td>
        </>
      );
    };

    const gl = gain_loss_totals;
    const netTotal = gl && gl.net != null ? gl.net : (total_gain_loss || 0);

    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Realized Gains/Losses</h3>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Period: {startDate} to {endDate}</p>
            </div>
            <div className="text-right">
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Gain/Loss</p>
              <p className={`text-2xl font-bold whitespace-nowrap ${gainCls(netTotal)}`}>
                {formatCurrency(netTotal)}
              </p>
            </div>
          </div>
          {(basis_convention || lot_relief_method) && (
            <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {basis_convention ? `Basis: ${basis_convention}` : ''}
              {basis_convention && lot_relief_method ? ' • ' : ''}
              {lot_relief_method ? `Lot relief: ${lot_relief_method}` : ''}
            </p>
          )}
          {apx_export && (
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              APX export (amortized to maturity): {formatCurrency(apx_export.total_gain_loss || 0)} over {apx_export.transactionCount} lots
              {apx_export.truncated && apx_export.backup_cutoff
                ? ` — covers only through ${String(apx_export.backup_cutoff).slice(0, 10)}; closes after that date are not in the APX export series, so this total is not comparable to the period total above.`
                : ' — reconciliation reference only.'}
            </p>
          )}
          {/* §7: T-bill closures are off the schedule (their discount is income,
              not a capital gain — matching APX). Say so, with the omitted
              proceeds, so the schedule stays reconcilable to the ledger. */}
          {excluded_tbill_redemptions && (
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Excludes {excluded_tbill_redemptions.lot_count} T-bill redemption
              {excluded_tbill_redemptions.lot_count === 1 ? '' : 's'} totalling{' '}
              {formatCurrency(excluded_tbill_redemptions.proceeds)} in proceeds — {excluded_tbill_redemptions.note}
            </p>
          )}
        </div>

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`${thCls} text-left`}>Security</th>
                  <th className={`${thCls} text-left`}>Open Date</th>
                  <th className={`${thCls} text-left`}>Close Date</th>
                  <th className={`${thCls} text-right`}>Quantity</th>
                  <th className={`${thCls} text-right`}>Cost Basis</th>
                  <th className={`${thCls} text-right`}>Amort. or Accretion</th>
                  <th className={`${thCls} text-right`}>Proceeds</th>
                  <th className={`${thCls} text-right`}>Short Term</th>
                  <th className={`${thCls} text-right`}>Long Term</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const symbol = (r.symbol || r.cusip || '').toUpperCase();
                  return (
                    <tr key={`${r.security_id || r.cusip || i}-${r.close_date || ''}-${i}`} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <td className="p-3" data-export-text>
                        <div className="font-medium">{symbol || '-'}</div>
                        {(r.security_name || r.cusip) && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {r.security_name || ''}{r.cusip ? ` (${r.cusip})` : ''}
                          </div>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap">{isoToMDY(r.open_date) || '-'}</td>
                      <td className="p-3 whitespace-nowrap">{isoToMDY(r.close_date || endDate) || '-'}</td>
                      <td className="p-3 text-right whitespace-nowrap">{fmtShares(r.quantity)}</td>
                      <td className="p-3 text-right whitespace-nowrap">{formatCurrency(costBasisOf(r) || 0)}</td>
                      <td className="p-3 text-right whitespace-nowrap">{r.amort_accretion != null ? formatCurrency(r.amort_accretion) : '—'}</td>
                      <td className="p-3 text-right whitespace-nowrap">{formatCurrency(r.proceeds || 0)}</td>
                      {stLtCells(r)}
                    </tr>
                  );
                })}
              </tbody>
              {gl && (
                <tfoot>
                  <tr className={`border-t-2 font-semibold ${darkMode ? 'border-white/20 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`}>
                    <td className="p-3" colSpan={4}>TOTALS</td>
                    <td className="p-3 text-right whitespace-nowrap">{formatCurrency(totOriginalCost || 0)}</td>
                    <td className="p-3 text-right whitespace-nowrap">{totAmortAccretion != null ? formatCurrency(totAmortAccretion) : '—'}</td>
                    <td className="p-3 text-right whitespace-nowrap">{formatCurrency(totProceeds || 0)}</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls(totShortTerm)}`}>{formatCurrency(totShortTerm || 0)}</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls(totLongTerm)}`}>{formatCurrency(totLongTerm || 0)}</td>
                  </tr>
                  <tr className={`border-t-2 font-semibold ${darkMode ? 'border-white/20 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`}>
                    <td className="p-3" colSpan={7}>TOTAL GAINS</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls((gl.gains && gl.gains.short_term) || 0)}`}>{formatCurrency((gl.gains && gl.gains.short_term) || 0)}</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls((gl.gains && gl.gains.long_term) || 0)}`}>{formatCurrency((gl.gains && gl.gains.long_term) || 0)}</td>
                  </tr>
                  <tr className={`font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
                    <td className="p-3" colSpan={7}>TOTAL LOSSES</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls((gl.losses && gl.losses.short_term) || 0)}`}>{formatCurrency((gl.losses && gl.losses.short_term) || 0)}</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls((gl.losses && gl.losses.long_term) || 0)}`}>{formatCurrency((gl.losses && gl.losses.long_term) || 0)}</td>
                  </tr>
                  <tr className={`border-t-2 font-bold ${darkMode ? 'border-white/20 bg-[#1a1a1a]' : 'border-gray-700 bg-gray-100'}`}>
                    <td className="p-3" colSpan={7}>TOTAL REALIZED GAIN/LOSS</td>
                    <td className={`p-3 text-right whitespace-nowrap ${gainCls(netTotal)}`} colSpan={2}>{formatCurrency(netTotal)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No realized gains/losses found for this period.
          </div>
        )}
      </div>
    );
  };

  const renderUnrealizedGainsLosses = () => {
    const {
      sections: allSections = [], positions = [],
      grand_total = {},
    } = reportData;
    // Holdings basis: unsettled purchases are positions on the trade basis only,
    // sold-but-undelivered positions on the settlement basis only. Filtered once
    // here so the section bands, the `row-keep` tbodies, the expand-all key list
    // and the lot rows all read the same set; the subtotals below come from the
    // backend's settlement mirrors, never from re-summing the visible rows.
    const sections = allSections.map((s) => ({ ...s, rows: basisRows(s.rows || []) }));

    // Sign color, matching the realized-gains renderer: >=0 green else red. Only
    // these two utilities survive the print black-on-white flatten (pinned in
    // pagedReportCss.js), so the sign color prints correctly.
    const gainCls = (v) => (v >= 0 ? 'text-green-600' : 'text-red-600');
    const money = (v) => (v === null || v === undefined ? '-' : formatCurrency(v));

    // The APX Unrealized G&L printout is a per-SECURITY table grouped into
    // asset-class sections (Date | Quantity | Symbol | Security | Adj Unit Cost |
    // Total Adjusted Cost | Price | Market Value | Unrealized G/L | % G/L), with
    // per-section subtotals and one GRAND TOTAL. A multi-lot position prints as
    // ONE row - the earliest lot's trade date, the adjusted cost summed over
    // lots - exactly as APX does, and carries its individual lots for the
    // expandable drill-down below. Body rows, subtotals and the grand total all
    // live in ONE <table> so columns line up and paged.js repeats the single
    // <thead> across split pages (see renderFixedIncomeHoldingsByMaturity for
    // the same machinery).
    const totalCols = 10;
    const LEAD_COLS = 5; // Date | Quantity | Symbol | Security | Adj Unit Cost

    // Individual tax lots, same contract as FI Holdings and the Security Cross
    // Reference: a position built from more than one purchase carries its lots
    // in the payload and expands into muted sub-rows under the lumped row.
    // DISPLAY ONLY - every subtotal and the grand total are the backend's,
    // computed from the LUMPED rows, so expanding can never double-count.
    const rowHasLots = (r) => (r.lots || []).length > 1;
    // Every expandable row on the page, for the expand/collapse-all control and
    // for the shared left inset (see lotGutter).
    const multiLotKeys = sections
      .flatMap((s) => s.rows || [])
      .filter(rowHasLots)
      .map(lotRowKey);
    const allLotsExpanded =
      multiLotKeys.length > 0 && multiLotKeys.every((k) => expandedRows?.[k]);
    // The chevron is absolutely positioned at the left edge of the Date cell, so
    // it adds no width; `report-lot-gutter` reserves the space it occupies
    // (index.css, .report-scroll scope - a Tailwind pl-* utility loses to the
    // viewer's cell-compaction rule). Unlike FI Holdings' right-aligned Par
    // column, Date is CENTRED, so the inset goes on the header and on EVERY cell
    // in the column - insetting only the rows that carry an arrow would leave
    // their dates 10px right of everyone else's.
    const lotGutter = multiLotKeys.length > 0 ? ' report-lot-gutter' : '';

    // Three summary cards: Total Market Value | Total Cost Basis (neutral) |
    // Total Unrealized Gain/Loss (sign-colored, matching grand_total).
    const summaryCards = (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Market Value</p>
            <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(pickSettle(reportData, 'total_market_value') || 0)}</p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Cost Basis</p>
            <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(pickSettle(reportData, 'total_cost_basis') || 0)}</p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Unrealized Gain/Loss</p>
            <p className={`text-xl font-bold ${gainCls(pickSettle(reportData, 'total_unrealized_gain_loss') || 0)}`}>{formatCurrency(pickSettle(reportData, 'total_unrealized_gain_loss') || 0)}</p>
          </div>
        </div>
      </div>
    );

    const headTh = (extra) =>
      `p-2 font-semibold ${extra} ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const renderHead = () => (
      <thead>
        <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
          <th className={`${headTh('text-center')}${lotGutter}`}>Date</th>
          <th className={headTh('text-right')}>Quantity</th>
          <th className={headTh('text-left')}>Symbol</th>
          <th className={headTh('text-left')}>Security</th>
          <th className={headTh('text-right')}>Adj Unit Cost</th>
          <th className={headTh('text-right')}>Total Adjusted Cost</th>
          <th className={headTh('text-right')}>Price</th>
          <th className={headTh('text-right')}>Market Value</th>
          <th className={headTh('text-right')}>Unrealized G/L</th>
          <th className={headTh('text-right')}>% G/L</th>
        </tr>
      </thead>
    );

    // The APX print's % G/L = gain / adjusted cost, one decimal (GT prints
    // e.g. 0.2 / -8.7); null (cash rows, zero-cost) renders '-'.
    const fmtPct = (v) => (v == null ? '-' : `${v.toFixed(1)}%`);

    // One data row per position. Cash/margin rows carry nulls for
    // date/quantity/adj_unit_cost/price/gain_loss/pct and render '-'. The money
    // cells read the active basis (pickSettle), as the subtotals below do: a cash
    // line's settlement balance differs from its trade one, and the settlement
    // subtotal carries it.
    const renderBodyRow = (r, key) => {
      const hasLots = rowHasLots(r);
      const isExpanded = hasLots && !!expandedRows?.[lotRowKey(r)];
      const gain = pickSettle(r, 'gain_loss');
      const pct = pickSettle(r, 'pct');
      return (
        <tr
          key={key}
          {...(hasLots ? { 'data-toggle-key': lotRowKey(r) } : {})}
          className={`border-b ${hasLots ? 'cursor-pointer ' : ''}${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}
        >
          <td className={`p-2 text-center whitespace-nowrap relative${lotGutter}`}>
            {hasLots && (
              // Screen-only affordance: stripped from the print DOM.
              // data-no-insight keeps the viewer's field-insight click off it - the
              // arrow only toggles the lots, while the date beside it stays a
              // normal inspectable cell.
              <span className="absolute left-0 top-1/2 -translate-y-1/2" data-print-skip data-no-insight>
                {isExpanded
                  ? <ChevronDown className="w-3.5 h-3.5" />
                  : <ChevronRight className="w-3.5 h-3.5" />}
              </span>
            )}
            {r.date ? (isoToMDY(r.date) || '-') : '-'}
          </td>
          <td className="p-2 text-right">{fmtShares(r.quantity)}</td>
          <td className="p-2 whitespace-nowrap uppercase" data-export-text>{r.symbol || '-'}</td>
          <td className="p-2 align-top" title={r.description}>
            <div className="break-words">{stripCouponDue(r.description) || '-'}</div>
            {(r.coupon > 0 || r.due) && (
              <DueLine coupon={r.coupon} due={isoToMDY(r.due)} darkMode={darkMode} />
            )}
          </td>
          <td className="p-2 text-right">{fmtNum(r.adj_unit_cost, 2)}</td>
          <td className="p-2 text-right">{money(pickSettle(r, 'total_adjusted_cost'))}</td>
          <td className="p-2 text-right">{fmtNum(r.price, 2)}</td>
          <td className="p-2 text-right">{money(pickSettle(r, 'market_value'))}</td>
          <td className={`p-2 text-right ${gain == null ? '' : gainCls(gain)}`}>
            {gain == null ? '-' : formatCurrency(gain)}
          </td>
          <td className={`p-2 text-right ${pct == null ? '' : gainCls(pct)}`}>
            {fmtPct(pct)}
          </td>
        </tr>
      );
    };

    // The lumped row's individual open tax lots as muted sub-rows, numbered in
    // SETTLE-date order by the shared `numberedLots` comparator so the label can
    // never disagree with the row's position on the page. The italic label is
    // what identifies the row in PRINT, where the chevron and the muted
    // background are both flattened away.
    //
    // Symbol and Price stay blank: both are security-level, so a per-lot cell
    // would repeat the parent rather than say anything. Everything else IS per
    // lot - and Adj Unit Cost most of all: each figure is the constant-yield
    // book value solved from THAT lot's own purchase price and date, which is
    // why this report earns a drill-down FI Holdings cannot give (there the
    // adjusted cost arrives from the appraisal at position level).
    const renderLotRows = (r, key) =>
      numberedLots(r.lots).map(({ lot, number }) => (
        <tr
          key={`${key}-lot-${number}`}
          className={`border-b ${darkMode ? 'border-white/5 bg-[#101010] text-gray-400' : 'border-gray-100 bg-gray-50 text-gray-600'}`}
        >
          <td className={`p-2 text-center whitespace-nowrap${lotGutter}`}>
            {isoToMDY(lot.trade_date) || '-'}
          </td>
          <td className="p-2 text-right">{fmtShares(lot.quantity)}</td>
          <td className="p-2" />
          <td className="p-2 pl-6 italic">{`Tax lot #${number}`}</td>
          <td className="p-2 text-right">{fmtNum(lot.adj_unit_cost, 2)}</td>
          <td className="p-2 text-right">{money(lot.total_adjusted_cost)}</td>
          <td className="p-2" />
          <td className="p-2 text-right">{money(lot.market_value)}</td>
          <td className={`p-2 text-right ${lot.gain_loss == null ? '' : gainCls(lot.gain_loss)}`}>
            {lot.gain_loss == null ? '-' : formatCurrency(lot.gain_loss)}
          </td>
          <td className={`p-2 text-right ${lot.pct == null ? '' : gainCls(lot.pct)}`}>
            {fmtPct(lot.pct)}
          </td>
        </tr>
      ));

    // A body row plus, when expanded, its lot sub-rows. Wrapped in a Fragment so
    // the caller's <tbody> grouping (section-keep / row-keep) is unchanged - the
    // lots ride inside the same break-inside:avoid group as their parent.
    const renderBodyGroup = (r, key) => (
      <React.Fragment key={key}>
        {renderBodyRow(r, key)}
        {rowHasLots(r) && expandedRows?.[lotRowKey(r)] && renderLotRows(r, key)}
      </React.Fragment>
    );

    // A totals row (section subtotal or GRAND TOTAL): a colSpan label over the
    // five leading columns, then Total Adjusted Cost / (blank Price) / Market
    // Value / Unrealized G/L / % G/L so it stays pinned under the headers.
    // Cash-only subtotals carry no gain_loss/pct and render blank.
    const renderTotalsRow = (label, obj, { pad = 'p-2', rowClass }) => {
      const gain = pickSettle(obj, 'gain_loss');
      const pct = pickSettle(obj, 'pct');
      return (
        <tr className={rowClass}>
          <td className={`${pad} text-left`} colSpan={LEAD_COLS}>{label}</td>
          <td className={`${pad} text-right`}>{money(pickSettle(obj, 'total_adjusted_cost'))}</td>
          <td className={`${pad} text-right`} />
          <td className={`${pad} text-right`}>{money(pickSettle(obj, 'market_value'))}</td>
          <td className={`${pad} text-right ${gain == null ? '' : gainCls(gain)}`}>
            {gain == null ? '-' : formatCurrency(gain)}
          </td>
          <td className={`${pad} text-right ${pct == null ? '' : gainCls(pct)}`}>
            {fmtPct(pct)}
          </td>
        </tr>
      );
    };

    const head = renderHead();
    const bodyRow = renderBodyGroup;
    const totalsRow = renderTotalsRow;

    // Legacy fallback: old cached payloads have no `sections`, only the flat
    // positions[] list. Render the trimmed pre-sectioning table.
    if (sections.length === 0 && positions.length > 0) {
      return (
        <div className="space-y-4">
          {summaryCards}
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>CUSIP</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Quantity</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Cost Basis</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Market Value</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Unrealized G/L</th>
                </tr>
              </thead>
              <tbody>
                {positions.map((p, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <td className="p-3" data-export-text>{p.cusip || '-'}</td>
                    <td className="p-3 text-right">{fmtShares(p.quantity)}</td>
                    <td className="p-3 text-right">{formatCurrency(p.cost_basis || 0)}</td>
                    <td className="p-3 text-right">{formatCurrency(p.market_value || 0)}</td>
                    <td className={`p-3 text-right ${gainCls(p.unrealized_gain_loss)}`}>{formatCurrency(p.unrealized_gain_loss || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (sections.length === 0) {
      return (
        <div className="space-y-4">
          {summaryCards}
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No positions found.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {summaryCards}
        {/* `report-sticky-toolbar`: the viewer freezes this bar with the column
            headers (index.css, .report-scroll scope) so Expand All stays
            reachable from anywhere in a long report. It must stay a SIBLING of
            the table wrapper below - the measured bar height is written on their
            shared parent and inherited by the <thead> that pins under it. */}
        {(multiLotKeys.length > 0 || holdingsBasisSelect) && (
          <div className="report-sticky-toolbar flex items-center justify-end gap-4" data-export-skip>
            {multiLotKeys.length > 0 && (
              <button
                type="button"
                data-print-skip
                data-toggle-all={allLotsExpanded ? 'collapse' : 'expand'}
                data-toggle-all-keys={multiLotKeys.join(',')}
                className={`text-xs underline mr-auto ${darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                {allLotsExpanded ? 'Minimize All' : 'Expand All'}
              </button>
            )}
            {holdingsBasisSelect}
          </div>
        )}
        {basisDisclosure(allSections.flatMap((s) => s.rows || []))}
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            {head}
            {/* A section the active basis holds nothing of prints no band and
                no zero subtotal (see FI Holdings). */}
            {sections.filter((section) => section.rows.length > 0).map((section, si) => {
              const rows = section.rows || [];
              return (
                <React.Fragment key={`${section.name}-${si}`}>
                  {/* The section band + its FIRST position row form their own
                      `section-keep` tbody so paged.js never orphans a section
                      title at the foot of a page (see FI Holdings renderer). */}
                  <tbody className="section-keep">
                    <tr>
                      <td
                        colSpan={totalCols}
                        className={`text-sm font-bold uppercase tracking-wide p-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}
                      >
                        {section.name}
                      </td>
                    </tr>
                    {rows.slice(0, 1).map((r, ri) => bodyRow(r, `${si}-${ri}`))}
                  </tbody>
                  {/* Every remaining position row gets its own break-inside:avoid
                      `row-keep` tbody so paged.js keeps it - and its expanded
                      lots, which ride in the same group - together. */}
                  {rows.slice(1).map((r, ri) => (
                    <tbody className="row-keep" key={`rk-${si}-${ri + 1}`}>
                      {bodyRow(r, `${si}-${ri + 1}`)}
                    </tbody>
                  ))}
                  <tbody>
                    {totalsRow(`${section.name} Total`, section.subtotal, {
                      rowClass: `font-semibold border-t-2 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`,
                    })}
                  </tbody>
                </React.Fragment>
              );
            })}
            <tbody>
              {totalsRow('GRAND TOTAL', grand_total, {
                pad: 'p-3',
                rowClass: `font-bold border-t-4 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-700 bg-gray-100'}`,
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDateToDateGainLoss = () => {
    const {
      start_value, end_value, gain_loss, gain_loss_economic, gain_loss_twr_basis,
      pct_change, startDate, endDate, management_fees, fee_basis, security_schedule,
    } = reportData;
    // 2026-07-29: the headline gain_loss is the ECONOMIC gain (end - start -
    // net external flows, on the card's fee basis) - the same construction
    // Advent's Date-to-Date total prints. The summed daily PERFORMANCE.gain
    // (TWR basis) prints beside it as the "Investment Gain"; it falls back to
    // the economic figure for payloads that predate the twr_basis key.
    const totalReturnLabel = fee_basis === 'net_of_fees'
      ? 'Total Return Gain/Loss (net of fees)'
      : 'Total Return Gain/Loss (gross of fees)';
    // Null start/gain/% = window predates PERFORMANCE coverage (begin MV
    // unknown); show '-', never a fabricated $0 / 0.00% (Class-12).
    const money = (v) => (v == null ? '-' : formatCurrency(v));
    const signColor = (v) => (v == null
      ? (darkMode ? 'text-white' : 'text-gray-900')
      : (v >= 0 ? 'text-green-600' : 'text-red-600'));

    // L1: the GT per-security schedule - asset-class sections with begin/end
    // Qty/Price/MV+AI + a per-security Total Return Gain/Loss, section subtotals
    // and a grand total. When the seam-spanning window has no begin APPRAISAL
    // snapshot (grand_total.begin_available === false) or a row omits a begin
    // field, the begin columns dash out - never a fabricated 0.
    const renderSchedule = () => {
      if (!security_schedule || !Array.isArray(security_schedule.sections)) return null;
      const gt = security_schedule.grand_total || {};
      const beginKnown = gt.begin_available !== false;
      const num = (v, digits) => (v == null
        ? '-'
        : Number(v).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }));
      const beginNum = (v, digits) => (beginKnown ? num(v, digits) : '-');
      const beginMoney = (v) => (beginKnown && v != null ? formatCurrency(v) : '-');
      const thc = `px-2 py-1 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
      const scheduleRow = (r, key) => (
        <tr key={key} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <td className="px-2 py-1" data-export-text>
            <div className="font-medium">{r.name || r.symbol || r.cusip || '-'}</div>
            {r.cusip && <div className={`text-[10px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{r.cusip}</div>}
          </td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{beginNum(r.begin_quantity, 2)}</td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{beginNum(r.begin_price, 4)}</td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{beginMoney(r.begin_mv)}</td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{num(r.end_quantity, 2)}</td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{num(r.end_price, 4)}</td>
          <td className="px-2 py-1 text-right whitespace-nowrap">{money(r.end_mv)}</td>
          <td className={`px-2 py-1 text-right whitespace-nowrap ${signColor(r.total_return_gain_loss)}`}>{money(r.total_return_gain_loss)}</td>
        </tr>
      );
      return (
        <div className="overflow-x-auto">
          {/* 11px + single-step row padding: the schedule is the long part of
              this report, so tighter rows are what actually buys printed pages. */}
          <table className={`w-full border-collapse text-[11px] ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className={`${thc} text-left`}>Security</th>
                <th className={`${thc} text-right`}>{startDate} Qty</th>
                <th className={`${thc} text-right`}>{startDate} Price</th>
                <th className={`${thc} text-right`}>{startDate} MV + AI</th>
                <th className={`${thc} text-right`}>{endDate} Qty</th>
                <th className={`${thc} text-right`}>{endDate} Price</th>
                <th className={`${thc} text-right`}>{endDate} MV + AI</th>
                <th className={`${thc} text-right`}>Total Return Gain/Loss</th>
              </tr>
            </thead>
            {security_schedule.sections.map((section, si) => {
              const sub = section.subtotals || {};
              return (
                <tbody key={`${section.label}-${si}`}>
                  <tr>
                    <td colSpan={8} className={`text-xs font-bold uppercase tracking-wide px-2 py-1 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>
                      {section.label}
                    </td>
                  </tr>
                  {(section.rows || []).map((r, ri) => scheduleRow(r, `${si}-${ri}`))}
                  <tr className={`font-semibold border-t ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-300 bg-gray-50'}`}>
                    <td className="px-2 py-1">{section.label} Subtotal</td>
                    <td colSpan={2}></td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{beginMoney(sub.begin_mv)}</td>
                    <td colSpan={2}></td>
                    <td className="px-2 py-1 text-right whitespace-nowrap">{money(sub.end_mv)}</td>
                    <td className={`px-2 py-1 text-right whitespace-nowrap ${signColor(sub.total_return_gain_loss)}`}>{money(sub.total_return_gain_loss)}</td>
                  </tr>
                </tbody>
              );
            })}
            <tfoot>
              <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/20 bg-[#1a1a1a]' : 'border-gray-700 bg-gray-100'}`}>
                <td className="px-2 py-1">Grand Total</td>
                <td colSpan={2}></td>
                <td className="px-2 py-1 text-right whitespace-nowrap">{beginMoney(gt.begin_mv)}</td>
                <td colSpan={2}></td>
                <td className="px-2 py-1 text-right whitespace-nowrap">{money(gt.end_mv)}</td>
                <td className={`px-2 py-1 text-right whitespace-nowrap ${signColor(gt.total_return_gain_loss)}`}>{money(gt.total_return_gain_loss)}</td>
              </tr>
            </tfoot>
          </table>
          {!beginKnown && (
            <p className={`mt-2 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Beginning quantity, price and market value are unavailable: no APPRAISAL snapshot exists at or before {startDate}.
            </p>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {/* The report header already prints the title and the period, so this
            card carries only the figures - repeating either here just costs
            vertical space on page 1. Type is deliberately one step down from
            the other report cards to fit more schedule rows per printed sheet. */}
        <div className={`p-3 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Start Value</p>
              <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{money(start_value)}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>End Value</p>
              <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(end_value || 0)}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>% Change</p>
              <p className={`text-base font-bold ${signColor(pct_change)}`}>{pct_change == null ? '-' : `${pct_change.toFixed(2)}%`}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{totalReturnLabel}</p>
              <p className={`text-base font-bold whitespace-nowrap ${signColor(gain_loss)}`}>{money(gain_loss)}</p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Investment Gain</p>
              <p className={`text-base font-bold whitespace-nowrap ${signColor(gain_loss_twr_basis ?? gain_loss_economic)}`}>{money(gain_loss_twr_basis ?? gain_loss_economic)}</p>
            </div>
            {management_fees !== undefined && management_fees !== null && (
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Management Fees</p>
                <p className={`text-base font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(management_fees)}</p>
              </div>
            )}
          </div>
        </div>
        {renderSchedule()}
      </div>
    );
  };

  const renderChangeInPortfolioValue = () => {
    // L2: Change in Portfolio Value now ships Advent's own two-block DCF
    // reconciliation waterfall (its own generator, no longer the D2D card).
    // Fallback: an old cached D2D-shaped payload (no `portfolio_value_start`
    // key) keeps rendering the legacy card exactly as today.
    if (!('portfolio_value_start' in reportData)) {
      return renderDateToDateGainLoss();
    }
    const {
      portfolio_value_start, net_additions_withdrawals, net_intra_account_transfers,
      adjusted_portfolio_value, portfolio_value_end, change,
      income_received, change_in_market_value, change_in_accrued_interest,
      accrued_split_reliable, accrued_split_note,
      startDate, endDate,
    } = reportData;
    // Degrade-to-None convention: a null line prints '-', never 0 or a hidden
    // row (the value walk and Income Received stay correct even when the
    // accrued split is unsourceable on a seam-spanning window).
    const money = (v) => (v === null || v === undefined ? '-' : formatCurrency(v));
    const changeLabel = `Change from ${startDate} to ${endDate}`;

    // Block 1 - the value walk (begin + flows + transfers = adjusted; end -
    // adjusted = change).
    const block1 = [
      { label: `Portfolio Value on ${startDate}`, value: portfolio_value_start },
      { label: 'Net Additions & Withdrawals', value: net_additions_withdrawals },
      { label: 'Net Intra Account Transfers', value: net_intra_account_transfers },
      { label: 'Adjusted Portfolio Value', value: adjusted_portfolio_value, subtotal: true },
      { label: `Portfolio Value on ${endDate}`, value: portfolio_value_end },
      { label: changeLabel, value: change, subtotal: true },
    ];
    // Block 2 - attribution (income + change in MV + change in accrued = the
    // same change).
    const block2 = [
      { label: 'Income Received', value: income_received },
      { label: 'Change in Market Value', value: change_in_market_value },
      { label: 'Change in Accrued Interest', value: change_in_accrued_interest },
      { label: changeLabel, value: change, subtotal: true },
    ];
    const renderBlock = (blockRows, note) => (
      <div className={`p-4 rounded-lg border ${darkMode ? 'bg-[#141414] border-white/10' : 'bg-gray-50 border-gray-200'}`}>
        <div className="space-y-2">
          {blockRows.map((item, i) => (
            <div key={i} data-export-row className={`flex justify-between ${item.subtotal ? 'border-t pt-2 mt-2 font-semibold' : ''} ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{item.label}</span>
              <span className={`whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>{money(item.value)}</span>
            </div>
          ))}
        </div>
        {note && (
          <p className={`mt-3 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`} data-export-row>{note}</p>
        )}
      </div>
    );

    return (
      <div className="space-y-4">
        <div>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Change in Portfolio Value</h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Period: {startDate} to {endDate}</p>
        </div>
        {/* Both halves of the waterfall in ONE sheet: block 2 re-derives block
            1's change from the income side, and the pair only reconciles read
            together. The unreliable-split note is a row of it too - the walker
            reads a childless row as a sentence. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-export-rows="Change in Portfolio Value">
          {renderBlock(block1, null)}
          {renderBlock(block2, accrued_split_reliable === false ? accrued_split_note : null)}
        </div>
      </div>
    );
  };

  const renderIncomeAndExpenses = () => {
    const {
      sections = [], income_items = [], expense_items = [],
      total_income, total_expenses, net_income, total_fees, fees_excluded,
    } = reportData;

    // Sign color, matching the realized-gains renderer: >=0 green else red. Only
    // these two utilities survive the print black-on-white flatten (pinned in
    // pagedReportCss.js), so the sign color prints correctly.
    const gainCls = (v) => (v >= 0 ? 'text-green-600' : 'text-red-600');

    // The APX INCOME printout is four asset-class sections of per-security
    // payment rows: Symbol | Security | Pay Date | Amount, with per-security
    // subtotals (only when a security has >1 payment), section totals, and one
    // NET INCOME grand total. Body rows, subtotals, section totals and the grand
    // total all live in ONE <table> so columns line up and paged.js repeats the
    // single <thead> across split pages (see renderFixedIncomeHoldingsByMaturity
    // for the same machinery).
    const totalCols = 5; // Symbol | Security | Ex-Date | Pay Date | Amount

    // Three summary cards ordered Total Expenses | Total Income | Net Income.
    // The backend books total_expenses as a positive magnitude (an outflow); the
    // card shows it as a negative so it reads red and nets visually against
    // income (net = income - expenses).
    const expensesOutflow = -(total_expenses || 0);
    const summaryCards = (
      <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Expenses</p>
            <p className={`text-xl font-bold ${gainCls(expensesOutflow)}`}>{formatCurrency(expensesOutflow)}</p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Income</p>
            <p className={`text-xl font-bold ${gainCls(total_income || 0)}`}>{formatCurrency(total_income || 0)}</p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Net Income</p>
            <p className={`text-xl font-bold ${gainCls(net_income || 0)}`}>{formatCurrency(net_income || 0)}</p>
          </div>
        </div>
        {fees_excluded && (total_fees || 0) > 0 && (
          <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Management/custodial fees ({formatCurrency(total_fees)}) are not
            included in NET INCOME.
          </p>
        )}
      </div>
    );

    const headTh = (extra) =>
      `p-2 font-semibold ${extra} ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const renderHead = () => (
      <thead>
        <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
          <th className={headTh('text-left')}>Symbol</th>
          <th className={headTh('text-left')}>Security</th>
          <th className={headTh('text-center')}>Ex-Date</th>
          <th className={headTh('text-center')}>Pay Date</th>
          <th className={headTh('text-right')}>Amount</th>
        </tr>
      </thead>
    );

    // One data row per payment. The FIRST payment row of a security carries the
    // Symbol + Security cell; subsequent payment rows leave them blank so the
    // security reads as a single block (mirrors the APX printout).
    const renderPaymentRow = (sec, pay, isFirst, key) => (
      <tr key={key} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
        <td className="p-2 whitespace-nowrap" data-export-text>{isFirst ? (sec.symbol || '-') : ''}</td>
        <td className="p-2 align-top" title={isFirst ? sec.security_name : undefined}>
          {isFirst && (
            <>
              <div className="break-words">{stripCouponDue(sec.security_name) || '-'}</div>
              {(sec.coupon_rate > 0 || sec.maturity_date) && (
                <DueLine coupon={sec.coupon_rate} due={isoToMDY(sec.maturity_date)} darkMode={darkMode} />
              )}
            </>
          )}
        </td>
        <td className="p-2 text-center whitespace-nowrap">{isoToMDY(pay.ex_date) || '-'}</td>
        <td className="p-2 text-center whitespace-nowrap">{isoToMDY(pay.pay_date) || '-'}</td>
        <td className={`p-2 text-right ${gainCls(pay.amount || 0)}`}>{formatCurrency(pay.amount || 0)}</td>
      </tr>
    );

    // A totals row (per-security subtotal, section total, or NET INCOME): a label
    // spanning the three leading columns and the summed Amount in the last, so it
    // stays pinned under the headers.
    const renderTotalsRow = (label, amount, { pad = 'p-2', rowClass }) => (
      <tr className={rowClass}>
        <td className={`${pad} text-left`} colSpan={4}>{label}</td>
        <td className={`${pad} text-right ${gainCls(amount || 0)}`}>{formatCurrency(amount || 0)}</td>
      </tr>
    );

    // Legacy fallback: old cached payloads have no `sections`, only the flat
    // income_items/expense_items lists. Render them as the pre-sectioning tables.
    if (sections.length === 0) {
      const flatTable = (items, sign) => (
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Type</th>
                <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>CUSIP</th>
                <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <td className="p-3">{item.date || '-'}</td>
                  <td className="p-3">{item.type || '-'}</td>
                  <td className="p-3" data-export-text>{item.cusip || '-'}</td>
                  <td className={`p-3 text-right ${sign}`}>{formatCurrency(item.amount || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      return (
        <div className="space-y-4">
          {summaryCards}
          {income_items.length === 0 && expense_items.length === 0 && (
            <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No income or expenses found.
            </div>
          )}
          {income_items.length > 0 && (
            <div>
              <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Income Items</h4>
              {flatTable(income_items, 'text-green-600')}
            </div>
          )}
          {/* PM Q18: purchased accrued interest and fee rows print as explicit
              expense line items (fees included unless excluded above). */}
          {expense_items.length > 0 && (
            <div>
              <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Expense Items</h4>
              {flatTable(expense_items, 'text-red-600')}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {summaryCards}
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            {renderHead()}
            {sections.map((section, si) => {
              const securities = section.securities || [];
              // Flatten every security's payments into ordered rows, tagging the
              // first row of each security and appending a per-security subtotal
              // when the security has >1 payment.
              const secBlocks = securities.map((sec, sci) => {
                const payments = sec.payments || [];
                return { sec, sci, payments };
              });
              return (
                <React.Fragment key={`${section.name}-${si}`}>
                  {/* The section band + its FIRST payment row form their own
                      `section-keep` tbody so paged.js never orphans a section
                      title at the foot of a page (see FI Holdings renderer). */}
                  <tbody className="section-keep">
                    <tr>
                      <td
                        colSpan={totalCols}
                        className={`text-sm font-bold uppercase tracking-wide p-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}
                      >
                        {section.name}
                      </td>
                    </tr>
                    {secBlocks.length > 0 && secBlocks[0].payments.length > 0 &&
                      renderPaymentRow(secBlocks[0].sec, secBlocks[0].payments[0], true, `${si}-0-0`)}
                  </tbody>
                  {/* Every remaining row (later payments of the first security,
                      its subtotal, and all subsequent securities) gets its own
                      break-inside:avoid `row-keep` tbody so paged.js keeps each
                      logical row together across engines. */}
                  {secBlocks.map(({ sec, sci, payments }) => (
                    <React.Fragment key={`sec-${si}-${sci}`}>
                      {payments.map((pay, pi) => {
                        // The very first payment of the first security already
                        // rendered inside the section-keep tbody above.
                        if (sci === 0 && pi === 0) return null;
                        return (
                          <tbody className="row-keep" key={`rk-${si}-${sci}-${pi}`}>
                            {renderPaymentRow(sec, pay, pi === 0, `${si}-${sci}-${pi}`)}
                          </tbody>
                        );
                      })}
                      {payments.length > 1 && (
                        <tbody className="row-keep" key={`st-${si}-${sci}`}>
                          {renderTotalsRow(`${sec.symbol || sec.security_name || ''} Total`, sec.subtotal, {
                            rowClass: `font-medium ${darkMode ? 'border-white/10' : 'border-gray-300'}`,
                          })}
                        </tbody>
                      )}
                    </React.Fragment>
                  ))}
                  <tbody>
                    {renderTotalsRow(`${section.name} Total`, section.section_total, {
                      rowClass: `font-semibold border-t-2 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`,
                    })}
                  </tbody>
                </React.Fragment>
              );
            })}
            <tbody>
              {renderTotalsRow('NET INCOME', net_income, {
                pad: 'p-3',
                rowClass: `font-bold border-t-4 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-700 bg-gray-100'}`,
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Shared caveat banner: when no APPRAISAL custodial-cash snapshot exists at/
  // before the window, build_daily_cash_values / build_cash_ledger fall back to a
  // zero-based replay whose balances are RELATIVE (understate absolute cash). The
  // note is computed backend-side; surface it so the dollar figures are not read
  // as absolute. Mirrors the back-dated resolve_as_of_date clamp notice.
  const renderCashAnchorBanner = () => {
    const { cash_anchor_missing, cash_anchor_note } = reportData;
    if (!cash_anchor_missing || !cash_anchor_note) return null;
    return (
      <div className={`p-3 rounded-lg border text-sm ${darkMode ? 'bg-amber-900/20 border-amber-800 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
        {cash_anchor_note}
      </div>
    );
  };

  const renderCashLedger = () => {
    const {
      sections = [],
      ledger_entries = [],
      beginning_balance,
      ending_balance,
      beginning_balance_settle,
      ending_balance_settle,
      startDate,
      endDate,
    } = reportData;
    // Present only when a backend agent has supplied the settlement-basis
    // figures; old payloads render exactly as before.
    const hasSettle = beginning_balance_settle !== undefined || ending_balance_settle !== undefined;
    const hasEntrySettle =
      ledger_entries.some((e) => e.running_balance_settle !== undefined) ||
      sections.some((s) => (s.entries || []).some((e) => e.running_balance_settle !== undefined));

    return (
      <div className="space-y-4">
        {renderCashAnchorBanner()}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Cash Ledger with Running Balance</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Period: {startDate} to {endDate}</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Beginning Balance</p>
              {hasSettle && (
                <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Trade Date</p>
              )}
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(beginning_balance || 0)}</p>
              {hasSettle && (
                <>
                  <p className={`text-xs mt-2 mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Settlement Date</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(beginning_balance_settle || 0)}</p>
                </>
              )}
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Ending Balance</p>
              {hasSettle && (
                <p className={`text-xs mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Trade Date</p>
              )}
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(ending_balance || 0)}</p>
              {hasSettle && (
                <>
                  <p className={`text-xs mt-2 mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Settlement Date</p>
                  <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(ending_balance_settle || 0)}</p>
                </>
              )}
            </div>
          </div>
          {hasSettle && (
            <p className={`text-xs mt-4 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Trade-date balance is the booking-date (APX-parity) figure; settlement-date is post-settlement custodial cash. The two converge as pending trades settle.
            </p>
          )}
        </div>

        {sections.length > 0 ? (
          // APX-parity per-account sections: each cash security is its own block
          // with a Beginning Balance row, its in-window entries (each carrying its
          // OWN running balance), and an Ending Balance row. The aggregate card
          // above sums them.
          <div className="space-y-6">
            {sections.map((section, si) => (
              <div key={si} className="overflow-x-auto">
                <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {section.account_name || `Cash ${si + 1}`}
                </h4>
                <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Description</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Debit</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Credit</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Balance</th>
                      {hasSettle && (
                        <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Balance (Settle)</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className={`border-b font-medium ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-200 bg-gray-50'}`}>
                      <td className="p-3">-</td>
                      <td className="p-3">Beginning Balance</td>
                      <td className="p-3 text-right">-</td>
                      <td className="p-3 text-right">-</td>
                      <td className="p-3 text-right">{formatCurrency(section.beginning_balance || 0)}</td>
                      {hasSettle && (
                        <td className="p-3 text-right">{section.beginning_balance_settle !== undefined ? formatCurrency(section.beginning_balance_settle) : '-'}</td>
                      )}
                    </tr>
                    {(section.entries || []).map((entry, index) => (
                      <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className="p-3">{entry.date || '-'}</td>
                        <td className="p-3">{entry.description || '-'}</td>
                        <td className="p-3 text-right text-red-600">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</td>
                        <td className="p-3 text-right text-green-600">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</td>
                        <td className="p-3 text-right font-medium">{formatCurrency(entry.running_balance || 0)}</td>
                        {hasSettle && (
                          <td className="p-3 text-right font-medium">{entry.running_balance_settle !== undefined ? formatCurrency(entry.running_balance_settle) : '-'}</td>
                        )}
                      </tr>
                    ))}
                    <tr className={`font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>
                      <td className="p-3">-</td>
                      <td className="p-3">Ending Balance</td>
                      <td className="p-3 text-right">-</td>
                      <td className="p-3 text-right">-</td>
                      <td className="p-3 text-right">{formatCurrency(section.ending_balance || 0)}</td>
                      {hasSettle && (
                        <td className="p-3 text-right">{section.ending_balance_settle !== undefined ? formatCurrency(section.ending_balance_settle) : '-'}</td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : ledger_entries.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Description</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Debit</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Credit</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Balance</th>
                  {hasEntrySettle && (
                    <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Balance (Settle)</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {ledger_entries.map((entry, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <td className="p-3">{entry.date || '-'}</td>
                    <td className="p-3">{entry.description || '-'}</td>
                    <td className="p-3 text-right text-red-600">{entry.debit > 0 ? formatCurrency(entry.debit) : '-'}</td>
                    <td className="p-3 text-right text-green-600">{entry.credit > 0 ? formatCurrency(entry.credit) : '-'}</td>
                    <td className="p-3 text-right font-medium">{formatCurrency(entry.running_balance || 0)}</td>
                    {hasEntrySettle && (
                      <td className="p-3 text-right font-medium">{entry.running_balance_settle !== undefined ? formatCurrency(entry.running_balance_settle) : '-'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No ledger entries found.
          </div>
        )}
      </div>
    );
  };

  const renderAssetsUnderManagementWithPortCode = () => {
    const { account_count, portfolios = [], asOfDate, mv_source } = reportData;
    const splitUnavailable = mv_source && mv_source !== 'appraisal_snapshot';
    const fmtSplit = (v) => (v == null ? 'N/A' : formatCurrency(v));
    // Headline figures on the active holdings basis.
    const total_aum = pickSettle(reportData, 'total_aum');
    const holdings_value = pickSettle(reportData, 'holdings_value');
    const cash_value = pickSettle(reportData, 'cash_value');
    const accrued_interest = pickSettle(reportData, 'accrued_interest');

    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assets Under Mgmt with Port Code</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>As of: {asOfDate}</p>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total AUM</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(total_aum || 0)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Holdings</p>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmtSplit(holdings_value)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Cash</p>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmtSplit(cash_value)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Accrued Interest</p>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmtSplit(accrued_interest)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Accounts</p>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{account_count || 0}</p>
            </div>
          </div>
          {splitUnavailable && (
            <p className={`text-xs mt-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Holdings/Cash split unavailable for this date; market value is from the performance series.
            </p>
          )}
        </div>

        {portfolios.length > 0 && (() => {
          // APX prints Code | Portfolio | Rep Group | Open Date | Index1 |
          // Index 2 | the four asset-class columns | Total, plus a TOTAL
          // footer. The payload already carries fixed_income/equity/
          // cash_and_equivalents/other per portfolio; a null Rep Group prints
          // APX's own '?'.
          const th = `p-2 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
          const money = (v) => (v == null ? '-' : formatCurrency(v));
          const sum = (k) => portfolios.reduce((a, p) => a + (Number(pickSettle(p, k)) || 0), 0);
          const hasSplit = portfolios.some((p) => p.fixed_income != null
            || p.equity != null || p.cash_and_equivalents != null || p.other != null);
          return (
          <div>
            <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>By Portfolio</h4>
            {holdingsBasisSelect && (
              <div className="report-sticky-toolbar flex items-center justify-end" data-export-skip data-print-skip>
                {holdingsBasisSelect}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className={`${th} text-left`}>Code</th>
                    <th className={`${th} text-left`}>Portfolio</th>
                    <th className={`${th} text-left`}>Rep Group</th>
                    <th className={`${th} text-left`}>Open Date</th>
                    <th className={`${th} text-left`}>Index1</th>
                    <th className={`${th} text-left`}>Index 2</th>
                    {hasSplit && (
                      <>
                        <th className={`${th} text-right`}>Fixed Income</th>
                        <th className={`${th} text-right`}>Equity</th>
                        <th className={`${th} text-right`}>Cash and Equivalents</th>
                        <th className={`${th} text-right`}>Other</th>
                      </>
                    )}
                    <th className={`${th} text-right`}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolios.map((p, index) => (
                    <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <td className="p-2" data-export-text>{p.port_code || '-'}</td>
                      <td className="p-2" data-export-text>{p.name || '-'}</td>
                      <td className="p-2">{p.rep_group || '?'}</td>
                      <td className="p-2">{p.open_date || '-'}</td>
                      <td className="p-2">{p.index1 || '-'}</td>
                      <td className="p-2">{p.index2 || '-'}</td>
                      {hasSplit && (
                        <>
                          <td className="p-2 text-right">{money(pickSettle(p, 'fixed_income'))}</td>
                          <td className="p-2 text-right">{money(pickSettle(p, 'equity'))}</td>
                          <td className="p-2 text-right">{money(pickSettle(p, 'cash_and_equivalents'))}</td>
                          <td className="p-2 text-right">{money(pickSettle(p, 'other'))}</td>
                        </>
                      )}
                      <td className="p-2 text-right">{formatCurrency(pickSettle(p, 'value') || 0)}</td>
                    </tr>
                  ))}
                  <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                    <td className="p-2" colSpan={6}>TOTAL</td>
                    {hasSplit && (
                      <>
                        <td className="p-2 text-right">{formatCurrency(sum('fixed_income'))}</td>
                        <td className="p-2 text-right">{formatCurrency(sum('equity'))}</td>
                        <td className="p-2 text-right">{formatCurrency(sum('cash_and_equivalents'))}</td>
                        <td className="p-2 text-right">{formatCurrency(sum('other'))}</td>
                      </>
                    )}
                    <td className="p-2 text-right">{formatCurrency(sum('value'))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}
      </div>
    );
  };

  // APX's "ASSETS UNDER MANAGEMENT" list: one row per account, the four
  // asset-class columns, the accrued MEMO column (when ticked) and the row
  // Total. Accrued is already inside Fixed Income (a dirty market value), so it is
  // never added to Total again - Total is the four class columns.
  //
  // Sorting and filtering are done server-side (so print/export/rerun agree),
  // which leaves the header to state what was asked for and the count that
  // survived it. A plain <table> keeps the print/export walkers working.
  const renderAssetsUnderManagementList = () => {
    const {
      accounts = [], totals = {}, asOfDate, mv_source,
      account_count, total_account_count, excluded_unknown_open_date,
      sort = {}, filters = {}, include_accrued_interest,
    } = reportData;
    const columns = aumListColumns(include_accrued_interest === true);
    const groupInfo = reportData.__group || null;
    // Back-dated dates with no appraisal snapshot fall to the performance
    // series: total only, no split - the same notice the sibling report prints.
    const splitUnavailable = mv_source && mv_source !== 'appraisal_snapshot';
    const shown = account_count == null ? accounts.length : account_count;
    const countText = total_account_count > shown
      ? `${shown} of ${total_account_count} accounts`
      : `${shown} accounts`;

    const summary = [`Sorted by ${SORT_LABELS[sort.by] || SORT_LABELS.total}, ${sort.dir === 'asc' ? 'increasing' : 'decreasing'}`];
    if (filters.opened_from && filters.opened_to) {
      summary.push(`Opened ${isoToMDY(filters.opened_from)} to ${isoToMDY(filters.opened_to)}`);
    } else if (filters.opened_from) {
      summary.push(`Opened on or after ${isoToMDY(filters.opened_from)}`);
    } else if (filters.opened_to) {
      summary.push(`Opened on or before ${isoToMDY(filters.opened_to)}`);
    }
    if (filters.min_total != null) {
      summary.push(`Minimum total ${formatCurrency(filters.min_total)}`);
    }
    if (excluded_unknown_open_date > 0) {
      summary.push(`${excluded_unknown_open_date} account${excluded_unknown_open_date === 1 ? '' : 's'} without an inception date excluded`);
    }

    const thBase = `p-2 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const negCls = (v) => ((v || 0) < 0 ? (darkMode ? 'text-red-400' : 'text-red-600') : '');
    // A null is an ABSENT figure (the performance path), never a zero balance.
    const money = (v) => (v == null ? '-' : formatCurrency(v));
    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assets Under Management</h3>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            As of: {asOfDate}
            {groupInfo ? ` · ${groupInfo.name || groupInfo.codename}` : ''}
            {` · ${countText}`}
          </p>
          <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {summary.join(' · ')}
          </p>
          {splitUnavailable && (
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Holdings/Cash split unavailable for this date; market value is from the performance series.
            </p>
          )}
        </div>
        {holdingsBasisSelect && (
          <div className="report-sticky-toolbar flex items-center justify-end" data-export-skip data-print-skip>
            {holdingsBasisSelect}
          </div>
        )}
        {accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`${thBase} text-left`}>Port Code</th>
                  <th className={`${thBase} text-left`}>Portfolio</th>
                  {columns.map(([key, label]) => (
                    <th key={key} className={`${thBase} text-right`}>{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {accounts.map((a, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <td className="p-2" data-export-text>{a.port_code || '-'}</td>
                    <td className="p-2" data-export-text>{a.name || '-'}</td>
                    {columns.map(([key]) => {
                      const v = pickSettle(a, key);
                      return <td key={key} className={`p-2 text-right ${negCls(v)}`}>{money(v)}</td>;
                    })}
                  </tr>
                ))}
                {/* Total Row */}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-400 bg-gray-100'}`}>
                  <td className="p-2">TOTAL</td>
                  <td className="p-2"></td>
                  {columns.map(([key]) => {
                    const v = pickSettle(totals, key);
                    return <td key={key} className={`p-2 text-right ${negCls(v)}`}>{money(v)}</td>;
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No accounts match this selection.
          </div>
        )}
      </div>
    );
  };

  const renderCashBalances = () => {
    const { accounts = [], asOfDate, account_count } = reportData;
    // Settlement view prints the SETTLED cash balance - the figure the custodian
    // statement carries - and the % of assets computed against it.
    const total_cash_balance = pickSettle(reportData, 'total_cash_balance') || 0;
    const groupInfo = reportData.__group || null;
    const thBase = `p-2 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const negCls = (v) => ((v || 0) < 0 ? (darkMode ? 'text-red-400' : 'text-red-600') : '');
    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Cash Balances</h3>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            As of: {asOfDate}
            {groupInfo ? ` · ${groupInfo.name || groupInfo.codename}` : ''}
            {` · ${account_count || accounts.length} accounts`}
          </p>
        </div>
        {holdingsBasisSelect && (
          <div className="report-sticky-toolbar flex items-center justify-end" data-export-skip data-print-skip>
            {holdingsBasisSelect}
          </div>
        )}
        {accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`${thBase} text-left`}>Portfolio Code</th>
                  <th className={`${thBase} text-left`}>Portfolio</th>
                  <th className={`${thBase} text-right`}>Cash Balance</th>
                  <th className={`${thBase} text-right`}>% Assets</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a, i) => {
                  const cash = pickSettle(a, 'cash_balance');
                  const pct = pickSettle(a, 'pct_assets');
                  return (
                    <tr key={i} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                      <td className="p-2" data-export-text>{a.port_code || '-'}</td>
                      <td className="p-2" data-export-text>{a.name || '-'}</td>
                      <td className={`p-2 text-right ${negCls(cash)}`}>{formatCurrency(cash || 0)}</td>
                      <td className={`p-2 text-right ${negCls(pct)}`}>{fmtNum(pct, 1)}</td>
                    </tr>
                  );
                })}
                {/* Total Row */}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-400 bg-gray-100'}`}>
                  <td className="p-2">TOTAL CASH BALANCE</td>
                  <td className="p-2"></td>
                  <td className={`p-2 text-right ${negCls(total_cash_balance)}`}>{formatCurrency(total_cash_balance || 0)}</td>
                  <td className="p-2"></td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No accounts found for this selection.
          </div>
        )}
      </div>
    );
  };

  const renderBilling = () => {
    const {
      quarter = {}, summary = {}, clients = [], no_data,
      account_count,
    } = reportData;
    const dash = '-';
    const fmtMoney = (v) => (v === null || v === undefined ? dash : formatCurrency(v));
    const fmtRate = (r) => (r === null || r === undefined ? dash : `${Math.round(r * 10000)} bps`);
    // The invoice writes par with no cents: "Treasury Short Management Fee ($[amount])".
    const fmtPar = (v) => `$${Math.round(v ?? 0).toLocaleString('en-US')}`;
    const shortFeeLabel = (r) => `Treasury Short Management Fee (${fmtPar(r.par_value ?? r.avg_mv)})`;
    const sRows = summary.clients || [];
    const thCls = `p-3 font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`;
    const trCls = `border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`;
    const chargeTitle = quarter.in_progress
      ? `Quarter to date (through ${quarter.effective_end})`
      : 'Quarterly Charge';

    // Pivot a client's days into ONE table: a day-of-month row axis with one
    // value column per month (the printed APX invoice layout). A single table
    // is also what the print pipeline handles robustly - the previous
    // 3-tables-in-a-flex layout forced paged.js to split each month table,
    // which Gecko's overflow handling turned into thead-only pages and
    // silently dropped rows (see paginate.js onOverflow).
    const monthPivot = (days) => {
      const by = {};
      days.forEach((d) => {
        (by[d.date.slice(0, 7)] = by[d.date.slice(0, 7)] || {})[Number(d.date.slice(8, 10))] = d;
      });
      const months = Object.keys(by).sort();
      const maxDay = Math.max(0, ...days.map((d) => Number(d.date.slice(8, 10))));
      return { months, byMonth: by, maxDay };
    };
    const monthName = (ym) => new Date(`${ym}-15T00:00:00`)
      .toLocaleString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div className="space-y-4" data-export-sheet="Billing">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Billing - {quarter.label}
          </h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {quarter.start} through {quarter.effective_end}
            {quarter.in_progress ? ' (quarter in progress)' : ''}
          </p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Avg Daily MV</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmtMoney(summary.total_avg_mv)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{chargeTitle}</p>
              <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{fmtMoney(summary.total_charge)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Accounts</p>
              <p className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{account_count || 0}</p>
            </div>
          </div>
        </div>

        {no_data && (
          <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
            No market-value data is available for this quarter for the selected client(s).
          </p>
        )}

        {/* Summary: one row per client */}
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className={`${thCls} text-left`}>Client</th>
                <th className={`${thCls} text-left`}>Portfolios</th>
                <th className={`${thCls} text-right`}>Avg Daily MV</th>
                <th className={`${thCls} text-right`}>Annual Rate</th>
                <th className={`${thCls} text-right`}>{chargeTitle}</th>
              </tr>
            </thead>
            <tbody>
              {sRows.map((r, i) => (
                <tr key={i} className={trCls}>
                  <td className="p-3" data-export-text>
                    {r.name || r.code}
                    {r.short_par ? (
                      <span className={`block text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {shortFeeLabel(r)}
                      </span>
                    ) : null}
                  </td>
                  <td className="p-3" data-export-text>{(r.port_codes || []).join(', ')}</td>
                  <td className="p-3 text-right" data-value={r.avg_mv ?? ''}>{fmtMoney(r.avg_mv)}</td>
                  <td className="p-3 text-right" data-value={r.annual_rate ?? ''}>{fmtRate(r.annual_rate)}</td>
                  <td className="p-3 text-right" data-value={r.charge ?? ''}>{fmtMoney(r.charge)}</td>
                </tr>
              ))}
              <tr className={`font-bold border-t-2 ${darkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-400 bg-gray-100'}`}>
                <td className="p-3" colSpan={2}>TOTAL</td>
                <td className="p-3 text-right" data-value={summary.total_avg_mv ?? ''}>{fmtMoney(summary.total_avg_mv)}</td>
                <td className="p-3 text-right"></td>
                <td className="p-3 text-right" data-value={summary.total_charge ?? ''}>{fmtMoney(summary.total_charge)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {summary.unbilled_count > 0 && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            {summary.unbilled_count} client{summary.unbilled_count === 1 ? '' : 's'} with no fee schedule entry.
          </p>
        )}
        {/* The asterisk on the daily pages. Its per-client footnote went when
            the page was fitted to one printed sheet, so it is explained once,
            here. A carried day is not always a closed market (the 2026-06-01
            seam, a day not loaded yet), so the line does not say it is. */}
        {clients.some((c) => (c.days || []).some((d) => d.carried)) && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            * No valuation on this day: positions and prices are the last valued day&apos;s.
          </p>
        )}
        {/* Short books (PM rule 2026-08-15): billed at the PAR value of their
            short positions x annual rate x days in quarter / 365, at the
            parent account's rate.
            Their "Avg Daily MV" cells are average daily PAR, not market value. */}
        {sRows.some((r) => r.short_par) && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            † Short book - billed at the par value of short positions x annual rate x {quarter.days} / 365;
            equity short positions are excluded. Its Avg Daily MV column shows average daily par.
          </p>
        )}
        {/* Charge provenance. Pershing's MASF extract carries the fee actually
            billed; where we have one for every account in a client, the charge
            IS that amount rather than avg MV x rate x days / 365. No MASF rows exist
            before ~2026-08-05, so a historical quarter shows only the
            estimated line, exactly as it always did. */}
        {summary.actual_count > 0 && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            {summary.actual_count} client{summary.actual_count === 1 ? '' : 's'} billed at the amount
            Pershing actually charged
            {summary.estimated_count > 0
              ? `; ${summary.estimated_count} estimated from average daily market value x annual rate x ${quarter.days} / 365.`
              : '.'}
          </p>
        )}
        {sRows.some((r) => r.short_par) && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            Short management fees are billed on the par value of short positions;
            the firm does not charge on equity short positions.
          </p>
        )}
        {sRows.some((r) => r.charge_source === 'parent_actual') && (
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-export-skip>
            Short management fee included in the amount Pershing actually charged
            the parent account.
          </p>
        )}

        {/* One daily-MV section per client; .billing-client-page starts a fresh
            printed page (unscoped break-before rule in pagedReportCss). */}
        {clients.map((c, i) => (
            <div key={i} className="billing-client-page space-y-3 pt-4">
              <h4 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {c.name || c.code} - {c.short_par ? shortFeeLabel(c) : 'Daily Market Value'}, {quarter.label}
              </h4>
              {c.short_par && c.equity_short_mv ? (
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Equity short positions (market value {fmtMoney(c.equity_short_mv)}) are
                  not charged.
                </p>
              ) : null}
              {(() => {
                const { months, byMonth, maxDay } = monthPivot(c.days);
                return (
                  <table className={`billing-daily w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    <thead>
                      <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                        <th className={`${thCls} text-left`}>Day</th>
                        {months.map((m) => (
                          <th key={m} className={`${thCls} text-right`}>{monthName(m)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: maxDay }, (_, idx) => idx + 1).map((dayNum) => (
                        <tr key={dayNum} className={trCls}>
                          <td className="px-3 py-1">{dayNum}</td>
                          {months.map((m) => {
                            const d = byMonth[m][dayNum];
                            // The carried-day asterisk hangs in the cell's
                            // right padding, so the digits of a carried day
                            // line up with the days above and below it.
                            return (
                              <td key={m} className="px-3 py-1 text-right" data-value={d ? d.mv : ''}>
                                {d ? (
                                  <span className="relative">
                                    {fmtMoney(d.mv)}
                                    {d.carried ? <span className="absolute left-full">{' *'}</span> : null}
                                  </span>
                                ) : ''}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                );
              })()}
            </div>
          ))}
      </div>
    );
  };

  const renderSignificantContributionsWithdrawals = () => {
    // Match PDF layout exactly
    const {
      cash_flows = [], flowCount, threshold_pct,
      threshold_amount, excluded_exchanges = [], portfolio_name, portfolio_code
    } = reportData;

    // Group cash flows by portfolio if available
    const groupedFlows = cash_flows.reduce((acc, flow) => {
      const key = flow.portfolio_name || 'default';
      if (!acc[key]) acc[key] = [];
      acc[key].push(flow);
      return acc;
    }, {});

    return (
      <div className="space-y-4">
        <div className={`text-center p-4 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          {threshold_pct && (
            <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              Threshold: {threshold_pct}% of start-of-period market value
              {threshold_amount ? ` (cutoff ${formatCurrency(threshold_amount)})` : ''}
            </p>
          )}
        </div>

        {cash_flows.length > 0 ? (
          <div className="overflow-x-auto">
            {Object.entries(groupedFlows).map(([portfolioName, flows]) => (
              <div key={portfolioName} className="mb-6">
                {portfolioName !== 'default' && (
                  <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Portfolio gross of fees data flows for the {portfolio_code || portfolioName} portfolio: {portfolio_name || portfolioName}
                  </p>
                )}
                <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Type of Cash Flow</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Cash Flow Amount</th>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>To/From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flows.map((flow, index) => (
                      <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className="p-3">{flow.date || '-'}</td>
                        <td className={`p-3 font-medium ${flow.type_of_cash_flow === 'Contribution' ? 'text-green-600' : 'text-red-600'}`}>
                          {flow.type_of_cash_flow || '-'}
                        </td>
                        <td className={`p-3 text-right font-medium ${(flow.cash_flow_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {flow.cash_flow_amount < 0 ? '-' : ''}{formatCurrency(Math.abs(flow.cash_flow_amount || 0))}
                        </td>
                        <td className="p-3">{flow.to_from || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Total Cash Flows: {flowCount || cash_flows.length}
            </p>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No significant cash flows found for this period.
          </div>
        )}

        {/* GT grouping dimensions (additive backend `groupings` key). Portfolio
            and Asset Class apply the significance threshold; Security and
            Security Type Group are NET-ONLY per the GT's own notes (they list
            sub-threshold legs) - caption each section accordingly and never
            show the cutoff banner against a net-only section. Unavailable
            dimensions (no lake source) say so instead of fabricating. */}
        {Array.isArray(reportData.groupings) && reportData.groupings
          // The legacy flat table above IS the portfolio-dimension view; when
          // it rendered (non-empty cash_flows), skip the redundant Portfolio
          // grouping section so the same flows never print twice.
          .filter((section) => !(section.dimension === 'portfolio' && cash_flows.length > 0))
          .map((section, si) => (
          <div key={si} className="mt-4">
            <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {section.label || section.dimension}
              <span className={`ml-2 text-xs font-normal italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                {section.available === false
                  ? ''
                  : section.thresholded
                    ? '(significance threshold applied)'
                    : '(net flows - all legs listed, no threshold)'}
              </span>
            </p>
            {section.available === false ? (
              <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Unavailable{section.unavailable_reason ? ` - ${section.unavailable_reason}` : ''}.
              </p>
            ) : (section.rows || []).length === 0 ? (
              <p className={`text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                No flows in this grouping for the period.
              </p>
            ) : (
              <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                    <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Type of Cash Flow</th>
                    <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Cash Flow Amount</th>
                    <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>To/From</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((flow, fi) => (
                    <tr key={fi} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-2">{flow.date || '-'}</td>
                      <td className="p-2">{flow.type_of_cash_flow || '-'}</td>
                      <td className={`p-2 text-right ${(flow.cash_flow_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {flow.cash_flow_amount < 0 ? '-' : ''}{formatCurrency(Math.abs(flow.cash_flow_amount || 0))}
                      </td>
                      <td className="p-2">{flow.to_from || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}

        {/* Totals footer (additive backend `totals` key). */}
        {reportData.totals && reportData.totals.total_contributions != null && (
          <div className={`p-3 rounded border text-sm ${darkMode ? 'bg-[#141414] border-white/10 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
            <div className="flex justify-between"><span>Total Contributions</span><span>{formatCurrency(reportData.totals.total_contributions || 0)}</span></div>
            <div className="flex justify-between"><span>Total Withdrawals</span><span>{formatCurrency(reportData.totals.total_withdrawals || 0)}</span></div>
            <div className={`flex justify-between font-semibold border-t mt-1 pt-1 ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <span>Net Flows</span><span>{formatCurrency(reportData.totals.net_flows || 0)}</span>
            </div>
          </div>
        )}

        {/* PM Q20: matched same-day in-kind exchange pairs are excluded from
            the flows and totals as non-client flows; list them for
            transparency. */}
        {excluded_exchanges.length > 0 && (
          <div>
            <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Excluded In-Kind Exchanges (non-client flows)
            </h4>
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Leg</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>CUSIP</th>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Security</th>
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Value</th>
                </tr>
              </thead>
              <tbody>
                {excluded_exchanges.map((ex, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{ex.date || '-'}</td>
                    <td className="p-2">{(ex.cash_flow_amount || 0) >= 0 ? 'Deliver In' : 'Deliver Out'}</td>
                    <td className="p-2" data-export-text>{ex.cusip || '-'}</td>
                    <td className="p-2 break-words align-top" title={ex.security}>{ex.security || '-'}</td>
                    <td className="p-2 text-right">
                      {ex.cash_flow_amount < 0 ? '-' : ''}{formatCurrency(Math.abs(ex.cash_flow_amount || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderFundsInAndOut = () => {
    const {
      accounts = [], totals = {}, accountCount,
    } = reportData;
    const money = (v) => (v === null || v === undefined ? '-' : formatCurrency(v));

    return (
      <div className="space-y-4">
        <div className={`text-center p-4 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            Cash only. Securities transferred and management fees are listed
            separately and are not in the totals; internal movement between the
            account's own cash, margin and short ledgers is excluded.
          </p>
        </div>

        {accounts.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No funds moved in or out of this selection for the period.
          </div>
        ) : (
          accounts.map((account, ai) => (
            <div key={ai} className="mb-6">
              <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {account.account_number}
                {account.name ? ` - ${account.name}` : ''}
              </p>

              <div className="overflow-x-auto">
                <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Activity</th>
                      <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Counterparty</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>In</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Out</th>
                      <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Running Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(account.cash_flows || []).map((flow, fi) => (
                      <tr key={fi} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className="p-3">{flow.date || '-'}</td>
                        <td className={`p-3 font-medium ${
                          (flow.amount_in || 0) > 0
                            ? 'text-green-600'
                            : (flow.amount_out || 0) > 0 ? 'text-red-600' : ''
                        }`}
                        >
                          {flow.activity || '-'}
                        </td>
                        <td className="p-3 break-words align-top">{flow.counterparty || '-'}</td>
                        <td className="p-3 text-right">{flow.amount_in ? money(flow.amount_in) : '-'}</td>
                        <td className="p-3 text-right">{flow.amount_out ? money(flow.amount_out) : '-'}</td>
                        <td className={`p-3 text-right font-medium ${(flow.running_net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {money(flow.running_net)}
                        </td>
                      </tr>
                    ))}
                    {(account.cash_flows || []).length === 0 && (
                      <tr>
                        <td colSpan={6} className={`p-3 text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                          No cash moved in or out of this account for the period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className={`border-t font-semibold ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                      <td className="p-3" colSpan={3}>Account Total</td>
                      <td className="p-3 text-right">{money(account.totals?.funds_in || 0)}</td>
                      <td className="p-3 text-right">{money(account.totals?.funds_out || 0)}</td>
                      <td className={`p-3 text-right ${(account.totals?.net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {money(account.totals?.net || 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {(account.securities || []).length > 0 && (
                <div className="mt-4">
                  <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Securities Transferred In/Out
                    <span className={`ml-2 text-xs font-normal italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      (not included in the cash totals)
                    </span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      <thead>
                        <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Security</th>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>CUSIP/Symbol</th>
                          <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Quantity</th>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Direction</th>
                          <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Market Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.securities.map((sec, si) => (
                          <tr key={si} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                            <td className="p-2">{sec.date || '-'}</td>
                            <td className="p-2 break-words align-top">{sec.security || '-'}</td>
                            <td className="p-2" data-export-text>{sec.cusip || sec.symbol || '-'}</td>
                            <td className="p-2 text-right">
                              {(sec.quantity || 0).toLocaleString('en-US', { maximumFractionDigits: 4 })}
                            </td>
                            <td className={`p-2 ${sec.direction === 'in' ? 'text-green-600' : 'text-red-600'}`}>
                              {sec.direction === 'in' ? 'In' : 'Out'}
                            </td>
                            <td className="p-2 text-right">{money(sec.market_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`border-t font-semibold ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                          <td className="p-2" colSpan={5}>Transferred In</td>
                          <td className="p-2 text-right">{money(account.totals?.securities_in || 0)}</td>
                        </tr>
                        <tr className="font-semibold">
                          <td className="p-2" colSpan={5}>Transferred Out</td>
                          <td className="p-2 text-right">{money(account.totals?.securities_out || 0)}</td>
                        </tr>
                        <tr className={`border-t font-semibold ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                          <td className="p-2" colSpan={5}>Net Transferred</td>
                          <td className={`p-2 text-right ${(account.totals?.securities_net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {money(account.totals?.securities_net || 0)}
                          </td>
                        </tr>
                        {(account.totals?.securities_unpriced || 0) > 0 && (
                          <tr>
                            <td colSpan={6} className={`p-2 text-xs italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              {account.totals.securities_unpriced} unpriced leg(s) not in the total
                            </td>
                          </tr>
                        )}
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {(account.fees || []).length > 0 && (
                <div className="mt-4">
                  <p className={`text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Management Fees
                    <span className={`ml-2 text-xs font-normal italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      (not included in the cash totals)
                    </span>
                  </p>
                  <div className="overflow-x-auto">
                    <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      <thead>
                        <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Date</th>
                          <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Description</th>
                          <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {account.fees.map((fee, fi) => (
                          <tr key={fi} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                            <td className="p-2">{fee.date || '-'}</td>
                            <td className="p-2 break-words align-top">{fee.description || '-'}</td>
                            <td className="p-2 text-right text-red-600">{money(fee.amount || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className={`border-t font-semibold ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                          <td className="p-2" colSpan={2}>Total Management Fees</td>
                          <td className="p-2 text-right text-red-600">{money(account.totals?.fees || 0)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))
        )}

        {accounts.length > 0 && (
          <div className={`p-3 rounded border text-sm ${darkMode ? 'bg-[#141414] border-white/10 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
            <div className="flex justify-between"><span>Total Funds In</span><span>{money(totals.funds_in || 0)}</span></div>
            <div className="flex justify-between"><span>Total Funds Out</span><span>{money(totals.funds_out || 0)}</span></div>
            <div className={`flex justify-between font-semibold border-t mt-1 pt-1 ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <span>Net Funds</span><span>{money(totals.net || 0)}</span>
            </div>
            <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <div className="flex justify-between"><span>Securities Transferred In</span><span>{money(totals.securities_in || 0)}</span></div>
              <div className="flex justify-between"><span>Securities Transferred Out</span><span>{money(totals.securities_out || 0)}</span></div>
              <div className="flex justify-between font-semibold"><span>Net Securities Transferred</span><span>{money(totals.securities_net || 0)}</span></div>
              {(totals.securities_unpriced || 0) > 0 && (
                <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {totals.securities_unpriced} unpriced leg(s) not in the securities totals
                </div>
              )}
              <div className="flex justify-between font-semibold mt-1"><span>Total Management Fees</span><span>{money(totals.fees || 0)}</span></div>
              <div className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Not included in Net Funds.
              </div>
            </div>
            <div className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {accountCount || accounts.length} account(s)
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFixedIncomeAmortizationAccretion = () => {
    const { groups = [], total_amortization, total_accretion, net_adjustment, startDate, endDate } = reportData;
    const items = groups.flatMap((g) => g.rows || []);
    
    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Fixed Income Amortization and Accretion</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Period: {startDate} to {endDate}</p>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Amortization</p>
              <p className={`text-xl font-bold text-red-600`}>{formatCurrency(total_amortization || 0)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Total Accretion</p>
              <p className={`text-xl font-bold text-green-600`}>{formatCurrency(total_accretion || 0)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Net Adjustment</p>
              <p className={`text-xl font-bold ${net_adjustment >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(net_adjustment || 0)}</p>
            </div>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>CUSIP</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Original Cost</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Current Cost</th>
                  <th className={`p-3 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Adjustment</th>
                  <th className={`p-3 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Type</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <td className="p-3" data-export-text>{item.cusip || '-'}</td>
                    <td className="p-3 text-right">{formatCurrency(item.total_cost || 0)}</td>
                    <td className="p-3 text-right">{formatCurrency(item.adjusted_cost || 0)}</td>
                    <td className={`p-3 text-right ${item.amortization_ttd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(item.amortization_ttd || 0)}
                    </td>
                    <td className={`p-3 ${item.amortization_ttd > 0 ? 'text-green-600' : 'text-red-600'}`}>{item.amortization_ttd > 0 ? 'Accretion' : 'Amortization'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No amortization/accretion items found.
          </div>
        )}
      </div>
    );
  };

  const renderFixedIncomeCashProjection = () => {
    const {
      months = [],
      groups = [],
      grand_total = {},
      grand_total_total = 0,
      projection_months,
      asOfDate,
    } = reportData;
    const monthly = months.length ? grand_total_total / months.length : 0;
    const annual = monthly * 12;
    const monthCols = months.length + 2; // Security + one per month + Total

    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Fixed Income Cash Projection</h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>As of: {asOfDate} | Projection: {projection_months} months</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Projected Annual Income</p>
              <p className={`text-2xl font-bold text-green-600`}>{formatCurrency(annual || 0)}</p>
            </div>
            <div>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Projected Monthly Income</p>
              <p className={`text-2xl font-bold text-green-600`}>{formatCurrency(monthly || 0)}</p>
            </div>
          </div>
        </div>

        {groups.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className={`p-2 text-left font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Security</th>
                  {months.map((m) => (
                    <th key={m.key} className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>{m.label} {String(m.year).slice(2)}</th>
                  ))}
                  <th className={`p-2 text-right font-semibold ${darkMode ? 'bg-[#141414]' : 'bg-gray-100'}`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group, gi) => (
                  <React.Fragment key={`${group.name}-${gi}`}>
                    <tr>
                      <td colSpan={monthCols} className={`text-sm font-bold uppercase tracking-wide p-2 ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'}`}>{group.name}</td>
                    </tr>
                    {(group.rows || []).map((r, ri) => (
                      <tr key={`${r.cusip || r.symbol}-${ri}`} className={`border-b ${darkMode ? 'border-white/10 hover:bg-[#1a1a1a]' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <td className="p-2" data-export-text>{r.description || r.symbol || r.cusip || '-'}</td>
                        {months.map((m) => (
                          <td key={m.key} className="p-2 text-right">{formatCurrency(r.total_by_month?.[m.key] || 0)}</td>
                        ))}
                        <td className="p-2 text-right font-semibold">{formatCurrency(r.total || 0)}</td>
                      </tr>
                    ))}
                    <tr className={`font-semibold border-t-2 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-400 bg-gray-50'}`}>
                      <td className="p-2 text-left">{group.name} Total</td>
                      {months.map((m) => (
                        <td key={m.key} className="p-2 text-right">{formatCurrency(group.subtotals?.[m.key] || 0)}</td>
                      ))}
                      <td className="p-2 text-right">{formatCurrency(group.subtotal_total || 0)}</td>
                    </tr>
                  </React.Fragment>
                ))}
                <tr className={`font-bold border-t-4 ${darkMode ? 'border-white/10 bg-[#141414]' : 'border-gray-700 bg-gray-100'}`}>
                  <td className="p-3 text-left">GRAND TOTAL</td>
                  {months.map((m) => (
                    <td key={m.key} className="p-3 text-right">{formatCurrency(grand_total?.[m.key] || 0)}</td>
                  ))}
                  <td className="p-3 text-right">{formatCurrency(grand_total_total || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            No projections available.
          </div>
        )}
      </div>
    );
  };

  // Performance by Asset Class (PDF page 41)
  // Per-asset-class start/end MV, dollar change, and % change. Accepts
  // both `performance_by_class` (canonical backend) and `asset_classes`
  // (legacy) shapes.
  // Shared WS-D period-attribution table (K4/N4), the GT shape behind Asset
  // Reconciliation, By-Security and By-Asset-Class period reports. Per asset-
  // class section: begin MV+accrued, purchases/additions, withdrawals/sales,
  // realized (on-MV), unrealized, interest/dividends, end MV+accrued; a cash/
  // margin section with blank realized/unrealized; section subtotals with
  // Average Capital + period IRR; and a TOTAL row splitting external flows.
  // `securityRows`: true lists every security under each section (By-Security /
  // Asset Recon); false renders one row per asset class (By-Asset-Class).
  const renderPeriodAttribution = (period, { securityRows = true } = {}) => {
    if (!period || !Array.isArray(period.sections)) return null;
    // Null renders blank (the APX cash line and any degraded realized/unrealized
    // print empty, not "-"); a real 0 still prints $0.00. A null IRR prints
    // "NA" — APX's own word on a non-positive capital base.
    const m = (v) => (v == null ? '' : formatCurrency(v));
    const pct = (v) => (v == null ? 'NA' : `${Number(v).toFixed(2)}%`);
    // APX prints each section's SUBTOTAL market value DIRTY (accrued-inclusive)
    // with the change-in-accrued inside Interest/Dividends, and Accrued
    // Interest as its own final row. The payload keeps clean/accrued split
    // (sections sum to the clean total), so the fold happens at render.
    const dirty = (o) => ({
      ...o,
      begin_mv: o.begin_mv == null ? null : o.begin_mv + (o.begin_accrued || 0),
      end_mv: o.end_mv == null ? null : o.end_mv + (o.end_accrued || 0),
      interest_dividends: o.interest_dividends == null ? null
        : o.interest_dividends + (o.change_in_accrued || 0),
    });
    const hasAccrued = (sub) => Boolean(
      (sub.begin_accrued || 0) || (sub.end_accrued || 0)
      || (sub.change_in_accrued || 0));
    const total = period.total || {};
    const sections = [...(period.sections || [])];
    if (period.cash_section) sections.push(period.cash_section);
    const NCOL = 10;
    const rowBorder = darkMode ? 'border-white/10' : 'border-gray-200';
    const subBorder = darkMode ? 'border-white/10' : 'border-gray-300';
    const dataCells = (o) => (
      <>
        <td className="p-2 text-right">{m(o.begin_mv)}</td>
        <td className="p-2 text-right">{m(o.purchases)}</td>
        <td className="p-2 text-right">{m(o.sales)}</td>
        <td className="p-2 text-right">{m(o.realized)}</td>
        <td className="p-2 text-right">{m(o.unrealized)}</td>
        <td className="p-2 text-right">{m(o.interest_dividends)}</td>
        <td className="p-2 text-right">{m(o.end_mv)}</td>
      </>
    );
    return (
      <div className="overflow-x-auto">
        <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
          <thead>
            <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
              <th className="p-2 text-left font-semibold">Security</th>
              <th className="p-2 text-right font-semibold">Beginning<br/>Market Value</th>
              <th className="p-2 text-right font-semibold">Purchases/<br/>Additions</th>
              <th className="p-2 text-right font-semibold">Withdrawals/<br/>Sales</th>
              <th className="p-2 text-right font-semibold">Realized</th>
              <th className="p-2 text-right font-semibold">Unrealized</th>
              <th className="p-2 text-right font-semibold">Interest/<br/>Dividends</th>
              <th className="p-2 text-right font-semibold">Ending<br/>Market Value</th>
              <th className="p-2 text-right font-semibold">Average<br/>Capital</th>
              <th className="p-2 text-right font-semibold">IRR</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((sec, si) => {
              const sub = sec.subtotals || {};
              return (
                <React.Fragment key={si}>
                  {securityRows && (
                    <tr>
                      <td colSpan={NCOL} className={`pt-3 pb-1 font-semibold italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{sec.label}</td>
                    </tr>
                  )}
                  {securityRows && (sec.rows || []).map((r, ri) => (
                    <tr key={ri} className={`border-b ${rowBorder}`}>
                      <td className="p-2 pl-4" data-export-text>{r.name || r.cusip || '-'}</td>
                      {dataCells(r)}
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right"></td>
                    </tr>
                  ))}
                  {securityRows && hasAccrued(sub) && (
                    /* APX prints Accrued Interest as the section's LAST row:
                       begin accrued | change (in the Interest/Dividends
                       column) | end accrued (mutely VRM: [amount] | [amount] |
                       [amount]). No accrued row under COMMON STOCK or CASH —
                       hasAccrued gates it. */
                    <tr className={`border-b ${rowBorder}`}>
                      <td className="p-2 pl-4 italic">Accrued Interest</td>
                      <td className="p-2 text-right">{m(sub.begin_accrued)}</td>
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right">{m(sub.change_in_accrued)}</td>
                      <td className="p-2 text-right">{m(sub.end_accrued)}</td>
                      <td className="p-2 text-right"></td>
                      <td className="p-2 text-right"></td>
                    </tr>
                  )}
                  <tr className={`font-semibold border-t ${subBorder}`}>
                    <td className="p-2">{securityRows ? `${sec.label} Subtotal` : sec.label}</td>
                    {dataCells(dirty(sub))}
                    <td className="p-2 text-right">{m(sub.average_capital)}</td>
                    <td className="p-2 text-right">{pct(sub.irr_pct)}</td>
                  </tr>
                </React.Fragment>
              );
            })}
            {/* TOTAL row: external additions/withdrawals sit in the flow
                columns. Rendered DIRTY like the subtotals — APX's TOTAL
                PORTFOLIO begin is the accrued-inclusive [amount], not the
                clean [amount] (the payload keeps the clean/accrued split). */}
            <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
              <td className="p-2">TOTAL</td>
              <td className="p-2 text-right">{m(dirty(total).begin_mv)}</td>
              <td className="p-2 text-right">{m(total.additions)}</td>
              <td className="p-2 text-right">{m(total.withdrawals)}</td>
              <td className="p-2 text-right">{m(total.realized)}</td>
              <td className="p-2 text-right">{m(total.unrealized)}</td>
              <td className="p-2 text-right">{m(dirty(total).interest_dividends)}</td>
              <td className="p-2 text-right">{m(dirty(total).end_mv)}</td>
              <td className="p-2 text-right">{m(total.average_capital)}</td>
              <td className="p-2 text-right" title={total.irr_period_label || undefined}>{pct(total.irr_pct)}</td>
            </tr>
          </tbody>
        </table>
        {(total.external_additions != null || total.external_withdrawals != null) && (
          <div className={`mt-2 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            External Additions {formatCurrency(total.external_additions || 0)}
            {' · '}External Withdrawals {formatCurrency(total.external_withdrawals || 0)}
            {' · '}Net {formatCurrency(total.external_net || 0)}
          </div>
        )}
      </div>
    );
  };

  const renderPerformanceByAssetClass = () => {
    // §B6a (user decision 2026-07-20): the GT transposed measure-matrix is
    // the primary render — columns are the asset classes + Total Portfolio,
    // rows are the measures (clean MV with separate Accrued rows, boundary-
    // netted Purchases/Sales, Transfers, Realized/Unrealized, CASH-basis
    // Interest/Dividends with the explicit Change-in-Accrued row, Average
    // Capital, Total Gain, the IRR row with the APX NA quirk).
    const matrix = reportData.by_class_matrix;
    if (matrix && Array.isArray(matrix.columns) && matrix.cells) {
      const money = (v) => (v === null || v === undefined ? '' : formatAmount(v, moneyDigits ?? 0));
      const irr = (v) => (v === null || v === undefined ? 'NA' : `${Number(v).toFixed(2)}%`);
      const cols = matrix.columns;
      const labels = matrix.labels || {};
      const measures = [
        ['begin_mv', `Market Value on ${matrix.from_date || reportData.start_date || ''}`, money],
        ['begin_accrued', 'Accrued Interest', money],
        ['purchases', 'Purchases/Contributions', money],
        ['sales', 'Sales/Withdrawals', money],
        ['transfers_in', 'Transfers In', money],
        ['transfers_out', 'Transfers Out', money],
        ['realized', 'Realized Gains', money],
        ['unrealized', 'Unrealized Gains', money],
        ['interest_income', 'Interest Income', money],
        ['dividend_income', 'Dividend Income', money],
        ['change_in_accrued', 'Change in Accrued Interest', money],
        ['end_mv', `Market Value on ${matrix.to_date || reportData.end_date || ''}`, money],
        ['end_accrued', 'Accrued Interest', money],
        ['average_capital', 'Average Capital Base', money],
        ['total_gain', 'Total Gain before Fees', money],
        // The one measure the return-basis control moves: the class partition's
        // time-weighted return by default, the money-weighted IRR on the toggle.
        twrView
          ? ['twr_pct', matrix.twr_label || 'TWR', irr]
          : ['irr_pct', matrix.irr_label || 'IRR', irr],
      ];
      return (
        <div>
          {returnBasisSelect && (
            <div className="report-sticky-toolbar mb-3 flex justify-end" data-export-skip data-print-skip>
              {returnBasisSelect}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className="p-2 text-left font-semibold">Time Period</th>
                  {cols.map((c) => (
                    <th key={c} className="p-2 text-right font-semibold">{labels[c] || c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {measures.map(([key, label, fmt]) => (
                  <tr key={`${key}-${label}`} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{label}</td>
                    {cols.map((c) => (
                      <td key={c} className="p-2 text-right">{fmt((matrix.cells[c] || {})[key])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }
    // Pre-wave payloads: the shared period-attribution table, then the legacy
    // start/end/change table.
    const period = reportData.period_attribution;
    if (period && Array.isArray(period.sections)) {
      return <div className="space-y-4">{renderPeriodAttribution(period, { securityRows: false })}</div>;
    }
    const list = reportData.performance_by_class || reportData.asset_classes || [];
    const startTotal = list.reduce((s, x) => s + (x.start_value || 0), 0);
    const endTotal = list.reduce((s, x) => s + (x.end_value || 0), 0);
    const totalChange = endTotal - startTotal;
    const total = reportData.total || null;
    // The TOTAL % Change is the backend's account-linked window return, NEVER
    // a naive (end-start)/start value ratio: flows make that ratio nonsense
    // (it printed +713.31% against Advent's 0.83% on a window with a -$5M
    // withdrawal). Null = uncomputable; show '-', never a fabricated number.
    const totalPct = total != null ? total.pct_change : null;
    const fmtPct = (v) => (v == null ? '-' : `${v.toFixed(2)}%`);
    const fmtMoney = (v) => (v == null ? '-' : formatCurrency(v));
    const endSuspect = total != null && total.reliability === 'end_mv_suspect_pending_settlements';
    return (
      <div className="space-y-4">
        {list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className="p-2 text-left font-semibold">Asset Class</th>
                  <th className="p-2 text-right font-semibold">Start Value</th>
                  <th className="p-2 text-right font-semibold">End Value</th>
                  <th className="p-2 text-right font-semibold">Change</th>
                  <th className="p-2 text-right font-semibold">Average Capital</th>
                  <th className="p-2 text-right font-semibold">% Change</th>
                  <th className="p-2 text-right font-semibold">IRR</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{item.asset_class || '-'}</td>
                    <td className="p-2 text-right">{formatCurrency(item.start_value || 0)}</td>
                    <td className="p-2 text-right">{formatCurrency(item.end_value || 0)}</td>
                    <td className="p-2 text-right">{formatCurrency(item.change || 0)}</td>
                    {/* Per-class money-weighted metrics are None (dash) until the
                        class-member data supports the TOTAL row's basis - never a
                        mixed-basis number that cannot foot to the TOTAL. */}
                    <td className="p-2 text-right">{fmtMoney(item.average_capital)}</td>
                    {/* Null pct_change = uncomputable account/class return; show
                        '-' not a fake 0.00% (mirrors performance_by_security). */}
                    <td className="p-2 text-right">{fmtPct(item.pct_change)}</td>
                    <td className="p-2 text-right">{fmtPct(item.irr)}</td>
                  </tr>
                ))}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                  <td className="p-2">TOTAL</td>
                  <td className="p-2 text-right">{formatCurrency(startTotal)}</td>
                  <td className="p-2 text-right">{formatCurrency(endTotal)}</td>
                  <td className="p-2 text-right">{formatCurrency(totalChange)}</td>
                  <td className="p-2 text-right">{fmtMoney(total?.average_capital)}</td>
                  <td className="p-2 text-right">{fmtPct(totalPct)}</td>
                  <td className="p-2 text-right" title={total?.irr_period_label || undefined}>{fmtPct(total?.irr)}</td>
                </tr>
              </tbody>
            </table>
            {endSuspect && (
              <div className={`mt-2 text-xs ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                End value provisional - pending trade settlements; IRR and total gain are withheld until the
                lot data settles.
              </div>
            )}
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No performance data available.</div>
        )}
      </div>
    );
  };

  // Performance by Security (PDF page 42-43)
  // Per-security start/end MV, dollar change, and % change. Accepts both
  // `performance_by_security` (canonical backend) and `securities` (legacy)
  // shapes.
  const renderPerformanceBySecurity = () => {
    // §B6b (user decision 2026-07-20): the GT "Performance by Security
    // Including Accrued Interest" shape — STG sections, Symbol + coupon/
    // maturity subtitle, per-row Average Capital / Realized / Unrealized /
    // EARNED Interest / IRR / as-of MV / Pct Assets, section subtotals and a
    // TOTAL PORTFOLIO footer. Cash rows blank realized/unrealized; the cash
    // subtotal IRR prints NA (the APX negative-base quirk).
    const gt = reportData.by_security_gt;
    if (gt && Array.isArray(gt.sections)) {
      const money = (v) => (v === null || v === undefined ? '' : formatAmount(v, moneyDigits ?? 0));
      const irr = (v) => (v === null || v === undefined ? 'NA' : Number(v).toFixed(2));
      const pctA = (v) => (v === null || v === undefined ? '-' : Number(v).toFixed(2));
      const asOf = reportData.end_date || reportData.as_of_date || '';
      // DCF method prints the FROM-DATE Market Value in place of Average Capital
      // (GT header "03-16-26 Market Value", begin MV per row/section/TOTAL); the
      // Average Capital Base method keeps the ACB column.
      const isDcf = reportData.irr_method === 'dcf';
      const fromDate = reportData.start_date || '';
      const capCell = (o) => money(isDcf ? o.begin_mv : o.average_capital);
      const sub = (label, s) => (
        <tr className={`font-semibold border-t ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
          <td className="p-2" colSpan={2}>{label}</td>
          <td className="p-2 text-right">{money(s.end_mv)}</td>
          <td className="p-2 text-right">{pctA(s.pct_assets)}</td>
          <td className="p-2 text-right">{capCell(s)}</td>
          <td className="p-2 text-right">{money(s.interest_dividends)}</td>
          <td className="p-2 text-right">{money(s.unrealized)}</td>
          <td className="p-2 text-right">{money(s.realized)}</td>
          <td className="p-2 text-right">{money(s.total_gain)}</td>
          <td className="p-2 text-right">{irr(s.irr_pct)}</td>
        </tr>
      );
      return (
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className="p-2 text-left font-semibold">Symbol</th>
                <th className="p-2 text-left font-semibold">Security</th>
                <th className="p-2 text-right font-semibold">{asOf}<br/>Market Value</th>
                <th className="p-2 text-right font-semibold">Pct.<br/>Assets</th>
                <th className="p-2 text-right font-semibold">
                  {isDcf ? <>{fromDate}<br/>Market Value</> : <>Average<br/>Capital</>}
                </th>
                <th className="p-2 text-right font-semibold">Income Received<br/>(Interest Dividends)</th>
                <th className="p-2 text-right font-semibold">Unrealized<br/>Gains</th>
                <th className="p-2 text-right font-semibold">Realized<br/>Gains</th>
                <th className="p-2 text-right font-semibold">Total<br/>Return</th>
                <th className="p-2 text-right font-semibold">IRR<br/>-Fees</th>
              </tr>
            </thead>
            <tbody>
              {gt.sections.map((sec, si) => (
                <React.Fragment key={si}>
                  <tr><td colSpan={10} className={`pt-3 pb-1 font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{sec.label}</td></tr>
                  {(sec.rows || []).map((r, ri) => (
                    <tr key={ri} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-2 align-top" data-export-text>{r.cusip || r.symbol || '-'}</td>
                      <td className="p-2" data-export-text title={r.name}>
                        {stripCouponDue(r.name)}
                        {r.coupon_pct != null && r.maturity_date ? (
                          <DueLine coupon={r.coupon_pct} due={r.maturity_date} darkMode={darkMode} />
                        ) : null}
                      </td>
                      <td className="p-2 text-right">{money(r.end_mv)}</td>
                      <td className="p-2 text-right">{pctA(r.pct_assets)}</td>
                      <td className="p-2 text-right">{capCell(r)}</td>
                      <td className="p-2 text-right">{money(r.interest_dividends)}</td>
                      <td className="p-2 text-right">{money(r.unrealized)}</td>
                      <td className="p-2 text-right">{money(r.realized)}</td>
                      <td className="p-2 text-right">{money(r.total_gain)}</td>
                      <td className="p-2 text-right">{irr(r.irr_pct)}</td>
                    </tr>
                  ))}
                  {sub('', sec.subtotals || {})}
                </React.Fragment>
              ))}
              {sub('TOTAL PORTFOLIO', gt.total || {})}
            </tbody>
          </table>
        </div>
      );
    }
    // The GT block is built on every call; a payload without it is malformed.
    return (
      <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No performance data available.</div>
    );
  };

  // Individual Account Summary (PDF page 40)
  // The Banyan template lists one row per portfolio in the composite with
  // account number, account name, market value, and holdings count.
  // INDIVIDUAL ACCOUNT SUMMARY (GROSS OF FEES) — a date-range gains/losses
  // report: one row per portfolio + a backend-supplied TOTAL row, each with
  // begin/end market value and accrued (shown separately), additions/
  // withdrawals, realized/unrealized gains, interest/dividends, total gain and
  // a money-weighted IRR. Mirrors the APX 10-column layout.
  const renderIndividualAccountSummary = () => {
    const list = reportData.account_summaries || reportData.accounts || [];
    // MM-DD-YY column labels for the two market-value anchors.
    const shortDate = (iso) => {
      if (!iso || typeof iso !== 'string' || iso.length < 10) return '';
      const [y, m, d] = iso.slice(0, 10).split('-');
      return `${m}-${d}-${y.slice(2)}`;
    };
    const startLbl = shortDate(reportData.start_date);
    const endLbl = shortDate(reportData.end_date);
    const num = (v) => (v == null ? '-' : formatCurrency(v));
    const border = darkMode ? 'border-white/10' : 'border-gray-300';
    return (
      <div className="space-y-4">
        {returnBasisSelect && (
          <div className="report-sticky-toolbar flex justify-end" data-export-skip data-print-skip>
            {returnBasisSelect}
          </div>
        )}
        {list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm whitespace-nowrap ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${border}`}>
                  <th className="p-2 text-left font-semibold" rowSpan={2}>Portfolio</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Market Value{startLbl ? ` ${startLbl}` : ''}</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Accrued Interest</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Additions/ Withdrawals</th>
                  <th className="p-2 text-center font-semibold border-b border-current" colSpan={2}>Gains/Losses</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Interest/ Dividends</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Market Value{endLbl ? ` ${endLbl}` : ''}</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Accrued Interest</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>Total Gain</th>
                  <th className="p-2 text-right font-semibold" rowSpan={2}>{twrView ? 'TWR' : 'IRR'}</th>
                </tr>
                <tr className={`border-b ${border}`}>
                  <th className="p-2 text-right font-semibold">Realized</th>
                  <th className="p-2 text-right font-semibold">Unrealized</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => {
                  const isTotal = !!item.is_total;
                  const rowCls = isTotal
                    ? `font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`
                    : `border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`;
                  return (
                    <tr key={index} className={rowCls}>
                      <td className="p-2" data-export-text>{isTotal ? 'TOTAL' : (item.account_name || item.account_number || '-')}</td>
                      <td className="p-2 text-right">{num(item.market_value_start)}</td>
                      <td className="p-2 text-right">{num(item.accrued_start)}</td>
                      <td className="p-2 text-right">{num(item.additions_withdrawals)}</td>
                      <td className="p-2 text-right">{num(item.realized)}</td>
                      <td className="p-2 text-right">{num(item.unrealized)}</td>
                      <td className="p-2 text-right">{num(item.interest_dividends)}</td>
                      <td className="p-2 text-right">{num(item.market_value_end)}</td>
                      <td className="p-2 text-right">{num(item.accrued_end)}</td>
                      <td className="p-2 text-right">{num(item.total_gain)}</td>
                      {/* The return column the basis control moves. TOTAL row
                          prints blank (not '-') when null - a composite's
                          money-weighted IRR is undefined and a single account's
                          pooled TWR would only repeat the row; APX leaves the
                          cell empty rather than dashing it. */}
                      <td className="p-2 text-right">
                        {(() => {
                          const ret = twrView ? item.twr : item.irr;
                          return ret == null ? (isTotal ? '' : '-') : ret.toFixed(2);
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No data available.</div>
        )}
      </div>
    );
  };

  // Portfolio Performance Cross Reference (PDF page 50)
  // Lists each portfolio with market value + return percentage. Accepts
  // both the new `portfolios[]` list shape and the legacy
  // `portfolio_data` dict shape from the backend.
  const renderPortfolioPerformanceCrossReference = () => {
    // K5/N8 multi-horizon matrix: one row per portfolio (APX account CODE, never
    // the internal pid) x MTD / QTD / YTD / L12M / ITD / annualized ITD, plus
    // benchmark index rows. There is NO market-value column - this is a pure
    // performance cross-reference. The new backend contract carries `horizons`.
    const isMatrix = Array.isArray(reportData.horizons) && Array.isArray(reportData.portfolios);
    if (isMatrix) {
      const portfolios = reportData.portfolios || [];
      const indexes = reportData.indexes || [];
      const pct = (v) => (v == null ? '-' : `${Number(v).toFixed(2)}%`);
      // §C6 (user decision 2026-07-20): the GT column set — Inception Date |
      // MTD | QTD | YTD | Latest 1 Year | Ann Latest 3 Yr | Ann Latest 5 Yr |
      // Ann ITD | Since <as-of> — PLUS the kept cumulative ITD column. A None
      // trailing horizon prints "-" (anchor predates inception); the
      // degenerate Since-as-of window is definitionally 0.00 (the GT account
      // cell is an APX glyph defect; its index row prints 0.00).
      const asOf = reportData.as_of_date || reportData.asOfDate || '';
      const horizonCols = [
        ['month_to_date', 'Month To Date'],
        ['quarter_to_date', 'Quarter To Date'],
        ['year_to_date', 'Year To Date'],
        ['last_12m', 'Latest 1 Year'],
        ['ann_3y', 'Annualized Latest 3 Years'],
        ['ann_5y', 'Annualized Latest 5 Years'],
        ['inception_to_date_annualized', 'Annualized Inception To Date'],
        ['inception_to_date', 'Inception To Date (Cumulative)'],
      ];
      const rowCells = (item) => (
        <>
          {horizonCols.map(([key]) => (
            <td key={key} className="p-2 text-right">{pct(item[key])}</td>
          ))}
          <td className="p-2 text-right">0.00%</td>
        </>
      );
      return (
        <div className="space-y-4">
          {portfolios.length > 0 ? (
            <div className="overflow-x-auto">
              <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                    <th className="p-2 text-left font-semibold">Account</th>
                    <th className="p-2 text-left font-semibold">Portfolio Name</th>
                    <th className="p-2 text-left font-semibold">Inception Date</th>
                    {horizonCols.map(([key, label]) => (
                      <th key={key} className="p-2 text-right font-semibold">{label}</th>
                    ))}
                    <th className="p-2 text-right font-semibold">{`Since ${asOf}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolios.map((item, index) => (
                    <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-2" data-export-text>{item.account_code || '-'}</td>
                      <td className="p-2" data-export-text>{item.portfolio_name || '-'}</td>
                      <td className="p-2">{item.inception_date || '-'}</td>
                      {rowCells(item)}
                    </tr>
                  ))}
                  {visibleIndexes(indexes).length > 0 && (
                    <>
                      <tr className={`border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                        <td className="p-2 font-semibold" colSpan={4 + horizonCols.length}>Index</td>
                      </tr>
                      {visibleIndexes(indexes).map((idx, i) => (
                        <tr key={`idx-${i}`} className={`group border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <td className="p-2" colSpan={3}><span className="inline-flex items-center">{idx.label || idx.name || 'Index'}{indexHideBtn(idx)}</span></td>
                          {rowCells(idx)}
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No data available.</div>
          )}
        </div>
      );
    }

    // Legacy fallback (pre-matrix payload): portfolio_data dict / flat portfolios.
    let list = reportData.portfolios || [];
    if (!list.length && reportData.portfolio_data) {
      list = Object.entries(reportData.portfolio_data).map(([code, val]) => ({
        portfolio_code: code,
        portfolio_name: val.portfolio_name || code,
        market_value: val.total_value || 0,
        // Preserve null (no perf data for this portfolio) so we can show '-'
        // rather than a fabricated 0.00%.
        return_pct: val.return_pct,
      }));
    }
    return (
      <div className="space-y-4">
        {list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className="p-2 text-left font-semibold">Portfolio Code</th>
                  <th className="p-2 text-left font-semibold">Portfolio Name</th>
                  <th className="p-2 text-right font-semibold">Market Value</th>
                  <th className="p-2 text-right font-semibold">Return % (YTD)</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2" data-export-text>{item.portfolio_code || '-'}</td>
                    <td className="p-2" data-export-text>{item.portfolio_name || '-'}</td>
                    <td className="p-2 text-right">{formatCurrency(item.market_value || 0)}</td>
                    <td className="p-2 text-right">{item.return_pct == null ? '-' : `${item.return_pct.toFixed(2)}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No data available.</div>
        )}
      </div>
    );
  };

  // Performance History (PDF page 45) — single-row "From X To Y" header
  // line with start value, end value, dollar gain, and return %.
  // Accepts both `performance_history` (canonical backend) and `periods`
  // (legacy) field names.
  const renderPerformanceHistory = () => {
    const list = reportData.performance_history || reportData.periods || [];
    // Benchmark index columns between Return % and Cumulative TWR. The ordered
    // column set is `index_columns` (backend, display-order sorted); older
    // payloads fall back to the whole-window `indexes` names. Per-period cells
    // read item.indexes[name]; a period dashes where the monthly feed is
    // incomplete (post-2026-05-29, until the separate ICE index feed lands).
    const indexes = Array.isArray(reportData.indexes) ? reportData.indexes : [];
    const idxCols = (Array.isArray(reportData.index_columns) && reportData.index_columns.length
      ? reportData.index_columns
      : indexes.map((ix) => ({ name: ix.name, label: ix.label || ix.name })));
    const visIdxCols = idxCols.filter(
      (c) => !hiddenIndexes.has(indexKey(c)) && isIndexAllowed(c),
    );
    // Whole-window (Date to Date) benchmark value per index, for the total row.
    const dtdByName = {};
    indexes.forEach((ix) => {
      dtdByName[ix.name] = ix.date_to_date != null ? ix.date_to_date : ix.itd;
    });
    const anchorDate = reportData.anchor_date || reportData.start_date || null;
    const fmtCum = (v) => (v == null || Number.isNaN(v) ? '-' : Number(v).toFixed(2));
    const fmtPct = (v) => (v == null || Number.isNaN(Number(v)) ? '-' : `${Number(v).toFixed(2)}%`);
    // Whole-window (Date to Date) account return = last cumulative index - 100.
    const lastCum = [...list].reverse().find((r) => r.cumulative_twr != null)?.cumulative_twr;
    const dtdAccount = lastCum == null ? null : lastCum - 100;
    const hasDtd = dtdAccount != null || visIdxCols.length > 0;
    return (
      <div className="space-y-4">
        {list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className="p-2 text-left font-semibold">Period</th>
                  <th className="p-2 text-right font-semibold">Start Value</th>
                  <th className="p-2 text-right font-semibold">End Value</th>
                  <th className="p-2 text-right font-semibold">Gain</th>
                  <th className="p-2 text-right font-semibold">Return %</th>
                  {visIdxCols.map((c) => (
                    <th key={c.name} className="group p-2 text-right font-semibold" title={c.label}>
                      <span className="inline-flex items-center justify-end">{c.label}{indexHideBtn(c)}</span>
                    </th>
                  ))}
                  <th className="p-2 text-right font-semibold">Cumulative TWR<br/>Basis = 100</th>
                </tr>
              </thead>
              <tbody>
                {anchorDate && (
                  // Basis-100 anchor row (APX prints the from-date at 100.00).
                  <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{anchorDate}</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">-</td>
                    {visIdxCols.map((c) => (<td key={c.name} className="p-2 text-right">-</td>))}
                    <td className="p-2 text-right">100.00</td>
                  </tr>
                )}
                {list.map((item, index) => {
                  // Backend returns null start/end MV when no daily snapshot exists
                  // at a period boundary (months before the 2026-02-06 data anchor).
                  // Show '-' and skip the derived Gain/Return math for those rows
                  // instead of coercing to a misleading $0.00 / 0.00%.
                  const startV = item.start_value;
                  const endV = item.end_value;
                  const hasEndpoints = startV != null && endV != null;
                  // Prefer the backend's flow-netted investment gain; fall back to
                  // the raw MV delta only when it is absent and both endpoints exist.
                  const gain = item.gain ?? (hasEndpoints ? endV - startV : null);
                  const ret =
                    item.return ??
                    item.return_pct ??
                    (hasEndpoints && startV > 0 ? ((endV - startV) / startV) * 100 : null);
                  // Per-period benchmark returns keyed by index name (dash where
                  // the monthly feed has no value for this period).
                  const perIdx = (item.indexes && typeof item.indexes === 'object'
                    && !Array.isArray(item.indexes)) ? item.indexes : {};
                  return (
                    <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                      <td className="p-2">{item.period || '-'}</td>
                      <td className="p-2 text-right">{startV == null ? '-' : formatCurrency(startV)}</td>
                      <td className="p-2 text-right">{endV == null ? '-' : formatCurrency(endV)}</td>
                      <td className="p-2 text-right">{gain == null ? '-' : formatCurrency(gain)}</td>
                      <td className="p-2 text-right">{ret == null ? '-' : `${Number(ret).toFixed(2)}%`}</td>
                      {visIdxCols.map((c) => (
                        <td key={c.name} className="p-2 text-right">{fmtPct(perIdx[c.name])}</td>
                      ))}
                      <td className="p-2 text-right">{fmtCum(item.cumulative_twr)}</td>
                    </tr>
                  );
                })}
                {hasDtd && (
                  // Date-to-Date whole-window totals in the same columns: the
                  // account window return under Return %, each benchmark's window
                  // value in its own column (the values formerly rendered as
                  // separate italic rows below the table).
                  <tr className={`border-t-2 font-semibold ${darkMode ? 'border-white/40' : 'border-gray-400'}`}>
                    <td className="p-2">Date to Date{anchorDate && reportData.end_date ? ` (${anchorDate} to ${reportData.end_date})` : ''}</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">-</td>
                    <td className="p-2 text-right">{fmtPct(dtdAccount)}</td>
                    {visIdxCols.map((c) => (
                      <td key={c.name} className="p-2 text-right">{fmtPct(dtdByName[c.name])}</td>
                    ))}
                    <td className="p-2 text-right">-</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No performance history available.</div>
        )}
      </div>
    );
  };

  // Performance History by Asset Class (PDF pages 51-52)
  // Per-period Percent Return + Cumulative TWR grid; columns vary with
  // whatever asset classes the backend returns under `asset_classes` and
  // whichever benchmark indices it includes under `indexes`.
  const renderPerformanceHistoryByAssetClass = () => {
    const {
      periods = [],
      asset_classes = ['Fixed Income', 'Equity', 'Cash and Equivalents', 'Other', 'Total'],
      indexes = [],
    } = reportData;
    const formatPct = (v) => (v === null || v === undefined || Number.isNaN(v) ? 'NA' : Number(v).toFixed(2));
    // Single-class fallback: the backend rendered a lone Fixed Income column
    // equal to Total (the account is a single-class book with no per-class split
    // in the lake). Show a soft note, not the alarming "unavailable" banner.
    const singleClass = reportData.single_class === true;

    return (
      <div className="space-y-4">
        {singleClass ? (
          <div
            className={`text-sm px-3 py-2 rounded ${
              darkMode
                ? 'bg-[#141414] text-gray-300 border border-white/10'
                : 'bg-gray-50 text-gray-700 border border-gray-200'
            }`}
            role="status"
          >
            No per-asset-class breakdown is available for this period; the Fixed Income row equals the Account total.
          </div>
        ) : reportData.breakdown_complete === false && (
          <div
            className={`text-sm px-3 py-2 rounded ${
              darkMode
                ? 'bg-amber-950/50 text-amber-200 border border-amber-800/60'
                : 'bg-amber-50 text-amber-900 border border-amber-200'
            }`}
            role="status"
          >
            Asset-class breakdown unavailable for this portfolio; only the Total series is shown.
          </div>
        )}
        {periods.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No performance history available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold align-bottom" rowSpan={2}>Time Period</th>
                  <th className="p-2 text-center font-semibold" colSpan={asset_classes.length}>Percent Return Per Period</th>
                  <th className="p-2 text-center font-semibold" colSpan={asset_classes.length}>Cumulative TWR<br/>Basis = 100</th>
                </tr>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  {asset_classes.map((ac) => (
                    <th key={`p-${ac}`} className="p-2 text-right font-semibold">{ac}</th>
                  ))}
                  {asset_classes.map((ac) => (
                    <th key={`c-${ac}`} className="p-2 text-right font-semibold">{ac}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{p.label || `${p.start || ''} to ${p.end || ''}`}</td>
                    {asset_classes.map((ac) => (
                      <td key={`pp-${ac}`} className="p-2 text-right">{formatPct(p.percent_return?.[ac])}</td>
                    ))}
                    {asset_classes.map((ac) => (
                      <td key={`pc-${ac}`} className="p-2 text-right">{formatPct(p.cumulative_twr?.[ac])}</td>
                    ))}
                  </tr>
                ))}
                {reportData.date_to_date && (
                  /* APX's Date-to-Date footer: the window cumulative per
                     column (last cumulative - 100). */
                  <tr className={`font-semibold border-t ${darkMode ? 'border-white/20' : 'border-gray-400'}`}>
                    <td className="p-2">Date to Date</td>
                    {asset_classes.map((ac) => (
                      <td key={`dd-${ac}`} className="p-2 text-right">{formatPct(reportData.date_to_date[ac])}</td>
                    ))}
                    <td colSpan={asset_classes.length} />
                  </tr>
                )}
                {visibleIndexes(indexes).length > 0 && (
                  <>
                    <tr>
                      <td colSpan={1 + asset_classes.length * 2} className={`p-2 italic font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Index</td>
                    </tr>
                    {visibleIndexes(indexes).map((idx, i) => (
                      <tr key={`idx-${i}`} className={`group italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <td className="p-2"><span className="inline-flex items-center">{idx.label || idx.name || 'Index'}{indexHideBtn(idx)}</span></td>
                        {asset_classes.map((ac) => (
                          <td key={`idx-pp-${ac}`} className="p-2 text-right">-</td>
                        ))}
                        {asset_classes.map((ac) => (
                          <td key={`idx-pc-${ac}`} className="p-2 text-right">-</td>
                        ))}
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
            {indexes.length > 0 && (
              <p className={`text-xs italic mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Benchmark index values are shown as &lsquo;-&rsquo; here; index returns are reported by horizon, not per asset-class period.
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Performance History by Sector (PDF page 53)
  // One row per industry sector with MTD / QTD / YTD / Last 12 Months /
  // Inception To Date columns; benchmark indices render below in italic.
  // Accepts the backend's legacy `performance_by_sector` shape (no MTD/QTD
  // breakdown — just start_value/end_value/pct_change) by remapping it
  // into the PDF columns; the unspecified columns render as `?`.
  const renderPerformanceHistoryBySector = () => {
    let { sectors = [], indexes = [] } = reportData;
    if (!sectors.length && Array.isArray(reportData.performance_by_sector)) {
      sectors = reportData.performance_by_sector.map(s => ({
        name: s.sector,
        ytd: s.pct_change,
        itd: s.pct_change,
      }));
    }
    const fmt = (v) => (v === null || v === undefined || Number.isNaN(v) ? '-' : Number(v).toFixed(2));
    return (
      <div className="space-y-4">
        {reportData.breakdown_complete === false && (
          <div
            className={`text-sm px-3 py-2 rounded ${
              darkMode
                ? 'bg-amber-950/50 text-amber-200 border border-amber-800/60'
                : 'bg-amber-50 text-amber-900 border border-amber-200'
            }`}
            role="status"
          >
            Asset-class breakdown unavailable for this portfolio; only the Total series is shown.
          </div>
        )}
        {sectors.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No sector performance data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold">Industry Sector</th>
                  <th className="p-2 text-right font-semibold">Month<br/>To Date</th>
                  <th className="p-2 text-right font-semibold">Quarter<br/>To Date</th>
                  <th className="p-2 text-right font-semibold">Year<br/>To Date</th>
                  <th className="p-2 text-right font-semibold">Last 12<br/>Months</th>
                  <th className="p-2 text-right font-semibold">Inception<br/>To Date</th>
                </tr>
              </thead>
              <tbody>
                {sectors.map((s, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} ${s.is_total ? 'font-bold' : ''}`}>
                    <td className="p-2">{s.name || '-'}</td>
                    <td className="p-2 text-right">{fmt(s.mtd)}</td>
                    <td className="p-2 text-right">{fmt(s.qtd)}</td>
                    <td className="p-2 text-right">{fmt(s.ytd)}</td>
                    <td className="p-2 text-right">{fmt(s.last_12m)}</td>
                    <td className="p-2 text-right">{fmt(s.itd)}</td>
                  </tr>
                ))}
                {visibleIndexes(indexes).length > 0 && (
                  <>
                    <tr><td colSpan={6} className={`pt-3 pb-1 italic font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Index</td></tr>
                    {visibleIndexes(indexes).map((idx, i) => (
                      <tr key={`idx-${i}`} className={`group italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <td className="p-2"><span className="inline-flex items-center">{idx.label || idx.name || 'Index'}{indexHideBtn(idx)}</span></td>
                        <td className="p-2 text-right">{fmt(idx.mtd)}</td>
                        <td className="p-2 text-right">{fmt(idx.qtd)}</td>
                        <td className="p-2 text-right">{fmt(idx.ytd)}</td>
                        <td className="p-2 text-right">{fmt(idx.last_12m)}</td>
                        <td className="p-2 text-right">{fmt(idx.itd)}</td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Performance History Detail by Portfolio (PDF pages 54-55)
  // One row per sub-period with Average Capital, period Gain, IRR, and
  // Cumulative TWR. (§1f D1: the per-row "Retrieving Portfolio from Gross of
  // Fees ..." header was an APX retrieval LOG line, not report content —
  // replicating it printed debug text 84x into the client PDF. Stripped.)
  const renderPerformanceHistoryDetail = () => {
    const { periods = [], attribute = 'ptss totport' } = reportData;
    const fmtCap = (v) => (v === null || v === undefined ? '-' : formatAmount(v, moneyDigits ?? 0));
    const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(v) ? '-' : Number(v).toFixed(2));
    // The backend keeps cumulative_twr as a base-100 index (shared with the
    // by-asset-class report). APX's Detail report prints the percent return
    // (index - 100): 0.02, -0.05, ..., 1.11 - so subtract the basis at render.
    const fmtCumPct = (v) => fmtPct(v === null || v === undefined ? null : v - 100);
    return (
      <div className="space-y-4">
        {periods.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No detail performance history available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold">From Date</th>
                  <th className="p-2 text-left font-semibold">Through Date</th>
                  <th className="p-2 text-left font-semibold">Currency and Attribute</th>
                  <th className="p-2 text-right font-semibold">Average Capital</th>
                  <th className="p-2 text-right font-semibold">Gain</th>
                  <th className="p-2 text-right font-semibold">IRR</th>
                  <th className="p-2 text-right font-semibold">Cumulative TWR</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((p, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{p.start || '-'}</td>
                    <td className="p-2">{p.end || '-'}</td>
                    <td className="p-2">{p.attribute || attribute}</td>
                    <td className="p-2 text-right">{fmtCap(p.average_capital)}</td>
                    <td className={`p-2 text-right ${(p.gain || 0) >= 0 ? '' : 'text-red-600'}`}>{fmtCap(p.gain)}</td>
                    <td className="p-2 text-right">{fmtPct(p.irr)}</td>
                    <td className="p-2 text-right">{fmtCumPct(p.cumulative_twr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Composite Dispersion (PDF page 56)
  // Summary box (Composite Average + Standard Deviation) over a per-account
  // performance table. Accepts both `account_performances` (canonical) and
  // `accounts` (legacy) names.
  // Asset Reconciliation (PDF page 57)
  // One row per holding (account, CUSIP, security, market value) plus a
  // grand-total at the bottom. Accepts `reconciliation_items` (canonical
  // backend name) and `items` (legacy) shapes.
  const renderAssetReconciliation = () => {
    const list = reportData.reconciliation_items || reportData.items || [];
    const total = reportData.total_market_value
      ?? list.reduce((s, x) => s + (x.market_value || x.amount || 0), 0);
    // GT period report (K4/N4/F17): the shared period-attribution table is the
    // headline; the per-holding end-date listing (with security names) is a
    // secondary "Holdings Detail" section below it.
    const period = reportData.period_attribution;
    const holdingsDetail = (
      list.length > 0 ? (
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className="p-2 text-left font-semibold">Account</th>
                <th className="p-2 text-left font-semibold">CUSIP</th>
                <th className="p-2 text-left font-semibold">Security</th>
                <th className="p-2 text-right font-semibold">Market Value</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item, index) => (
                <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-2" data-export-text>{item.account_number || '-'}</td>
                  <td className="p-2" data-export-text>{item.cusip || '-'}</td>
                  <td className="p-2" data-export-text>{item.security_name || item.fund_mnemonic || item.security || item.item || '-'}</td>
                  <td className="p-2 text-right">{formatCurrency(item.market_value || item.amount || 0)}</td>
                </tr>
              ))}
              <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                <td className="p-2" colSpan={3}>TOTAL</td>
                <td className="p-2 text-right">{formatCurrency(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null
    );
    if (period && Array.isArray(period.sections)) {
      return (
        <div className="space-y-6">
          {renderPeriodAttribution(period, { securityRows: true })}
          {holdingsDetail && (
            <div className="space-y-2">
              <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Holdings Detail (as of {reportData.end_date || reportData.as_of_date})</h4>
              {holdingsDetail}
            </div>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {list.length > 0 ? (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                  <th className="p-2 text-left font-semibold">Account</th>
                  <th className="p-2 text-left font-semibold">CUSIP</th>
                  <th className="p-2 text-left font-semibold">Security</th>
                  <th className="p-2 text-right font-semibold">Market Value</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => (
                  <tr key={index} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2" data-export-text>{item.account_number || '-'}</td>
                    <td className="p-2" data-export-text>{item.cusip || '-'}</td>
                    <td className="p-2" data-export-text>{item.fund_mnemonic || item.security || item.item || '-'}</td>
                    <td className="p-2 text-right">{formatCurrency(item.market_value || item.amount || 0)}</td>
                  </tr>
                ))}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                  <td className="p-2" colSpan={3}>TOTAL</td>
                  <td className="p-2 text-right">{formatCurrency(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No reconciliation data available.</div>
        )}
      </div>
    );
  };

  // Performance Reconciliation by Asset Class (PDF page 68)
  // Row-per-asset-class table reconciling Beginning MV through Ending MV
  // with all flow / gain / income buckets, plus Average Capital Base and
  // IRR for each class and a Total row at the bottom.
  //
  // The backend currently exposes a flat reconciliation (start_value,
  // end_value, net_cash_flows, calculated_performance) for single-account
  // composites; if that's what we get we render a single TOTAL row so the
  // report at least surfaces the data instead of "No data available".
  const renderPerformanceReconciliation = () => {
    // §D8: the GT "Performance Reconciliation by …" family — one 11-column
    // shape (recon frame payload: rows + total) over five grouping variants.
    if (Array.isArray(reportData.rows) && reportData.total) {
      const { rows, total, group_label: groupLabel = 'Group',
              method_label: methodLabel = 'Average Capital Base Method' } = reportData;
      const money = (v) => (v === null || v === undefined ? '' : formatAmount(v, moneyDigits ?? 0));
      const irr = (v) => (v === null || v === undefined ? 'NA' : Number(v).toFixed(2));
      const cells = (r) => (
        <>
          <td className="p-2 text-right">{money(r.begin_mv)}</td>
          <td className="p-2 text-right">{money(r.purchases)}</td>
          <td className="p-2 text-right">{money(r.sales)}</td>
          <td className="p-2 text-right">{money(r.realized)}</td>
          <td className="p-2 text-right">{money(r.unrealized)}</td>
          <td className="p-2 text-right">{money(r.interest_dividends)}</td>
          <td className="p-2 text-right">{money(r.total_gain)}</td>
          <td className="p-2 text-right">{money(r.end_mv)}</td>
          <td className="p-2 text-right">{money(r.average_capital)}</td>
          <td className="p-2 text-right">{irr(r.irr_pct)}</td>
        </>
      );
      return (
        <div className="space-y-4">
          <p className={`text-center text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{methodLabel}</p>
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold">{groupLabel}</th>
                  <th className="p-2 text-right font-semibold">Beginning<br/>Market Value</th>
                  <th className="p-2 text-right font-semibold">Additions<br/>Purchases</th>
                  <th className="p-2 text-right font-semibold">Withdrawals<br/>Sales</th>
                  <th className="p-2 text-right font-semibold">Realized<br/>Gains</th>
                  <th className="p-2 text-right font-semibold">Unrealized<br/>Gains</th>
                  <th className="p-2 text-right font-semibold">Interest<br/>Dividends</th>
                  <th className="p-2 text-right font-semibold">Total Gain<br/>Before Fees</th>
                  <th className="p-2 text-right font-semibold">Ending<br/>Market Value</th>
                  <th className="p-2 text-right font-semibold">Average<br/>Capital Base</th>
                  <th className="p-2 text-right font-semibold">IRR</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{r.label || '-'}</td>
                    {cells(r)}
                  </tr>
                ))}
                <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                  <td className="p-2">Total</td>
                  {cells(total)}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    const { method = 'Average Capital Base Method' } = reportData;
    let { asset_classes = [] } = reportData;
    if (!asset_classes.length && (reportData.start_value !== undefined || reportData.end_value !== undefined)) {
      asset_classes = [{
        name: 'TOTAL PORTFOLIO',
        beginning_market_value: reportData.start_value,
        ending_market_value: reportData.end_value,
        additions_purchases: Math.max(reportData.net_cash_flows || 0, 0),
        withdrawals_sales: Math.min(reportData.net_cash_flows || 0, 0),
        total_gain_before_fees: reportData.calculated_performance,
        is_total: true,
      }];
    }
    const fmtMoney = (v) => (v === null || v === undefined ? '-' : formatAmount(v, moneyDigits ?? 0));
    const fmtPct = (v) => (v === null || v === undefined || Number.isNaN(v) ? 'NA' : Number(v).toFixed(2));
    return (
      <div className="space-y-4">
        <p className={`text-center text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{method}</p>
        {asset_classes.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No reconciliation data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold">Asset Class</th>
                  <th className="p-2 text-right font-semibold">Beginning<br/>Market Value</th>
                  <th className="p-2 text-right font-semibold">Additions/<br/>Purchases</th>
                  <th className="p-2 text-right font-semibold">Withdrawals/<br/>Sales</th>
                  <th className="p-2 text-right font-semibold">Realized<br/>Gains</th>
                  <th className="p-2 text-right font-semibold">Unrealized<br/>Gains</th>
                  <th className="p-2 text-right font-semibold">Interest/<br/>Dividends</th>
                  <th className="p-2 text-right font-semibold">Total Gain<br/>Before Fees</th>
                  <th className="p-2 text-right font-semibold">Ending<br/>Market Value</th>
                  <th className="p-2 text-right font-semibold">Average<br/>Capital Base</th>
                  <th className="p-2 text-right font-semibold">IRR</th>
                </tr>
              </thead>
              <tbody>
                {asset_classes.map((ac, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} ${ac.is_total ? 'font-bold' : ''}`}>
                    <td className="p-2">{ac.name || '-'}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.beginning_market_value)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.additions_purchases)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.withdrawals_sales)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.realized_gains)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.unrealized_gains)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.interest_dividends)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.total_gain_before_fees)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.ending_market_value)}</td>
                    <td className="p-2 text-right">{fmtMoney(ac.average_capital_base)}</td>
                    <td className="p-2 text-right">{fmtPct(ac.irr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ACB Performance Proof (APX "ACB Performance Proof" PDF).
  // Backend generate_acb_proof returns per-portfolio daily rows walking the
  // PERFORMANCE series: beginning value, flows, Average Capital Base, gain,
  // ending value and the daily return gain/ACB (gross) plus the net-of-fee
  // return, with a per-day identity residual (gain = ending - ACB). Rows and
  // returns are null when ACB is 0 - render those as '-' (null != real 0).
  const renderACBProof = () => {
    const {
      acb_summary,
      portfolios = [],
      proof_identity,
      max_identity_residual,
      start_date,
      as_of_date,
    } = reportData;

    const fmtPct = (v) =>
      v === null || v === undefined || Number.isNaN(Number(v)) ? '-' : `${Number(v).toFixed(4)}%`;
    const fmtPct2 = (v) =>
      v === null || v === undefined || Number.isNaN(Number(v)) ? '-' : `${Number(v).toFixed(2)}%`;
    const fmtCur = (v) =>
      v === null || v === undefined || Number.isNaN(Number(v)) ? '-' : formatCurrency(Number(v));

    if (!portfolios.length && !acb_summary) {
      return (
        <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No ACB data available.</div>
      );
    }

    // Headline: the money-weighted Average-Capital-Base contributions table
    // (deliverable 2/K13) - every external flow weighted by days-remaining/total-
    // days, then Beginning MV, ACB, Total Gain and IRR. The daily ledger below is
    // the SECONDARY proof section.
    const renderAcbSummary = (s) => (
      <div className={`p-4 rounded-lg space-y-4 ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
        <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Average Capital Base Proof</h3>
        <div className="overflow-x-auto">
          <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            <thead>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                <th className="p-2 text-left font-semibold">Date</th>
                <th className="p-2 text-right font-semibold">Contribution/<br/>Withdrawal</th>
                <th className="p-2 text-right font-semibold">Days<br/>Remaining</th>
                <th className="p-2 text-right font-semibold">Total<br/>Days</th>
                <th className="p-2 text-right font-semibold">Weighted<br/>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'} font-semibold`}>
                <td className="p-2">Beginning Market Value</td>
                <td className="p-2 text-right">{fmtCur(s.beginning_market_value)}</td>
                <td className="p-2 text-right">-</td>
                <td className="p-2 text-right">-</td>
                <td className="p-2 text-right">{fmtCur(s.beginning_market_value)}</td>
              </tr>
              {(s.contributions_withdrawals || []).map((c, i) => (
                <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                  <td className="p-2">{c.date}</td>
                  <td className="p-2 text-right">{fmtCur(c.amount)}</td>
                  <td className="p-2 text-right">{c.days_remaining ?? '-'}</td>
                  <td className="p-2 text-right">{c.total_days ?? '-'}</td>
                  <td className="p-2 text-right">{fmtCur(c.weighted_amount)}</td>
                </tr>
              ))}
              <tr className={`font-bold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                <td className="p-2">Average Capital Base</td>
                <td className="p-2 text-right"></td>
                <td className="p-2 text-right"></td>
                <td className="p-2 text-right"></td>
                <td className="p-2 text-right">{fmtCur(s.average_capital_base)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Ending Market Value: <span className="font-semibold">{fmtCur(s.ending_market_value)}</span></span>
          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Total Gain: <span className="font-semibold">{fmtCur(s.total_gain)}</span></span>
          <span className={darkMode ? 'text-gray-300' : 'text-gray-700'} title={s.irr_period_label || undefined}>IRR: <span className="font-semibold">{fmtPct2(s.irr_pct)}</span></span>
        </div>
      </div>
    );

    return (
      <div className="space-y-6">
        {acb_summary && renderAcbSummary(acb_summary)}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#141414]' : 'bg-gray-50'}`}>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>ACB Performance Proof (Daily)</h3>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Period: {start_date} to {as_of_date}</p>
          {proof_identity && (
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Identity: {proof_identity} (max residual {fmtCur(max_identity_residual)})</p>
          )}
        </div>

        {portfolios.map((p, pi) => {
          const s = p.period_summary || {};
          return (
            <div key={`${p.portfolio_id}-${pi}`} className="space-y-2">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                {/* Account label from the backend (heading/code) — never the
                    internal pid (§1f D4b, N8 family). */}
                <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{p.portfolio_label || 'Portfolio'}</span>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Linked Return: {fmtPct(s.linked_return_pct)}</span>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Net: {fmtPct(s.linked_return_net_pct)}</span>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Total Gain: {fmtCur(s.total_gain)}</span>
                <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>Days: {s.days ?? '-'}</span>
              </div>
              <div className="overflow-x-auto">
                <table className={`w-full border-collapse text-xs ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                  <thead>
                    <tr className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-300'}`}>
                      <th className="p-2 text-left font-semibold">Date</th>
                      <th className="p-2 text-right font-semibold">Beginning Value</th>
                      <th className="p-2 text-right font-semibold">Net Additions</th>
                      <th className="p-2 text-right font-semibold">Net Transfers</th>
                      <th className="p-2 text-right font-semibold">ACB</th>
                      <th className="p-2 text-right font-semibold">Gain</th>
                      <th className="p-2 text-right font-semibold">Ending Value</th>
                      <th className="p-2 text-right font-semibold">Internal Fee</th>
                      <th className="p-2 text-right font-semibold">Return %</th>
                      <th className="p-2 text-right font-semibold">Net Return %</th>
                      <th className="p-2 text-right font-semibold">Residual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(p.daily_rows || []).map((r, ri) => (
                      <tr key={`${r.date}-${ri}`} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                        <td className="p-2">{r.date || '-'}</td>
                        <td className="p-2 text-right">{fmtCur(r.beginning_value)}</td>
                        <td className="p-2 text-right">{fmtCur(r.net_additions)}</td>
                        <td className="p-2 text-right">{fmtCur(r.net_transfers)}</td>
                        <td className="p-2 text-right">{fmtCur(r.average_capital_base)}</td>
                        {/* Backup rows fold the next-day ACB basis reset (posted
                            margin interest) back into the period gain: prefer
                            gain_normalized when the backend supplies it (WS-A). */}
                        {(() => {
                          const g = r.gain_normalized ?? r.gain;
                          return <td className={`p-2 text-right ${g >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtCur(g)}</td>;
                        })()}
                        <td className="p-2 text-right">{fmtCur(r.ending_value)}</td>
                        <td className="p-2 text-right">{fmtCur(r.internal_fee)}</td>
                        <td className="p-2 text-right">{fmtPct(r.daily_return_pct)}</td>
                        <td className="p-2 text-right">{fmtPct(r.daily_return_net_pct)}</td>
                        <td className="p-2 text-right">{fmtCur(r.identity_residual)}</td>
                      </tr>
                    ))}
                    <tr className={`font-semibold border-t-2 ${darkMode ? 'border-white/10' : 'border-gray-400'}`}>
                      <td className="p-2">TOTAL</td>
                      <td className="p-2 text-right">{fmtCur(s.start_value)}</td>
                      <td className="p-2 text-right">{fmtCur(s.net_additions)}</td>
                      <td className="p-2 text-right">{fmtCur(s.net_transfers)}</td>
                      <td className="p-2 text-right">-</td>
                      <td className="p-2 text-right">{fmtCur(s.total_gain)}</td>
                      <td className="p-2 text-right">{fmtCur(s.end_value)}</td>
                      <td className="p-2 text-right">{fmtCur(s.internal_fees)}</td>
                      <td className="p-2 text-right">{fmtPct(s.linked_return_pct)}</td>
                      <td className="p-2 text-right">{fmtPct(s.linked_return_net_pct)}</td>
                      <td className="p-2 text-right">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Historical Market Value (PDF page 72)
  // Two-column table: Time Period (Month YYYY) | Market Value. The PDF
  // prints values "(in Thousands)"; we surface that via `scale_label`
  // (or default to nothing if the backend hasn't scaled). Accepts both
  // `points` (preferred, supports an arbitrary number of months) and
  // `historical_values` (legacy backend) shapes.
  const renderHistoricalMarketValue = () => {
    const list = reportData.points || reportData.historical_values || [];
    const { scale_label } = reportData;
    const fmtLabel = (raw) => {
      if (!raw) return '-';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    };
    const fmt = (v) => (v === null || v === undefined ? '-' : formatAmount(v, curDigits));
    return (
      <div className="space-y-4">
        {scale_label && (
          <p className={`text-center text-xs italic ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{scale_label}</p>
        )}
        {list.length === 0 ? (
          <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>No historical market value data available.</div>
        ) : (
          <div className="max-w-md mx-auto">
            <table className={`w-full border-collapse text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              <thead>
                <tr className={darkMode ? 'border-b border-gray-700' : 'border-b border-gray-300'}>
                  <th className="p-2 text-left font-semibold">Time Period</th>
                  <th className="p-2 text-right font-semibold">Market Value</th>
                </tr>
              </thead>
              <tbody>
                {list.map((p, i) => (
                  <tr key={i} className={`border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
                    <td className="p-2">{p.label || p.month || fmtLabel(p.date)}</td>
                    <td className="p-2 text-right">{fmt(p.market_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderWithHeader = (body) => {
    const header = reportData?.header;
    const fallbackDate =
      reportData?.asOfDate ||
      reportData?.as_of_date ||
      reportData?.endDate ||
      reportData?.end_date ||
      reportData?.date;
    const printedHeader = renderReportHeader(header, fallbackDate);
    return (
      // `report-body` scopes the top-alignment rule for report table cells
      // (index.css): every cell in a row must START on the same top line even
      // when the Description cell wraps. This wrapper is a DESCENDANT of the
      // paged-shell root (whose classes paginate.js strips), so the scope
      // survives into the paginated/print clones too.
      <div className="space-y-4 report-body">
        {/* The firm's own mark (Manual > Report Branding), top LEFT. It is a
            NORMAL flow child placed BEFORE the running header on purpose: the
            header band is lifted into every page's margin box during pagination
            (paginate.js), so a mark nested inside it would repeat on every page.
            Left in the flow it lands on page 1 only, which is the spec. It
            therefore must stay visible and unskipped - no data-export-skip /
            data-print-skip, no display:none, no position: running(). */}
        <ReportBrandMark brand={brand} />
        {printedHeader ? (
          // Overlay the logo in the top-right corner of the header band instead
          // of giving it its own row above the header. This keeps the centered
          // title at the very top of the page (no empty band above it) while the
          // logo still sits top-right, letterhead-style.
          //
          // `report-running-header` marks this band as the repeated per-page report
          // header: during pagination the RepeatReportHeaders handler (paginate.js)
          // clones it into the top margin of EVERY page and CSS hides this inline copy
          // (see pagedReportCss.js). On screen (un-paginated) it renders normally as
          // the first block, so the live/source/export views are unchanged.
          <div className="relative report-running-header">
            {showLogo && (
              <div className="absolute right-0 top-0">
                <ReportLogo />
              </div>
            )}
            {printedHeader}
          </div>
        ) : (
          showLogo && <ReportLogo />
        )}
        {body}
      </div>
    );
  };

  const renderReportContent = () => {
    let body;
    switch (reportType) {
      case 'security_cross_reference':
        body = renderSecurityCrossReference(); break;
      case 'portfolio_summary':
        body = renderPortfolioSummary(); break;
      case 'portfolio_performance_summary':
        body = renderPortfolioPerformanceSummary(); break;
      case 'asset_allocation':
        body = renderAssetAllocation(); break;
      case 'fixed_income_analysis':
        body = renderFixedIncomeAnalysis(); break;
      case 'fixed_income_distribution':
        body = renderFixedIncomeDistribution(); break;
      case 'fixed_income_holdings_by_maturity':
        body = renderFixedIncomeHoldingsByMaturity(); break;
      case 'transaction_history':
      case 'transaction_summary':
      case 'master_transaction_summary':
        body = renderTransactionHistory(); break;
      case 'performance_summary_compound':
        body = renderPerformanceSummaryCompound(); break;
      case 'bond_transactions':
      case 'purchase_and_sale':
      case 'trade_blotter':
        body = renderBondTransactions(); break;
      case 'realized_gains_losses':
        body = renderRealizedGainsLosses(); break;
      case 'unrealized_gains_losses':
        body = renderUnrealizedGainsLosses(); break;
      case 'date_to_date_gain_loss':
        body = renderDateToDateGainLoss(); break;
      case 'change_in_portfolio_value':
        body = renderChangeInPortfolioValue(); break;
      case 'income_and_expenses':
        body = renderIncomeAndExpenses(); break;
      case 'cash_ledger_running_balance':
        body = renderCashLedger(); break;
      case 'assets_under_management':
        body = renderAssetsUnderManagementList(); break;
      case 'assets_under_management_with_port_code':
        body = renderAssetsUnderManagementWithPortCode(); break;
      case 'cash_balances':
        body = renderCashBalances(); break;
      case 'billing':
        body = renderBilling();
        break;
      case 'significant_contributions_withdrawals':
        body = renderSignificantContributionsWithdrawals(); break;
      case 'fixed_income_amortization_accretion':
        body = renderFixedIncomeAmortizationAccretion(); break;
      case 'fixed_income_cash_projection':
        body = renderFixedIncomeCashProjection(); break;
      case 'transfer_transactions':
        body = renderTransactionHistory(); break;
      case 'funds_in_and_out':
        body = renderFundsInAndOut(); break;
      case 'portfolio_register':
        body = renderPortfolioRegister(); break;
      case 'performance_by_asset_class':
        body = renderPerformanceByAssetClass(); break;
      case 'performance_by_security':
        body = renderPerformanceBySecurity(); break;
      case 'individual_account_summary':
        body = renderIndividualAccountSummary(); break;
      case 'portfolio_performance_cross_reference':
        body = renderPortfolioPerformanceCrossReference(); break;
      case 'performance_history':
        body = renderPerformanceHistory(); break;
      case 'performance_history_by_asset_class':
        body = renderPerformanceHistoryByAssetClass(); break;
      case 'performance_history_by_sector':
        body = renderPerformanceHistoryBySector(); break;
      case 'performance_history_detail':
        body = renderPerformanceHistoryDetail(); break;
      case 'asset_reconciliation':
        body = renderAssetReconciliation(); break;
      case 'performance_reconciliation':
        body = renderPerformanceReconciliation(); break;
      case 'acb_proof':
        body = renderACBProof(); break;
      case 'historical_market_value':
        body = renderHistoricalMarketValue(); break;
      default:
        body = renderGenericReport();
    }
    return renderWithHeader(body);
  };

  // Embedded mode: render just the report body (no modal overlay). Used to
  // render each member's report inside the group "@" sequential shell.
  if (embedded) {
    return renderReportContent();
  }

  // Chromeless mode: render the report body WITHOUT the modal overlay/title/footer.
  // The floating ReportWindow supplies its own window chrome (titlebar with
  // export/print/min/max/close) and the group member tabs. Every report — single,
  // "@@" aggregate, and "@" sequential — renders through ONE stacked path: N member
  // sections, each producing its own frozen-header report body (see renderWithHeader).
  // N=1 (single / aggregate) => one section, no divider. ContinuousReportViewer
  // freezes each section's .report-running-header + <thead> and swaps the frozen
  // header per portfolio on scroll; the tabs (in ReportWindow) scroll between them.
  //
  // Each member section carries data-member-index (scroll-spy / field attribution /
  // per-member expand) and data-export-sheet (group export names one sheet per
  // portfolio; ignored for the single case, which exports per-table as before).
  if (chromeless) {
    const cReports = Array.isArray(reportData.__reports) ? reportData.__reports : null;
    const cFlags = {
      show_summary_information: reportData.show_summary_information,
      show_distribution_by_moody_rating: reportData.show_distribution_by_moody_rating,
      show_distribution_by_sp_rating: reportData.show_distribution_by_sp_rating,
      show_distribution_by_maturity: reportData.show_distribution_by_maturity,
    };
    const members = cReports || [{ account_header: reportData.header, data: reportData }];
    return (
      <div className="report-members">
        {members.map((m, i) => {
          const label = (m.account_header?.account_name) || `Account ${i + 1}`;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <div className="report-member-divider" data-export-skip data-print-skip>{label}</div>
              )}
              <section className="report-member" data-member-index={i} data-export-sheet={label}>
                {/* Presence of `error`, not its truthiness: the backend sets the
                    key only on a failed member, and it always sets data: null
                    alongside. An empty string must still take this branch. */}
                {m.error != null ? (
                  /* This member's generation failed; the rest of the group was
                     still delivered (see the group runner in api_reports.py).
                     Rendering `data: null` through the normal per-type renderers
                     would produce a page of zeros and dashes - a report that
                     LOOKS like this account holds nothing, which is worse than
                     the 500 this replaced. Say what happened instead. */
                  <div className="report-member-error" style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#b91c1c' }}>
                      {m.error || 'This account could not be generated.'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
                      The other accounts in this group are unaffected. Generate the
                      report again to retry this account.
                    </div>
                  </div>
                ) : (
                <ReportDisplayModal
                  embedded
                  isOpen
                  onClose={onClose}
                  reportData={{ ...(m.data || {}), ...cFlags }}
                  reportType={reportType}
                  reportName={reportName}
                  showLogo={showLogo}
                  // First member only, so the stacked on-screen document carries
                  // the firm mark exactly where the print does: page 1.
                  brand={i === 0 ? brand : null}
                  moneyPrecision={moneyPrecision}
                  expandedRows={(expandedByMember && expandedByMember[i]) || EMPTY_EXPANDED}
                  onToggleRow={NOOP}
                  onToggleAll={NOOP}
                  yocBasis={yocBasis}
                  onYocBasisChange={setYocBasis}
                  showMarketYield={showMarketYield}
                  onShowMarketYieldChange={setShowMarketYield}
                  includeCashYield={includeCashYield}
                  onIncludeCashYieldChange={setIncludeCashYield}
                  // Every display basis is forwarded, not just the yield pair:
                  // a member that keeps its own local state renders the DEFAULT
                  // basis in the print/export clones, which is what the on-screen
                  // toggle was flipped away from.
                  feeBasis={feeBasis}
                  onFeeBasisChange={setFeeBasis}
                  accruedBasis={accruedBasis}
                  onAccruedBasisChange={setAccruedBasis}
                  holdingsBasis={holdingsBasis}
                  onHoldingsBasisChange={setHoldingsBasis}
                  securityFilter={securityFilter}
                  onSecurityFilterChange={setSecurityFilter}
                  returnBasis={returnBasis}
                  onReturnBasisChange={setReturnBasis}
                  hiddenIndexes={hiddenIndexes}
                  onToggleIndex={onToggleIndex}
                  selectedIndexNames={selectedIndexNames}
                />
                )}
              </section>
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  // Group "@" sequential envelope: a string of per-account reports. Render a
  // master header + a member switcher, and reuse this same modal (embedded) to
  // render the selected member's report through the existing per-type renderers.
  const groupReports = Array.isArray(reportData.__reports) ? reportData.__reports : null;
  const groupInfo = reportData.__group || null;
  const groupNotice = reportData.__notice || null;
  const optionFlags = {
    show_summary_information: reportData.show_summary_information,
    show_distribution_by_moody_rating: reportData.show_distribution_by_moody_rating,
    show_distribution_by_sp_rating: reportData.show_distribution_by_sp_rating,
    show_distribution_by_maturity: reportData.show_distribution_by_maturity,
  };

  if (groupReports) {
    const idx = Math.min(activeMember, groupReports.length - 1);
    const active = groupReports[idx] || {};
    const activeData = { ...(active.data || {}), ...optionFlags };
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className={`w-full max-w-6xl max-h-[90vh] rounded-lg shadow-xl ${reportModalPanelClass(darkMode)} flex flex-col`}>
          <div className={`flex items-center justify-between p-6 border-b ${reportModalBorderClass(darkMode)}`}>
            <div>
              <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{reportName || 'Report Results'}</h2>
              <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {`${groupSigil(groupInfo?.consolidated)}${groupInfo?.codename || ''}`}{groupInfo?.name ? ` — ${groupInfo.name}` : ''}
                {` · ${groupReports.length} accounts (per-account)`}
              </p>
            </div>
            <button onClick={onClose} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-[#141414] text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}><X className="w-6 h-6" /></button>
          </div>
          {groupNotice && (
            <div className="px-6 py-2 text-sm bg-amber-100 text-amber-800">{groupNotice}</div>
          )}
          {/* Member switcher */}
          <div className={`flex gap-1 px-4 py-2 overflow-x-auto border-b ${reportModalBorderClass(darkMode)}`}>
            {groupReports.map((b, i) => (
              <button
                key={i}
                onClick={() => setActiveMember(i)}
                className={`px-3 py-1 rounded text-sm whitespace-nowrap ${i === idx ? 'bg-teal-600 text-white' : darkMode ? 'bg-[#141414] text-gray-300' : 'bg-gray-100 text-gray-700'}`}
              >
                {(b.account_header?.account_name) || `Account ${i + 1}`}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            {active.error ? (
              <div className="report-member-error p-8 text-center">
                <div className="font-semibold mb-1">
                  {active.account_header?.account_name || 'This account'}
                </div>
                <div className="text-sm text-red-600">
                  {active.error || 'This account could not be generated.'}
                </div>
              </div>
            ) : (
            <ReportDisplayModal
              embedded
              isOpen
              onClose={onClose}
              reportData={activeData}
              reportType={reportType}
              reportName={reportName}
              showLogo={showLogo}
              moneyPrecision={moneyPrecision}
              yocBasis={yocBasis}
              onYocBasisChange={setYocBasis}
              showMarketYield={showMarketYield}
              onShowMarketYieldChange={setShowMarketYield}
              includeCashYield={includeCashYield}
              onIncludeCashYieldChange={setIncludeCashYield}
              feeBasis={feeBasis}
              onFeeBasisChange={setFeeBasis}
              accruedBasis={accruedBasis}
              onAccruedBasisChange={setAccruedBasis}
              holdingsBasis={holdingsBasis}
              onHoldingsBasisChange={setHoldingsBasis}
              securityFilter={securityFilter}
              onSecurityFilterChange={setSecurityFilter}
              returnBasis={returnBasis}
              onReturnBasisChange={setReturnBasis}
              hiddenIndexes={hiddenIndexes}
              onToggleIndex={onToggleIndex}
              selectedIndexNames={selectedIndexNames}
            />
            )}
          </div>
          <div className={`p-6 border-t ${reportModalBorderClass(darkMode)} flex justify-end`}>
            <button onClick={onClose} className={`px-6 py-2 rounded-lg font-medium ${darkMode ? 'bg-[#141414] text-white hover:bg-[#1a1a1a]' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div
        className={`w-full max-w-6xl max-h-[90vh] rounded-lg shadow-xl ${reportModalPanelClass(darkMode)} flex flex-col`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${reportModalBorderClass(darkMode)}`}>
          <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {reportName || 'Report Results'}{groupInfo ? ` — ${groupSigil(groupInfo.consolidated)}${groupInfo.codename}` : ''}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg hover:bg-opacity-80 transition-colors ${
              darkMode ? 'hover:bg-[#141414] text-gray-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderReportContent()}
        </div>

        {/* Footer */}
        <div className={`p-6 border-t ${reportModalBorderClass(darkMode)} flex justify-end`}>
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              darkMode
                ? 'bg-[#141414] text-white hover:bg-[#1a1a1a]'
                : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportDisplayModal;

